import { useState, useEffect, useRef, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { apiError, fcfa, API, openPdf, openImage, downloadFile, downloadPdf } from "../lib/api";
import { mediaUrl } from "../lib/mediaUrl";
import { AppHeader } from "../components/AppHeader";
import AdminMenuManager from "../components/AdminMenuManager";
import VatSection from "../components/VatSection";

import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  LayoutDashboard, Users, Route, CreditCard, Globe, Check, X, Clock, TrendingUp, Bike, Wallet, Star, Eye, FileText, ShieldAlert, ShieldCheck, UserPlus, Trash2, Download, Ban, Trophy, Search, Unlock, MessageSquare, MapPin, FolderOpen, Receipt, FileSignature, Video, KeyRound, Mail, MessageCircle, Banknote, QrCode, GitMerge, Ticket, Copy, RefreshCw, Activity, UtensilsCrossed, Printer, Share2, ExternalLink, HandHelping,
} from "lucide-react";
import LiveMap from "../components/LiveMap";
import { PromoPoster } from "../components/PromoPoster";
import VisitorsWidget from "../components/admin/VisitorsWidget";
import PdfViewerModal from "../components/PdfViewerModal";
import NicoleCallsTab from "../components/NicoleCalls";
import ProspectionTab from "../components/ProspectionTab";
import DriverProspectionTab from "../components/DriverProspectionTab";
import SeedTestsCard from "../components/SeedTestsCard";
import { useZone } from "../context/ZoneContext";
import { AdminZoneSwitch } from "../components/AdminZoneSwitch";
import { MeetingRoom } from "../components/MeetingRoom";
import { MailConsole } from "../components/MailConsole";
import { MailGenerator } from "../components/MailGenerator";
import AssistedMenusAdmin from "./admin/AssistedMenusAdmin";
import PrintStatsAdmin from "./admin/PrintStatsAdmin";
import CarpoolVerifAdmin from "../components/CarpoolVerifAdmin";
import SocialAgentAdmin from "../components/social/SocialAgentAdmin";
import { BrandAssetsGenerator } from "../components/BrandAssetsGenerator";
import { isNoldDomain } from "../lib/domain";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import QRCode from "qrcode";

const SECTIONS = [
  { id: "stats", icon: LayoutDashboard, key: "stats" },
  { id: "drivers", icon: Users, key: "drivers" },
  { id: "activation", icon: KeyRound, key: "activation" },
  { id: "collab", icon: Ticket, key: "collab" },
  { id: "review", icon: ShieldAlert, key: "review" },
  { id: "carpool_verif", icon: ShieldCheck, key: "carpool_verif" },
  { id: "social", icon: Video, key: "social" },
  { id: "rides", icon: Route, key: "rides" },
  { id: "foodorders", icon: UtensilsCrossed, key: "foodorders" },
  { id: "livemap", icon: MapPin, key: "livemap" },
  { id: "team", icon: UserPlus, key: "team" },
  { id: "agentactivity", icon: Activity, key: "agentActivity" },
  { id: "documents", icon: FileText, key: "documents" },
  { id: "flyer", icon: QrCode, key: "flyer" },
  { id: "mail", icon: Mail, key: "mail" },
  { id: "contracts", icon: FileSignature, key: "contracts" },
  { id: "complaints", icon: MessageSquare, key: "complaints" },
  { id: "expenses", icon: Receipt, key: "expenses" },
  { id: "txns", icon: CreditCard, key: "txns" },
  { id: "recharges", icon: Wallet, key: "recharges" },
  { id: "selfies", icon: Eye, key: "selfies" },
  { id: "fraud", icon: ShieldAlert, key: "fraud" },
  { id: "pay", icon: Banknote, key: "pay" },
  { id: "paysettings", icon: Wallet, key: "paysettings" },
  { id: "restaurateurs", icon: UtensilsCrossed, key: "restaurateurs" },
  { id: "restos-manage", icon: UtensilsCrossed, key: "restos_manage" },
  { id: "vat", icon: Receipt, key: "vat_management" },
  { id: "assistedmenus", icon: HandHelping, key: "assistedMenus" },
  { id: "accounting", icon: TrendingUp, key: "accounting" },
  { id: "log", icon: FileText, key: "log" },
  { id: "countries", icon: Globe, key: "countries" },
];

// Sur le domaine Nold Go (Europe), on masque les rubriques exclusivement BentSkin Go (Afrique) :
// - Agent + Activité agents (recrutement agents Afrique)
// - Collègue (invitations mensuelles BentSkin)
// - Notes de frais (agents terrain Afrique)
// - Transactions, Recharges portefeuille (paiements mobile money Afrique)
// - Selfies passagers (contrôle KYC Afrique)
// - Transferts paiement (versements OM/MoMo Afrique)
// Les sections BentSkin Go conservent tout leur affichage, aucun changement côté bentskingo.com.
const NOLD_HIDDEN_SECTIONS = new Set([
  "team", "agentactivity", "collab", "expenses",
  "txns", "recharges", "selfies", "pay", "paysettings",
]);

// La section Commandes Food est réservée à Nold Go. Elle est masquée sur BentSkin (pas de food au Cameroun pour le moment).
// Idem : vérifications covoiturage réservées à Nold Go.
const BENTSKIN_HIDDEN_SECTIONS = new Set(["foodorders", "restos-manage", "vat", "carpool_verif", "social"]);

const GROUP_HEADERS = { stats: "Général", drivers: "Opérations", team: "Équipe & Support", txns: "Finances", log: "Système" };

// Petit composant pour afficher un badge d'agent (endpoint admin protégé → il faut fetcher
// avec le token JWT via axios puis créer une blob URL, une balise <img> classique renverrait 401).
function AgentBadgePreview({ slug, label }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let currentUrl;
    api.get(`/admin/agent-badge/preview/${slug}`, { responseType: "blob" })
      .then((r) => { currentUrl = URL.createObjectURL(r.data); setUrl(currentUrl); })
      .catch(() => setErr(true));
    return () => { if (currentUrl) URL.revokeObjectURL(currentUrl); };
  }, [slug]);
  return (
    <div className="rounded-2xl border border-[#E2DFD8] p-3 bg-[#FDF8F0]" data-testid={`preview-badge-${slug}`}>
      {err ? (
        <div className="w-full aspect-[86/135] rounded-xl border border-dashed border-[#F26419] bg-white flex items-center justify-center text-xs text-[#75807A] text-center p-4">Badge introuvable sur ce serveur.<br />Redéployez pour propager les fichiers.</div>
      ) : url ? (
        <img src={url} alt={`Badge ${label}`} className="w-full rounded-xl border border-[#E2DFD8] bg-white" />
      ) : (
        <div className="w-full aspect-[86/135] rounded-xl border border-[#E2DFD8] bg-white animate-pulse" />
      )}
      <div className="mt-2 flex flex-wrap justify-between items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wider text-[#1A1A1A] truncate">{label}</span>
        {url && (
          <div className="flex items-center gap-1.5 shrink-0">
            <a href={url} download={`badge-${slug}.jpg`} data-testid={`download-badge-${slug}`} className="text-[11px] font-bold text-[#F26419] hover:underline inline-flex items-center gap-1"><Download className="w-3 h-3" /> Télécharger</a>
            <button type="button" onClick={() => {
              const w = window.open("", "_blank", "noopener,width=500,height=760");
              if (!w) return;
              w.document.write(`<!doctype html><html><head><title>Badge ${label}</title><style>
                body{margin:0;padding:24px;background:#FDF8F0;font-family:system-ui,Arial;text-align:center}
                h1{color:#F26419;font-size:20px;margin:0 0 10px}
                img{max-width:100%;height:auto;border-radius:16px;background:white;padding:8px;box-shadow:0 4px 20px rgba(0,0,0,0.08)}
                p{color:#75807A;font-size:11px;margin-top:14px}
                @page{size:auto;margin:10mm}
                </style></head><body>
                <h1>Nold Go — Badge Agent</h1>
                <img src="${url}" alt="Badge ${label}"/>
                <p>${label}</p>
                <script>window.onload=()=>setTimeout(()=>window.print(),400)<\/script>
                </body></html>`);
              w.document.close();
            }} data-testid={`print-badge-${slug}`} className="text-[11px] font-bold text-[#1A1A1A] hover:text-[#F26419] inline-flex items-center gap-1"><Printer className="w-3 h-3" /> Imprimer</button>
            <button type="button" onClick={async () => {
              const to = window.prompt(`Envoyer le badge de ${label} par email à :`, "");
              if (!to || !to.includes("@")) return;
              try {
                await api.post("/admin/agent-badge/email", { slug, to: to.trim(), label });
                toast.success(`✅ Badge envoyé à ${to.trim()}`);
              } catch (e) {
                toast.error(apiError(e));
              }
            }} data-testid={`email-badge-${slug}`} className="text-[11px] font-bold text-[#1A1A1A] hover:text-[#F26419] inline-flex items-center gap-1"><Mail className="w-3 h-3" /> Email</button>
          </div>
        )}
      </div>
    </div>
  );
}


// Boutons Imprimer + Partager + QR (Web Share API + WhatsApp fallback) pour un flyer/PDF donné
function PrintShareButtons({ pdfPath, jpgUrl, shareTitle, shareText, testIdPrefix, kind, onEvent }) {
  const [qrOpen, setQrOpen] = useState(false);
  const track = (action) => {
    if (!kind) return;
    api.post("/marketing/track-share", { kind, action }).then(() => onEvent && onEvent()).catch(() => { /* ignore */ });
  };
  const doPrint = async () => {
    try {
      const token = localStorage.getItem("bsg_token") || "";
      const r = await fetch(mediaUrl(pdfPath), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!r.ok) throw new Error("dl");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed"; iframe.style.right = "-9999px"; iframe.style.bottom = "-9999px";
      iframe.style.width = "1px"; iframe.style.height = "1px"; iframe.style.border = "0";
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); track("print"); } catch { /* ignore */ }
        setTimeout(() => { document.body.removeChild(iframe); URL.revokeObjectURL(url); }, 60_000);
      };
    } catch { toast.error("Impression indisponible — utilisez le bouton Télécharger."); }
  };
  const doShare = async () => {
    const shareUrl = mediaUrl(pdfPath);
    const wa = `https://wa.me/?text=${encodeURIComponent((shareText || shareTitle) + " → " + shareUrl)}`;
    try {
      if (navigator.share && jpgUrl) {
        try {
          const r = await fetch(mediaUrl(jpgUrl));
          const blob = await r.blob();
          const file = new File([blob], "nold-go-flyer.jpg", { type: blob.type || "image/jpeg" });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: shareTitle, text: shareText || "" });
            track("share"); return;
          }
        } catch { /* fallback */ }
        await navigator.share({ title: shareTitle, text: shareText || "", url: shareUrl });
        track("share"); return;
      }
    } catch { return; }
    window.open(wa, "_blank", "noopener");
    track("share");
  };
  const doQr = () => { track("qr"); setQrOpen(true); };
  const shareUrl = mediaUrl(pdfPath);
  return (
    <>
      <Button data-testid={`${testIdPrefix}-print`} onClick={doPrint} type="button"
        className="h-11 px-4 rounded-xl bg-white border-2 border-[#75807A]/40 hover:border-[#1A1A1A] text-[#1A1A1A] font-bold">
        <Printer className="w-4 h-4 mr-1.5" /> Imprimer
      </Button>
      <Button data-testid={`${testIdPrefix}-share`} onClick={doShare} type="button"
        className="h-11 px-4 rounded-xl bg-[#25D366] hover:bg-[#20b356] text-white font-bold">
        <Share2 className="w-4 h-4 mr-1.5" /> Partager
      </Button>
      <Button data-testid={`${testIdPrefix}-qr`} onClick={doQr} type="button"
        className="h-11 px-4 rounded-xl bg-[#1A1A1A] hover:bg-[#3B423E] text-white font-bold">
        <QrCode className="w-4 h-4 mr-1.5" /> QR
      </Button>
      {qrOpen && <QrModal url={shareUrl} title={shareTitle} onClose={() => setQrOpen(false)} />}
    </>
  );
}

function QrModal({ url, title, onClose }) {
  const qrSrc = `${API}/marketing/qr.png?data=${encodeURIComponent(url)}&size=640`;
  const doPrint = () => {
    const w = window.open("", "_blank", "noopener,width=520,height=720");
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>QR — ${title}</title><style>
      body{font-family:system-ui,Arial;background:#FDF8F0;margin:0;padding:32px;text-align:center;color:#1A1A1A}
      h1{color:#F26419;font-size:22px;margin:0 0 6px}p{color:#75807A;font-size:12px;margin:8px 0 20px;word-break:break-all}
      img{max-width:100%;border-radius:16px;background:white;padding:16px}
      .cta{margin-top:20px;font-size:14px}
      </style></head><body>
      <h1>Nold Go</h1>
      <div style="font-size:14px;font-weight:700;margin-bottom:16px">${title}</div>
      <img src="${qrSrc}" alt="QR"/>
      <p>${url}</p>
      <div class="cta">Scannez ce QR code pour accéder au flyer.</div>
      <script>window.onload=()=>setTimeout(()=>window.print(),500)</script>
      </body></html>`);
    w.document.close();
  };
  const doCopy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Lien copié !"); } catch { toast.error("Copie impossible."); } };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="qr-modal">
      <div className="bg-white w-full max-w-md rounded-3xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-head font-black text-lg text-[#1A1A1A]">Partager en QR code</div>
          <button onClick={onClose} data-testid="qr-modal-close" className="w-9 h-9 rounded-full hover:bg-[#F0EBE1] grid place-items-center"><X className="w-5 h-5" /></button>
        </div>
        <div className="text-xs text-[#75807A] mb-3">{title}</div>
        <div className="rounded-2xl bg-[#FAF5EC] p-4 flex justify-center">
          <img src={qrSrc} alt="QR" className="w-64 h-64" data-testid="qr-modal-img" />
        </div>
        <div className="mt-3 text-[11px] text-[#75807A] text-center break-all">{url}</div>
        <div className="mt-4 flex gap-2">
          <Button onClick={doCopy} data-testid="qr-modal-copy" className="flex-1 h-11 rounded-xl bg-white border border-[#E2DFD8] hover:border-[#1A1A1A] text-[#1A1A1A] font-bold"><Copy className="w-4 h-4 mr-1.5" /> Copier</Button>
          <Button onClick={doPrint} data-testid="qr-modal-print" className="flex-1 h-11 rounded-xl bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold"><Printer className="w-4 h-4 mr-1.5" /> Imprimer QR</Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [section, setSection] = useState(() => new URLSearchParams(window.location.search).get("section") || "stats");
  const [stats, setStats] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [activationPending, setActivationPending] = useState({ items: [], count: 0, fee: 200 });
  const [ghostCount, setGhostCount] = useState(0);
  const [smsStats, setSmsStats] = useState(null);
  const [activatedAccounts, setActivatedAccounts] = useState({ items: [], count: 0, drivers: 0, passengers: 0 });
  const [collabData, setCollabData] = useState({ items: [], reward: 100, total_activations: 0, total_payout: 0 });
  const [collabEdits, setCollabEdits] = useState({});
  const [collabSearch, setCollabSearch] = useState("");


// Modal — Journal des refus livreur (manuel + timeout 30 s)
function RefusalsModal({ onClose, refusals, setRefusals }) {
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/food/driver-refusals?limit=100");
        setRefusals(data);
      } catch { setRefusals({ items: [], count: 0, top_30d: [] }); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div data-testid="refusals-modal" className="fixed inset-0 z-[3300] bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-4xl w-full space-y-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-head font-black text-xl">🚫 Journal des refus livreur</h3>
            <p className="text-sm text-[#75807A]">Refus manuels + timeouts 30 s · les 100 plus récents</p>
          </div>
          <button data-testid="refusals-close" onClick={onClose} className="text-2xl text-[#75807A] hover:text-[#1A1A1A]">×</button>
        </div>
        {loading && <div className="py-8 text-center text-[#75807A]">Chargement…</div>}
        {!loading && refusals && (
          <>
            {refusals.top_30d && refusals.top_30d.length > 0 && (
              <div className="rounded-xl bg-[#FFF3E6] border border-[#F26419]/30 p-3">
                <div className="font-bold text-sm text-[#7A3A00] mb-2">🏆 Top livreurs refusants — 30 derniers jours</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                  {refusals.top_30d.map((t, i) => (
                    <div key={t.driver_id} className="bg-white rounded-lg p-2 border border-[#E2DFD8]">
                      <div className="font-bold text-[#1A1A1A]">#{i + 1} · {t.driver_name || t.driver_id.slice(0, 8)}</div>
                      <div className="text-[#75807A] mt-0.5">
                        <b className="text-[#D32F2F]">{t.total}</b> refus (⌛ {t.timeout} · ✋ {t.manual})
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="rounded-xl border border-[#E2DFD8] overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-[#F0EBE1] text-[#3B423E]">
                  <tr>
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-left">Livreur</th>
                    <th className="p-2 text-left">Restaurant</th>
                    <th className="p-2 text-left">Cause</th>
                    <th className="p-2 text-left">Commande</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0EBE1]">
                  {refusals.items.map((r) => (
                    <tr key={r.id} data-testid={`refusal-row-${r.id}`}>
                      <td className="p-2 whitespace-nowrap text-[#75807A]">{(r.refused_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="p-2 font-semibold">{r.driver_name || r.driver_id?.slice(0, 8) || "—"}</td>
                      <td className="p-2">{r.restaurant_name || r.restaurant_id?.slice(0, 8) || "—"}</td>
                      <td className="p-2">
                        {r.reason === "timeout"
                          ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 font-bold">⌛ Timeout</span>
                          : <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-800 px-2 py-0.5 font-bold">✋ Manuel</span>}
                      </td>
                      <td className="p-2 font-mono text-[10px] text-[#75807A]">{r.order_id?.slice(0, 8)}</td>
                    </tr>
                  ))}
                  {refusals.items.length === 0 && (
                    <tr><td colSpan={5} className="p-6 text-center text-[#75807A]">Aucun refus enregistré.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// Poller pour un job de spot vidéo (2-5 min) — GET toutes les 5 s jusqu'à done/failed
function VideoJobPoller({ job, onUpdate }) {
  const [elapsed, setElapsed] = useState(0);
  const startedAtRef = useRef(null);
  const jobId = job.job_id || job.id;

  useEffect(() => {
    if (!jobId) return;
    // ⚠️ startedAt ne doit être fixé QU'UNE FOIS par jobId (sinon le compteur
    // se re-remet à 0 à chaque poll qui renvoie de nouvelles clés).
    if (startedAtRef.current === null) startedAtRef.current = Date.now();

    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/admin/marketing/restaurant-video/${jobId}`);
        onUpdate({ ...data, job_id: jobId });
        if (data.status === "done" || data.status === "failed") return;
      } catch { /* on retente au tick suivant */ }
      timer = setTimeout(poll, 5000);
    };
    poll();
    // Tick 1 fois par seconde pour afficher mm:ss. Résilient : lit toujours
    // startedAtRef.current (référence stable pour toute la durée du job).
    const tick = setInterval(() => {
      setElapsed(Math.floor((Date.now() - (startedAtRef.current || Date.now())) / 1000));
    }, 1000);
    return () => { clearTimeout(timer); clearInterval(tick); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const stalled = elapsed >= 300;
  const slow = elapsed >= 90 && !stalled;
  const usingSora = job.use_sora !== false;
  return (
    <div data-testid="video-progress" className="py-8 text-center space-y-3">
      <div className="mx-auto w-14 h-14 border-4 border-[#F26419] border-t-transparent rounded-full animate-spin" />
      <div className="font-bold text-[#1A1A1A]">
        {job.status === "pending" ? "⏳ En file d'attente…" : "🎥 Génération en cours…"}
      </div>
      <div className="text-xs text-[#75807A]" data-testid="video-elapsed">
        {usingSora ? "Sora 2 + assemblage ffmpeg" : "Diaporama + assemblage ffmpeg"}
        {" · "}<b>{mm}:{ss}</b> écoulées · patientez, ne fermez pas cette fenêtre.
      </div>
      {slow && (
        <div className="mx-4 rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-2 text-xs text-[#78350F]">
          ⏳ La génération prend plus longtemps que prévu. {usingSora ? "Sora 2 peut demander jusqu'à 5 minutes selon la charge OpenAI." : "Le serveur assemble encore les frames — patience."}
        </div>
      )}
      {stalled && (
        <div className="mx-4 rounded-xl border border-[#DC2626]/40 bg-[#DC2626]/10 px-3 py-2 text-xs text-[#991B1B] space-y-2">
          <div>⚠️ Génération bloquée depuis 5 min. Le job est probablement orphelin (serveur redémarré ou timeout Sora).</div>
          <button data-testid="video-force-cancel" onClick={() => onUpdate({ ...job, status: "failed", error: "Bloqué côté serveur. Réessayez sans Sora 2 pour un rendu rapide (~60s)." })}
            className="w-full h-9 rounded-lg bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-bold">
            Fermer et réessayer
          </button>
        </div>
      )}
    </div>
  );
}

// Bloc de statistiques de partages/impressions/QR des flyers (agrégés par kind × action)
function ShareStatsBlock() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try { const { data } = await api.get("/marketing/share-stats"); setStats(data); }
    catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const KINDS = [
    { k: "flyer-europe", label: "Flyer Europe", icon: "🇪🇺" },
    { k: "flyer-restaurateur", label: "Flyer Restaurateurs", icon: "🍔" },
    { k: "flyer-livreur", label: "Flyer Livreurs", icon: "🚲" },
  ];
  const all = stats?.all_time || {};
  const d7 = stats?.last_7d || {};
  const totalAll = KINDS.reduce((s, k) => s + (all[k.k]?.total || 0), 0);
  return (
    <div className="mb-5 bg-white rounded-2xl border border-[#E2DFD8] p-4" data-testid="flyer-share-stats">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-head font-bold text-base text-[#1A1A1A] inline-flex items-center gap-1.5"><Activity className="w-4 h-4 text-[#F26419]" /> Statistiques partages</h3>
          <p className="text-[11px] text-[#75807A]">Suivi impressions · partages · QR (total & 7 derniers jours)</p>
        </div>
        <button onClick={load} data-testid="flyer-stats-refresh" className="text-[11px] font-semibold text-[#F26419] hover:underline inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Actualiser</button>
      </div>
      {loading ? (
        <div className="py-3 text-center text-xs text-[#75807A]">Chargement…</div>
      ) : totalAll === 0 ? (
        <div className="py-3 text-center text-xs text-[#75807A]">Aucun partage enregistré pour l'instant.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {KINDS.map((it) => {
            const a = all[it.k] || { share: 0, print: 0, qr: 0, download: 0, total: 0 };
            const b = d7[it.k] || { share: 0, print: 0, qr: 0, download: 0, total: 0 };
            const bestClass = totalAll > 0 && a.total === Math.max(...KINDS.map((x) => all[x.k]?.total || 0)) && a.total > 0
              ? "border-[#F26419] bg-[#FFF3E6]" : "border-[#E2DFD8] bg-[#FAF5EC]";
            return (
              <div key={it.k} className={`rounded-xl border-2 ${bestClass} p-3`} data-testid={`stats-${it.k}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-bold text-[#1A1A1A]">{it.icon} {it.label}</div>
                  <div className="text-lg font-head font-black text-[#F26419]">{a.total}</div>
                </div>
                <div className="mt-1.5 flex gap-3 text-[10px] text-[#75807A]">
                  <span>🖨 <b className="text-[#1A1A1A]">{a.print}</b></span>
                  <span>📤 <b className="text-[#1A1A1A]">{a.share}</b></span>
                  <span>🔳 <b className="text-[#1A1A1A]">{a.qr}</b></span>
                </div>
                <div className="mt-1.5 text-[10px] text-[#F26419] font-semibold">7j : <b>{b.total}</b> interactions</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

  const [collabFType, setCollabFType] = useState("");
  const [collabFStatus, setCollabFStatus] = useState("");
  const [collabFCity, setCollabFCity] = useState("");
  const [rides, setRides] = useState([]);
  const [foodOrders, setFoodOrders] = useState([]);
  const [txns, setTxns] = useState([]);
  const [countries, setCountries] = useState([]);
  const [payout, setPayout] = useState(null);
  const [promo, setPromo] = useState(null);
  const [payoutNumber, setPayoutNumber] = useState("");
  const [reviewDriver, setReviewDriver] = useState(null);
  const [selfieHistory, setSelfieHistory] = useState(null);
  useEffect(() => {
    if (reviewDriver?.id) {
      setSelfieHistory(null);
      api.get(`/admin/drivers/${reviewDriver.id}/selfie-history`).then((r) => setSelfieHistory(r.data)).catch(() => setSelfieHistory({ items: [] }));
    }
  }, [reviewDriver]);
  const [agents, setAgents] = useState([]);
  const [agentActivity, setAgentActivity] = useState({ agents: [], count: 0 });
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [dailyDate, setDailyDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dailyData, setDailyData] = useState({ agents: [], totals: {}, bonus: 0 });
  const [agentEmailInputs, setAgentEmailInputs] = useState({});
  const [mailUnread, setMailUnread] = useState(0);
  const [agentBonus, setAgentBonus] = useState(0);
  const [bonusInput, setBonusInput] = useState("");
  const [thresholdInput, setThresholdInput] = useState("");
  const [driverCat, setDriverCat] = useState("all");
  const [restaurateurs, setRestaurateurs] = useState({ items: [], count: 0 });
  const [rtRejectId, setRtRejectId] = useState(null);
  const [rtRejectReason, setRtRejectReason] = useState("");
  const [driverSearch, setDriverSearch] = useState("");
  const [driverKyc, setDriverKyc] = useState("");
  const [driverAct, setDriverAct] = useState("");
  const [driverCity, setDriverCity] = useState("");
  const [newAgent, setNewAgent] = useState({ phone: "", name: "", zone: "", email: "" });
  const [recruitsModal, setRecruitsModal] = useState(null);
  const [payoutModal, setPayoutModal] = useState(null);
  const [payoutForm, setPayoutForm] = useState({ amount: "", note: "" });
  const [modAction, setModAction] = useState(null);
  const [mergeSrc, setMergeSrc] = useState(null);
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [mergeQuery, setMergeQuery] = useState("");
  const [mergeBusy, setMergeBusy] = useState(false);
  const [dossier, setDossier] = useState(null);
  const [moderationLog, setModerationLog] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [logAction, setLogAction] = useState("");
  const [logRole, setLogRole] = useState("");
  const [whatsapp, setWhatsapp] = useState(null);
  const [taxiDocsRequired, setTaxiDocsRequired] = useState(false);
  const [incompleteDrivers, setIncompleteDrivers] = useState([]);
  const [whatsappInput, setWhatsappInput] = useState("");
  const [omAssoInput, setOmAssoInput] = useState("");
  const [momoPayInput, setMomoPayInput] = useState("");
  const [payoutNumInput, setPayoutNumInput] = useState("");
  const [agentSearch, setAgentSearch] = useState("");
  const [complaints, setComplaints] = useState([]);
  const [agentApps, setAgentApps] = useState([]);
  const [acct, setAcct] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [recharges, setRecharges] = useState(null);
  const [rechargeFilter, setRechargeFilter] = useState("pending");
  const [selfies, setSelfies] = useState(null);
  const [fraud, setFraud] = useState(null);
  const [selfieQuery, setSelfieQuery] = useState("");
  const [expFilter, setExpFilter] = useState("pending");
  const [contracts, setContracts] = useState(null);
  const [contractFilter, setContractFilter] = useState("all");
  const [noldContracts, setNoldContracts] = useState({ items: [], count: 0 });
  const [pdfViewer, setPdfViewer] = useState({ open: false, title: "", path: "", filename: "document.pdf" });
  const [prospectTab, setProspectTab] = useState("resto");
  const showPdf = (title, path, filename) => setPdfViewer({ open: true, title, path, filename });
  const [reminderDays, setReminderDays] = useState(3);
  const [contractDuration, setContractDuration] = useState(6);
  const [aiReport, setAiReport] = useState("");
  const [livePoints, setLivePoints] = useState([]);
  const [liveFilter, setLiveFilter] = useState("all");
  const [aiLoading, setAiLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [teamFeed, setTeamFeed] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [teamCode, setTeamCode] = useState(null);
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [meetModal, setMeetModal] = useState(false);
  const [meetForm, setMeetForm] = useState({ title: "", target: "all", zone: "", when: "now", scheduled_at: "", reminder: 15 });
  const [teamMsg, setTeamMsg] = useState("");
  // Agenda RDV
  const [appts, setAppts] = useState([]);
  const [apptForm, setApptForm] = useState({ title: "", at: "", with_name: "", phone: "", location: "", kind: "prospection", notes: "", notify_email: "" });
  const [apptSaving, setApptSaving] = useState(false);
  const loadAppts = () => api.get("/admin/appointments").then(r => setAppts(r.data.items || [])).catch(() => {});
  const saveAppt = async () => {
    if (!apptForm.title.trim() || !apptForm.at) { toast.error("Titre + date requis"); return; }
    if (apptForm.notify_email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(apptForm.notify_email.trim())) {
      toast.error("Email invalide"); return;
    }
    setApptSaving(true);
    try {
      await api.post("/admin/appointments", { ...apptForm, notify_email: apptForm.notify_email.trim().toLowerCase() });
      toast.success(apptForm.notify_email ? "RDV enregistré + email envoyé ✓" : "Rendez-vous enregistré ✓");
      setApptForm({ title: "", at: "", with_name: "", phone: "", location: "", kind: "prospection", notes: "", notify_email: "" });
      loadAppts();
    } catch (e) { toast.error(apiError(e)); }
    finally { setApptSaving(false); }
  };
  const deleteAppt = async (id) => {
    if (!window.confirm("Supprimer ce rendez-vous ?")) return;
    try { await api.delete(`/admin/appointments/${id}`); toast.success("Supprimé"); loadAppts(); } catch (e) { toast.error(apiError(e)); }
  };
  const markDone = async (id) => {
    try { await api.patch(`/admin/appointments/${id}`, { status: "done" }); loadAppts(); } catch (e) { toast.error(apiError(e)); }
  };
  const [adminPartners, setAdminPartners] = useState({ env_admins: [], extra_admins: [] });
  const [adminInviteEmail, setAdminInviteEmail] = useState("");
  const [adminInviteName, setAdminInviteName] = useState("");
  const [adminInviting, setAdminInviting] = useState(false);
  const loadAdminPartners = () => api.get("/admin/team/admins").then(r => setAdminPartners(r.data || { env_admins: [], extra_admins: [] })).catch(() => {});
  const inviteAdminPartner = async () => {
    const email = adminInviteEmail.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error("Email invalide"); return; }
    setAdminInviting(true);
    try {
      await api.post("/admin/team/admins", { email, name: adminInviteName.trim() || "Partenaire" });
      toast.success(`✅ ${email} promu admin. Email de bienvenue envoyé.`);
      setAdminInviteEmail(""); setAdminInviteName(""); loadAdminPartners();
    } catch (e) { toast.error(apiError(e)); }
    finally { setAdminInviting(false); }
  };
  const revokeAdminPartner = async (email) => {
    if (!window.confirm(`Révoquer l'accès admin de ${email} ?`)) return;
    try {
      await api.delete(`/admin/team/admins/${encodeURIComponent(email)}`);
      toast.success(`Accès admin révoqué pour ${email}`); loadAdminPartners();
    } catch (e) { toast.error(apiError(e)); }
  };
  const [expReply, setExpReply] = useState({});
  const [payOrders, setPayOrders] = useState([]);
  const [payCfg, setPayCfg] = useState({ momo_pay_number: "", om_asso_number: "", display_name: "", instructions: "" });
  const [noldCompany, setNoldCompany] = useState({ iban: "" });
  const [liveHistory, setLiveHistory] = useState([]);
  const [zoneStatus, setZoneStatus] = useState(null);
  const { zone: viewZone, isSuperAdmin } = useZone();
  const prevKycRef = useRef(null);

  const playChime = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine"; o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
      o.start(); o.stop(ctx.currentTime + 0.42);
    } catch (e) { /* audio unavailable */ }
  };

  const load = (initInputs = false) => {
    api.get("/admin/stats").then((r) => {
      const pk = r.data?.pending_kyc || 0;
      if (prevKycRef.current !== null && pk > prevKycRef.current) {
        playChime();
        toast(t("admin.newKyc"), { description: t("admin.newKycDesc") });
      }
      prevKycRef.current = pk;
      setStats(r.data);
    }).catch(() => {});
    api.get("/admin/drivers").then((r) => setDrivers(r.data)).catch(() => {});
    api.get("/admin/activation-pending").then((r) => setActivationPending(r.data)).catch(() => {});
    api.get("/admin/ghost-drivers").then((r) => setGhostCount(r.data.count || 0)).catch(() => {});
    api.get("/admin/sms-stats").then((r) => setSmsStats(r.data)).catch(() => {});
    api.get("/admin/activated-accounts").then((r) => setActivatedAccounts(r.data)).catch(() => {});
    api.get("/admin/collab-codes").then((r) => setCollabData(r.data)).catch(() => {});
    api.get("/admin/rides").then((r) => setRides(r.data)).catch(() => {});
    api.get("/admin/food-orders").then((r) => setFoodOrders(r.data)).catch(() => {});
    api.get("/admin/transactions").then((r) => setTxns(r.data)).catch(() => {});
    api.get("/countries").then((r) => setCountries(r.data)).catch(() => {});
    api.get("/admin/payout").then((r) => { setPayout(r.data); if (initInputs) setPayoutNumber(r.data.number || ""); }).catch(() => {});
    api.get("/promo").then((r) => setPromo(r.data)).catch(() => {});
    api.get("/admin/agents").then((r) => { setAgents(r.data.agents || []); setAgentBonus(r.data.bonus_per || 0); if (initInputs) { setBonusInput(String(r.data.bonus_per || 0)); setThresholdInput(String(r.data.payout_threshold || 10000)); setOmAssoInput(r.data.om_asso || ""); setMomoPayInput(r.data.momo_pay || ""); } }).catch(() => {});
    api.get("/agent/leaderboard").then((r) => setLeaderboard(r.data || [])).catch(() => {});
    api.get("/admin/agent-activity").then((r) => setAgentActivity(r.data || { agents: [], count: 0 })).catch(() => {});
    loadDaily(new Date().toISOString().slice(0, 10));
    api.get("/admin/moderation-log").then((r) => setModerationLog(r.data || [])).catch(() => {});
    api.get("/admin/whatsapp").then((r) => { setWhatsapp(r.data); if (initInputs) setWhatsappInput(r.data.from_number || ""); }).catch(() => {});
    api.get("/admin/kyc-policy").then((r) => setTaxiDocsRequired(!!r.data.taxi_docs_required)).catch(() => {});
    api.get("/admin/drivers/incomplete").then((r) => setIncompleteDrivers(r.data.items || [])).catch(() => {});
    api.get("/admin/complaints").then((r) => setComplaints(r.data || [])).catch(() => {});
    api.get("/admin/agent-applications").then((r) => setAgentApps(r.data || [])).catch(() => {});
    api.get("/admin/accounting").then((r) => setAcct(r.data)).catch(() => {});
    api.get("/admin/expenses").then((r) => setExpenses(r.data)).catch(() => {});
    api.get("/admin/wallet-recharges").then((r) => setRecharges(r.data)).catch(() => {});
    api.get("/admin/passenger-selfies").then((r) => setSelfies(r.data)).catch(() => {});
    api.get("/admin/fraud-alerts").then((r) => setFraud(r.data)).catch(() => {});
    api.get("/admin/contact-messages").then((r) => setMailUnread(r.data.unread || 0)).catch(() => {});
    api.get("/agent/feed").then((r) => setTeamFeed(r.data || [])).catch(() => {});
    api.get("/admin/meetings").then((r) => setMeetings(r.data.items || [])).catch(() => {});
    api.get("/admin/team-code").then((r) => setTeamCode(r.data)).catch(() => {});
    loadAdminPartners();
    loadAppts();
    api.get("/admin/contracts").then((r) => setContracts(r.data)).catch(() => {});
    api.get("/admin/contracts/settings").then((r) => { setReminderDays(r.data.reminder_days); setContractDuration(r.data.duration_months); }).catch(() => {});
    api.get("/admin/pay/orders").then((r) => setPayOrders(r.data.items || [])).catch(() => {});
    api.get("/admin/payment-settings").then((r) => setPayCfg(r.data)).catch(() => {});
    api.get("/admin/nold-settings").then((r) => setNoldCompany(r.data)).catch(() => {});
  };
  const savePayCfg = async () => {
    try { await api.put("/admin/payment-settings", payCfg); toast.success("Paramètres paiement enregistrés ✅"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveNoldCompany = async () => {
    try {
      const { data } = await api.put("/admin/nold-settings", { iban: noldCompany.iban });
      setNoldCompany({ iban: data.iban });
      toast.success("IBAN Nold Go enregistré ✅ (visible sur les prochaines factures)");
    } catch (e) { toast.error(apiError(e)); }
  };
  const markPaidOut = async (sid) => {
    try { await api.post(`/admin/pay/orders/${sid}/payout`); toast.success("Transfert marqué comme versé ✅"); const r = await api.get("/admin/pay/orders"); setPayOrders(r.data.items || []); }
    catch (e) { toast.error(apiError(e)); }
  };
  const waProof = (o) => {
    const num = String(o.tel_client || "").replace(/[^0-9]/g, "");
    if (!num) return toast.error("Numéro client indisponible");
    const prenom = (o.nom_client || "").split(" ")[0];
    const reseau = o.reseau === "MoMo" ? "MTN MoMo" : "Orange Money";
    const msg = `Bonjour ${prenom} 👋 Ton transfert ${isNoldDomain() ? "Nold Go" : "BentSkin Go"} Pay de ${fcfa(o.fcfa_recu)} vers ${o.num_beneficiaire} (${reseau}) a bien été envoyé ✅. Voici la preuve :`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const postTeamMsg = async () => {
    const text = teamMsg.trim();
    if (!text) return;
    try { await api.post("/agent/feed", { text }); setTeamMsg(""); const r = await api.get("/agent/feed"); setTeamFeed(r.data || []); }
    catch (e) { toast.error(apiError(e)); }
  };
  const deleteTeamMsg = async (id) => {
    try { await api.delete(`/agent/feed/${id}`); setTeamFeed((f) => f.filter((x) => x.id !== id)); }
    catch (e) { toast.error(apiError(e)); }
  };
  const processExpense = async (id) => {
    try { await api.post(`/admin/expenses/${id}/process`); toast.success(t("admin.expProcessing")); load(false); }
    catch (e) { toast.error(apiError(e)); }
  };
  const validateExpense = async (id) => {
    try { await api.post(`/admin/expenses/${id}/validate`); toast.success(t("admin.expValidated")); load(false); }
    catch (e) { toast.error(apiError(e)); }
  };
  const rejectExpense = async (id) => {
    try { await api.post(`/admin/expenses/${id}/reject`); toast.success(t("admin.expRejected")); load(false); }
    catch (e) { toast.error(apiError(e)); }
  };
  const approveRecharge = async (id) => {
    try { const { data } = await api.post(`/admin/wallet-recharges/${id}/approve`); toast.success(`Recharge validée ✅ Nouveau solde : ${(data.balance || 0).toLocaleString("fr-FR")} FCFA`); load(false); }
    catch (e) { toast.error(apiError(e)); }
  };
  const rejectRecharge = async (id) => {
    if (!window.confirm("Refuser cette recharge ? Le passager ne sera pas crédité.")) return;
    try { await api.post(`/admin/wallet-recharges/${id}/reject`); toast.success("Recharge refusée"); load(false); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remindContract = async (agentId) => {
    try { await api.post(`/admin/contracts/${agentId}/remind`); toast.success("Rappel envoyé par SMS ✅"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remindAll = async () => {
    try { const { data } = await api.post("/admin/contracts/remind-all"); toast.success(data.count ? `Relance envoyée à ${data.count} agent(s) ✅` : "Aucun agent en attente de signature"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const createMeeting = async () => {
    const title = meetForm.title.trim() || (isNoldDomain() ? "Réunion Nold Go" : "Réunion BentSkin");
    const payload = { title, target: meetForm.target, zone: meetForm.target === "zone" ? meetForm.zone : null };
    if (meetForm.when === "later" && meetForm.scheduled_at) { payload.scheduled_at = new Date(meetForm.scheduled_at).toISOString(); payload.reminder_minutes = meetForm.reminder; }
    try {
      const { data } = await api.post("/admin/meetings", payload);
      toast.success(`Réunion créée · ${data.invited_count} agent(s) invité(s) ✅`);
      setMeetModal(false);
      setMeetForm({ title: "", target: "all", zone: "", when: "now", scheduled_at: "" });
      const r = await api.get("/admin/meetings"); setMeetings(r.data.items || []);
      if (meetForm.when === "now") setActiveMeeting(data.id);
    } catch (e) { toast.error(apiError(e)); }
  };
  const endMeeting = async (id) => {
    try { await api.post(`/admin/meetings/${id}/end`); toast.success("Réunion terminée"); const r = await api.get("/admin/meetings"); setMeetings(r.data.items || []); }
    catch (e) { toast.error(apiError(e)); }
  };
  const renewContract = async (contractId, name) => {
    try { const { data } = await api.post(`/admin/contracts/${contractId}/renew`); toast.success(`Contrat de ${name || "l'agent"} renouvelé jusqu'au ${new Date(data.expires_at).toLocaleDateString("fr-FR")} ✅`); load(false); }
    catch (e) { toast.error(apiError(e)); }
  };
  const exportContracts = async () => {
    try {
      const res = await api.get("/admin/contracts/export.zip", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `contrats-signes-${isNoldDomain() ? "noldgo" : "bentskingo"}.zip`; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveReminderDays = async (v) => {
    setReminderDays(v);
    try { await api.put("/admin/contracts/settings", { reminder_days: v }); toast.success(`Relance auto réglée sur ${v} jours`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveDuration = async (v) => {
    setContractDuration(v);
    try { await api.put("/admin/contracts/settings", { duration_months: v }); toast.success(`Durée CDD réglée sur ${v} mois`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const loadLog = (action, role) => {
    const params = {};
    if (action) params.action = action;
    if (role) params.role = role;
    api.get("/admin/moderation-log", { params }).then((r) => setModerationLog(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(true); const iv = setInterval(() => load(false), 60000); return () => clearInterval(iv); }, []);

  // Charge le compteur de restaurateurs en attente au montage pour afficher le badge sidebar dès l'ouverture.
  useEffect(() => {
    api.get("/restaurateur/admin/pending").then((r) => setRestaurateurs(r.data || { items: [], count: 0 })).catch(() => {});
  }, []);

  useEffect(() => {
    if (section !== "restaurateurs") return;
    const loadRt = () => api.get("/restaurateur/admin/pending").then((r) => setRestaurateurs(r.data || { items: [], count: 0 })).catch(() => {});
    const loadContracts = () => api.get("/restaurateur/admin/contracts").then((r) => setNoldContracts(r.data || { items: [], count: 0 })).catch(() => {});
    loadRt(); loadContracts();
    const iv = setInterval(() => { loadRt(); loadContracts(); }, 30000);
    return () => clearInterval(iv);
  }, [section]);

  const cancelNoldContract = async (contractId, label) => {
    if (!window.confirm(`Annuler la signature du contrat de « ${label} » ? Le partenaire pourra re-signer.`)) return;
    try {
      await api.delete(`/restaurateur/admin/contracts/${contractId}`);
      toast.success("Signature annulée ✓");
      setNoldContracts((s) => ({ ...s, items: s.items.filter((c) => c.id !== contractId), count: Math.max(0, s.count - 1) }));
    } catch (e) { toast.error(apiError(e)); }
  };

  // Modal d'approbation avec choix de la durée gratuite (identique à la création manuelle)
  const [approveId, setApproveId] = useState(null);
  const [approveDays, setApproveDays] = useState(0); // 0=à vie, 30/60/90=période
  const approveRestaurateur = async () => {
    if (!approveId) return;
    try {
      await api.post(`/restaurateur/admin/${approveId}/approve`, { free_days: Number(approveDays) || 0 });
      const label = approveDays === 0 ? "à vie" : `${approveDays} jours`;
      toast.success(`Restaurateur approuvé ✓ · Gratuité ${label}`);
      setRestaurateurs((s) => ({ ...s, items: s.items.filter((x) => x.id !== approveId), count: Math.max(0, s.count - 1) }));
      setApproveId(null); setApproveDays(0);
    } catch (e) { toast.error(apiError(e)); }
  };
  const rejectRestaurateur = async () => {
    if (!rtRejectId) return;
    try {
      await api.post(`/restaurateur/admin/${rtRejectId}/reject`, { reason: rtRejectReason.trim() || "Documents non conformes" });
      toast.success("Dossier refusé");
      setRestaurateurs((s) => ({ ...s, items: s.items.filter((x) => x.id !== rtRejectId), count: Math.max(0, s.count - 1) }));
      setRtRejectId(null); setRtRejectReason("");
    } catch (e) { toast.error(apiError(e)); }
  };

  // Modal "Rendre gratuit" (admin) — période d'essai ou gratuité à vie sans Stripe
  const [rtFreeId, setRtFreeId] = useState(null);
  const [rtFreeDays, setRtFreeDays] = useState(30);
  const [rtFreeNote, setRtFreeNote] = useState("");
  const grantFreeRestaurateur = async () => {
    if (!rtFreeId) return;
    try {
      const days = Number(rtFreeDays) || 0;
      await api.post(`/restaurateur/admin/${rtFreeId}/grant-free`, {
        days: days > 0 ? days : null,
        note: rtFreeNote.trim() || null,
      });
      toast.success(days > 0 ? `Accès gratuit accordé (${days} j) ✓` : "Accès gratuit à vie accordé ✓");
      setRestaurateurs((s) => ({ ...s, items: s.items.filter((x) => x.id !== rtFreeId), count: Math.max(0, s.count - 1) }));
      setRtFreeId(null); setRtFreeDays(30); setRtFreeNote("");
    } catch (e) { toast.error(apiError(e)); }
  };

  // Créer un restaurant manuellement (admin) — sans dossier, en mode gratuit par défaut
  const [rtCreateOpen, setRtCreateOpen] = useState(false);
  const [scrapeOpen, setScrapeOpen] = useState(false);
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [scrapePreview, setScrapePreview] = useState(null);
  const [scrapeBusy, setScrapeBusy] = useState(false);
  const [scrapeOwnerEmail, setScrapeOwnerEmail] = useState("");
  const [scrapeDragIdx, setScrapeDragIdx] = useState(null); // index (global) du plat en cours de drag
  const [scrapeAiIdx, setScrapeAiIdx] = useState(null); // index du plat en cours de génération IA
  const [scrapeDetectedPlatforms, setScrapeDetectedPlatforms] = useState([]);
  const [scrapeBlockedReason, setScrapeBlockedReason] = useState(null);
  const [scrapeBatchMode, setScrapeBatchMode] = useState(false); // toggle single URL / multi-URLs
  const [scrapeBatchUrls, setScrapeBatchUrls] = useState(""); // textarea (une URL par ligne)
  const [scrapeBatchResults, setScrapeBatchResults] = useState([]); // [{ url, ok, preview, error, selected, created }]
  const [scrapeBatchProgress, setScrapeBatchProgress] = useState(null); // { current, total, phase: 'analyse'|'create' }
  const [scrapeMultiOpen, setScrapeMultiOpen] = useState(false); // mode "plusieurs URLs = 1 seul resto"
  const [scrapeMultiUrls, setScrapeMultiUrls] = useState(""); // textarea (une URL par ligne, même resto)
  const [scrapeMultiStats, setScrapeMultiStats] = useState(null); // { per_url: [...] }
  const [rtCreateForm, setRtCreateForm] = useState({
    name: "", address: "", description: "", logo_url: "", cover_url: "",
    cuisine: "", phone: "", city: "Bruxelles", postal_code: "",
    owner_email: "", free_days: 0, admin_note: "",
  });
  const [rtCreateLoading, setRtCreateLoading] = useState(false);
  const [rtCreatedResto, setRtCreatedResto] = useState(null);  // restaurant object après création

  // Gestion des restaurants (Brouillons / En ligne)
  const [restosList, setRestosList] = useState({ items: [], count: 0 });
  const [restosTab, setRestosTab] = useState("draft"); // draft | published | all | no-photo
  const [restosLoading, setRestosLoading] = useState(false);
  const [restosQuery, setRestosQuery] = useState("");
  // Invitation restaurateur — modale ouverte pour un restaurant précis
  const [inviteResto, setInviteResto] = useState(null); // { id, name }
  const [handoverResto, setHandoverResto] = useState(null); // { id, name, address }
  // Prospectus PDF automne — preview + download + email
  const [flyerResto, setFlyerResto] = useState(null); // { id, name }
  const [flyerEmail, setFlyerEmail] = useState("");
  const [flyerBusy, setFlyerBusy] = useState(false);
  // Spot vidéo publicitaire 30s (Sora 2 + diaporama) — job asynchrone
  const [videoResto, setVideoResto] = useState(null); // { id, name }
  const [videoJob, setVideoJob] = useState(null); // { job_id, status, video_url?, error?, ... }
  const [videoUseSora, setVideoUseSora] = useState(false);
  const [videoPrompt, setVideoPrompt] = useState("");
  const [videoEmail, setVideoEmail] = useState("");
  const [videoBusy, setVideoBusy] = useState(false);
  // Journal des refus livreur (manuel + timeout 30 s) — modal + fetch on open
  const [refusalsOpen, setRefusalsOpen] = useState(false);
  const [refusals, setRefusals] = useState(null); // { items, count, top_30d }
  // Photo preuve de livraison — modal admin (visualisation plein écran)
  const [proofOrder, setProofOrder] = useState(null); // { id, restaurant_name, delivered_at, url }
  const [handoverItems, setHandoverItems] = useState([
    { label: "Tablette Archos T80HD Wi-Fi", serial: "" },
    { label: "Imprimante Archos thermique 58 mm Bluetooth", serial: "" },
  ]);
  const [handoverName, setHandoverName] = useState("");
  const [handoverAddress, setHandoverAddress] = useState("");
  const [handoverPenalty, setHandoverPenalty] = useState(180);
  const [handoverEmail, setHandoverEmail] = useState("");
  const [handoverSignedFile, setHandoverSignedFile] = useState(null); // File du PDF signé scanné
  const [handoverBusy, setHandoverBusy] = useState(false);
  // Contrat de reprise (miroir)
  const [returnResto, setReturnResto] = useState(null); // { id, name }
  const [returnName, setReturnName] = useState("");
  const [returnEmail, setReturnEmail] = useState("");
  const [returnPenalty, setReturnPenalty] = useState(180);
  const [returnItems, setReturnItems] = useState([
    { label: "Tablette Archos T80HD Wi-Fi", serial: "", condition: "ok", note: "" },
    { label: "Imprimante Archos thermique 58 mm Bluetooth", serial: "", condition: "ok", note: "" },
  ]);
  const [returnBusy, setReturnBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteLastUrl, setInviteLastUrl] = useState(null);
  const sendRestaurateurInvite = async () => {
    const email = (inviteEmail || "").trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Email invalide.");
      return;
    }
    setInviteLoading(true);
    try {
      const { data } = await api.post(`/restaurateur/admin/${inviteResto.id}/invite`, { email, origin: window.location.origin });
      toast.success(`Invitation envoyée à ${email} ✓`);
      setInviteLastUrl(data.invite_url);
    } catch (e) { toast.error(apiError(e)); }
    finally { setInviteLoading(false); }
  };
  const loadRestos = async (tab = restosTab, q = restosQuery) => {
    setRestosLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab !== "all") params.set("status", tab);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/restaurateur/admin/restaurants?${params.toString()}`);
      setRestosList(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setRestosLoading(false); }
  };
  useEffect(() => { if (section === "restos-manage") loadRestos(restosTab, restosQuery); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [section, restosTab]);
  const createRestaurantByAdmin = async () => {
    if (!rtCreateForm.name.trim() || !rtCreateForm.address.trim()) {
      toast.error("Nom et adresse sont obligatoires.");
      return;
    }
    if (rtCreateForm.logo_url === "loading") {
      toast.error("Photo en cours de lecture, patiente 1 seconde…");
      return;
    }
    setRtCreateLoading(true);
    try {
      const payload = {
        ...rtCreateForm,
        // On envoie explicitement le data-URL dans photo_data_url (le champ que le backend cherche en priorité)
        photo_data_url: (rtCreateForm.logo_url || "").startsWith("data:") ? rtCreateForm.logo_url : null,
        logo_url: undefined,  // évite de dupliquer un data-URL énorme dans le payload
        free_days: Number(rtCreateForm.free_days) || null,
        owner_email: rtCreateForm.owner_email.trim() || null,
      };
      const { data } = await api.post("/restaurateur/admin/create-restaurant", payload);
      toast.success("Restaurant créé ✓ (mode gratuit)");
      setRtCreatedResto({ ...data.restaurant, user_id: data.user_id });
      setRtCreateOpen(false);
      setRtCreateForm({
        name: "", address: "", description: "", logo_url: "", cover_url: "",
        cuisine: "", phone: "", city: "Bruxelles", postal_code: "",
        owner_email: "", free_days: 0, admin_note: "",
      });
    } catch (e) { toast.error(apiError(e)); }
    finally { setRtCreateLoading(false); }
  };
  const uploadRtCreateLogo = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setRtCreateForm((f) => ({ ...f, logo_url: reader.result }));
    reader.onerror = () => toast.error("Lecture de la photo échouée");
    // Petit feedback visuel pour éviter que l'admin clique 'Créer' avant que la lecture soit finie
    setRtCreateForm((f) => ({ ...f, logo_url: "loading" }));
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (section !== "livemap") return;
    const loadLive = () => api.get("/admin/live-locations").then((r) => setLivePoints(r.data.points || [])).catch(() => {});
    const loadHistory = () => api.get("/admin/live-history", { params: { hours: 24 } }).then((r) => setLiveHistory(r.data.series || [])).catch(() => {});
    loadLive();
    loadHistory();
    const iv = setInterval(loadLive, 20000);
    const ih = setInterval(loadHistory, 60000);
    return () => { clearInterval(iv); clearInterval(ih); };
  }, [section]);

  useEffect(() => {
    const loadZone = () => api.get("/admin/zone-status").then((r) => setZoneStatus(r.data)).catch(() => {});
    loadZone();
    const iv = setInterval(loadZone, 30000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    setNoteText("");
    if (!dossier?.row?.id) { setNotes([]); return; }
    api.get(`/admin/users/${dossier.row.id}/notes`).then((r) => setNotes(r.data || [])).catch(() => {});
  }, [dossier?.row?.id]);

  const addNote = async () => {
    const text = noteText.trim();
    if (!text || !dossier?.row?.id) return;
    setNoteSaving(true);
    try {
      const { data } = await api.post(`/admin/users/${dossier.row.id}/notes`, { text });
      setNotes((n) => [data, ...n]);
      setNoteText("");
      toast.success(t("admin.noteAdded"));
    } catch (e) { toast.error(apiError(e)); }
    finally { setNoteSaving(false); }
  };
  const deleteNote = async (id) => {
    try { await api.delete(`/admin/users/${dossier.row.id}/notes/${id}`); setNotes((n) => n.filter((x) => x.id !== id)); }
    catch (e) { toast.error(apiError(e)); }
  };

  const savePayout = async (operator) => {
    try {
      const { data } = await api.put("/admin/payout", { operator: operator || payout?.operator || "orange", number: payoutNumber, mode: "centralise" });
      setPayout(data); toast.success("✓");
    } catch (e) { toast.error(apiError(e)); }
  };

  const endPromo = async () => {
    try { await api.post("/admin/promo/end"); toast.success(t("admin.promoActivated")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const extendPromo = async () => {
    try { await api.post("/admin/promo/extend?months=1"); toast.success(t("admin.promoExtended")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const restartPromo = async () => {
    try { await api.post("/admin/promo/extend?months=3"); toast.success(t("admin.promoRestarted")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveWhatsapp = async () => {
    try { const { data } = await api.put("/admin/whatsapp", { from_number: whatsappInput.trim() }); setWhatsapp((w) => ({ ...(w || {}), ...data })); setWhatsappInput(data.from_number || ""); toast.success(t("admin.waSaved")); }    catch (e) { toast.error(apiError(e)); }
  };
  const toggleTaxiDocs = async () => {
    const next = !taxiDocsRequired;
    try { const { data } = await api.put("/admin/kyc-policy", { taxi_docs_required: next }); setTaxiDocsRequired(!!data.taxi_docs_required); toast.success(next ? "Documents taxi désormais OBLIGATOIRES ✅" : "Documents taxi rendus facultatifs ✅"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const relanceInscription = async () => {
    try { const { data } = await api.post("/admin/drivers/relance-inscription"); toast.success(data.count ? `Relance envoyée à ${data.count} chauffeur(s) ✅` : "Aucune inscription incomplète à relancer"); const r = await api.get("/admin/drivers/incomplete"); setIncompleteDrivers(r.data.items || []); }
    catch (e) { toast.error(apiError(e)); }
  };
  const relanceOne = async (id) => {
    try { await api.post(`/admin/drivers/${id}/relance`); toast.success("Message de relance envoyé ✅"); const r = await api.get("/admin/drivers/incomplete"); setIncompleteDrivers(r.data.items || []); }
    catch (e) { toast.error(apiError(e)); }
  };
  const waRelance = (phone, name) => {
    const num = String(phone || "").replace(/[^0-9]/g, "");
    if (!num) return toast.error("Numéro indisponible");
    const prenom = (name || "").split(" ")[0];
    const _brand = isNoldDomain() ? "Nold Go" : "BentSkin Go";
    const _site = isNoldDomain() ? "https://noldgo.com/driver" : "https://www.bentskingo.com/driver";
    const _emoji = isNoldDomain() ? "🚗" : "🛵";
    const msg = `Bonjour ${prenom} 👋 C'est l'équipe ${_brand} ${_emoji}. Nous avons remarqué que votre inscription chauffeur n'est pas encore finalisée. Reprenez et activez votre compte en 2 min : ${_site}`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const waRelanceDup = (group) => {
    const nums = [...new Set((group?.members || []).map((d) => String(d.phone || "").replace(/[^0-9]/g, "")).filter(Boolean))];
    if (nums.length === 0) return toast.error("Aucun numéro WhatsApp dans ce groupe");
    const prenom = (group.label || "").split(" ")[0] || "";
    const _brand = isNoldDomain() ? "Nold Go" : "BentSkin Go";
    const _emoji = isNoldDomain() ? "🚗" : "🛵";
    const msg = `Bonjour ${prenom} 👋 C'est l'équipe ${_brand} ${_emoji}. Nous avons remarqué que vous avez plusieurs comptes chauffeur. Pour continuer à recevoir des courses, gardons UN SEUL compte : répondez-nous et nous fusionnons vos comptes (portefeuille et historique conservés) ✅.`;
    nums.forEach((num, i) => setTimeout(() => window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank"), i * 400));
    toast.success(`Relance WhatsApp ouverte pour ${nums.length} numéro${nums.length > 1 ? "s" : ""}`);
  };
  const approveSignup = async (id, name) => {
    if (!window.confirm(`Approuver et activer l'inscription de ${name || "ce chauffeur"} ? Il pourra recevoir des courses.`)) return;
    try { await api.post(`/admin/drivers/${id}/validate?approve=true`); toast.success("Inscription approuvée ✅"); const r = await api.get("/admin/drivers/incomplete"); setIncompleteDrivers(r.data.items || []); load(false); }
    catch (e) { toast.error(apiError(e)); }
  };

  const cleanupTestData = async (scope) => {
    if (scope === "all") {
      const msg = "⚠️ Action irréversible.\n\nCela va supprimer toutes les courses/commandes NON PAYÉES, les transactions non complétées, les réclamations et les candidatures agent.\n\n✅ SONT PROTÉGÉS : commandes payées Stripe, commandes Food, restaurants créés, tous les paiements réels.\n\nTape le mot SUPPRIMER (en majuscules) pour confirmer :";
      const confirm = window.prompt(msg);
      if ((confirm || "").trim().toUpperCase() !== "SUPPRIMER") {
        toast.info("Nettoyage annulé.");
        return;
      }
      try {
        const { data } = await api.post("/admin/cleanup-test-data", { scope, confirm: "SUPPRIMER" });
        const rr = data.removed || {};
        toast.success(`Nettoyage effectué : ${rr.rides || 0} courses, ${rr.users || 0} comptes, ${rr.complaints || 0} réclamations supprimés`);
        load();
      } catch (e) { toast.error(apiError(e)); }
      return;
    }
    if (!window.confirm("Supprimer les chauffeurs/agents de démo et les comptes de test (TEST_, UI, Demo) ainsi que leurs courses NON PAYÉES ?")) return;
    try { await api.post("/admin/cleanup-test-data", { scope }); toast.success("Nettoyage effectué"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const validate = async (id, approve) => {
    try { await api.post(`/admin/drivers/${id}/validate?approve=${approve}`); toast.success("✓"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const activateSub = async (id) => {
    try { await api.post(`/admin/drivers/${id}/activate-subscription`); toast.success(t("admin.subActivated")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const generateAiReport = async () => {
    setAiLoading(true);
    try { const { data } = await api.post("/admin/accounting/ai-report"); setAiReport(data.report || ""); if (data.data) setAcct(data.data); }
    catch (e) { toast.error(apiError(e)); }
    setAiLoading(false);
  };

  const saveCountry = async (code, patch) => {
    try { await api.put(`/admin/countries/${code}`, patch); toast.success("✓"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const addAgent = async () => {
    if (!newAgent.phone.trim() && !(newAgent.email || "").trim()) return toast.error("Email ou numéro requis");
    try {
      await api.post("/admin/agents", { phone: newAgent.phone.trim(), name: newAgent.name.trim(), zone: newAgent.zone.trim(), email: (newAgent.email || "").trim() });
      toast.success(t("admin.agentAdded")); setNewAgent({ phone: "", name: "", zone: "", email: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveAgentEmail = async (id) => {
    const email = (agentEmailInputs[id] || "").trim();
    if (!email) return toast.error("Entrez un email");
    try { await api.post(`/admin/agents/${id}/set-email`, { email }); toast.success("Email enregistré ✅"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remindLogin = async (id) => {
    try { await api.post(`/admin/agents/${id}/remind-login`); toast.success("Relance envoyée par email ✅"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const copyText = async (txt, label = "Code") => {
    try { await navigator.clipboard.writeText(String(txt)); toast.success(`${label} copié ✅`); }
    catch { toast.error("Copie impossible"); }
  };
  const regenAgentCode = async (id) => {
    if (!window.confirm("Générer un NOUVEAU code d'accès pour cet agent ? L'ancien code ne fonctionnera plus.")) return;
    try { const { data } = await api.post(`/admin/agents/${id}/regenerate-code`); toast.success(`Nouveau code : ${data.access_code}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const [badgeModal, setBadgeModal] = useState({ open: false, html: "", title: "" });
  const printAgentBadge = async (a) => {
    const code = a.access_code || "—";
    const isEu = a.zone === "europe";
    const site = isEu ? "https://www.noldgo.com" : "https://www.bentskingo.com";
    const brandName = isEu ? "Nold Go" : "BentSkin Go";
    const headBg = isEu ? "#0E1624" : "linear-gradient(135deg,#FFC531,#F26419)";
    const accent = isEu ? "#FFD23F" : "#F26419";
    const codeColor = isEu ? "#0E1624" : "#D94F2B";
    const url = `${site}/inscription?agent=${a.agent_code || ""}`;
    let qrDataUrl = "";
    try { qrDataUrl = await QRCode.toDataURL(url, { width: 360, margin: 1, errorCorrectionLevel: "H" }); } catch { /* ignore */ }
    const esc = (s) => String(s || "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Badge agent ${esc(a.name || a.agent_code)}</title>
<style>
  body{font-family:'Segoe UI',system-ui,sans-serif;margin:0;background:#F5F1E8;padding:24px;color:#1A1A1A}
  .card{max-width:380px;margin:0 auto;background:#fff;border-radius:20px;overflow:hidden;box-shadow:0 8px 30px rgba(0,0,0,.12)}
  .head{background:${headBg};padding:22px;text-align:center;color:#fff}
  .head h1{margin:0;font-size:22px;font-weight:800}
  .head p{margin:4px 0 0;font-size:12px;opacity:.9;letter-spacing:.12em;text-transform:uppercase}
  .body{padding:22px;text-align:center}
  .name{font-size:24px;font-weight:800;margin:0}
  .role{font-size:13px;color:#75807A;margin:2px 0 16px}
  .qr{width:220px;height:220px;margin:0 auto;display:block;border:1px solid #F0EBE1;border-radius:12px}
  .scan{font-size:12px;color:#75807A;margin:8px 0 18px}
  .codebox{background:#FFF7E8;border-radius:14px;padding:14px}
  .codebox .lbl{font-size:11px;color:#75807A;text-transform:uppercase;letter-spacing:.1em}
  .codebox .val{font-size:34px;font-weight:800;letter-spacing:.28em;color:${codeColor};font-family:monospace}
  .agentcode{margin-top:12px;font-size:12px;color:#75807A}
  .agentcode b{color:${accent}}
  @media print{body{background:#fff;padding:0}.card{box-shadow:none}}
</style></head><body>
<div class="card">
  <div class="head"><h1>${brandName}</h1><p>Agent officiel</p></div>
  <div class="body">
    <p class="name">${esc(a.name || "Agent")}</p>
    <p class="role">Carte agent · à présenter sur le terrain</p>
    ${qrDataUrl ? `<img class="qr" src="${qrDataUrl}" alt="QR"/>` : ""}
    <p class="scan">Scannez pour vous inscrire sous cet agent</p>
    <div class="codebox">
      <div class="lbl">Code de connexion (Espace Équipe)</div>
      <div class="val">${esc(code)}</div>
    </div>
    <div class="agentcode">Code agent : <b>${esc(a.agent_code || "—")}</b></div>
  </div>
</div></body></html>`;
    setBadgeModal({ open: true, html, title: `Badge — ${a.name || a.agent_code || "Agent"}` });
    // Backup fiable : télécharge aussi l'HTML pour ceux qui préfèrent l'ouvrir dans un onglet séparé
    try {
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const dlUrl = URL.createObjectURL(blob);
      const el = document.createElement("a");
      el.href = dlUrl;
      el.download = `badge-${(a.name || a.agent_code || "agent").replace(/[^a-z0-9]+/gi, "-")}.html`;
      document.body.appendChild(el); el.click(); el.remove();
      setTimeout(() => URL.revokeObjectURL(dlUrl), 60000);
    } catch { /* ignore */ }
  };
  const openMod = (type, target, row) => setModAction({ type, target, id: row.id, name: row.name || row.phone, reason: t("admin.defaultReason") });
  const confirmMod = async () => {
    const { type, target, id, reason } = modAction;
    const base = target === "driver" ? "drivers" : "agents";
    try {
      if (type === "delete") await api.delete(`/admin/${base}/${id}`, { params: { reason } });
      else if (type === "suspend") await api.post(`/admin/${base}/${id}/suspend`, { reason });
      else await api.post(`/admin/${base}/${id}/unsuspend`, {});
      toast.success("✓"); setModAction(null); load();
    } catch (e) {
      if (type === "delete" && e?.response?.status === 409) {
        if (window.confirm("Ce chauffeur est en pleine course. Annuler la course en cours et le supprimer quand même ?")) {
          try {
            await api.delete(`/admin/${base}/${id}`, { params: { reason, force: true } });
            toast.success("Chauffeur supprimé"); setModAction(null); load();
          } catch (e2) { toast.error(apiError(e2)); }
        }
        return;
      }
      toast.error(apiError(e));
    }
  };
  const doMerge = async () => {
    if (!mergeSrc || !mergeTargetId) return;
    setMergeBusy(true);
    try {
      const { data } = await api.post(`/admin/drivers/${mergeTargetId}/merge`, { source_id: mergeSrc.id });
      toast.success(`Fusion réussie ✅ ${Math.round(data.wallet_moved || 0)} FCFA transférés`);
      setMergeSrc(null); setMergeTargetId(""); setMergeQuery(""); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setMergeBusy(false); }
  };
  const markCollabPaid = async (code) => {
    try {
      const { data } = await api.post(`/admin/collab-codes/${code}/mark-paid`);
      toast.success(data.paid ? `Versement enregistré : ${data.paid.toLocaleString("fr-FR")} FCFA ✅` : "Rien à payer sur ce code");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveCollab = async (code) => {
    const e = collabEdits[code] || {};
    const row = (collabData.items || []).find((c) => c.code === code) || {};
    try {
      await api.post(`/admin/collab-codes/${code}`, { name: e.name ?? row.name ?? "", phone: e.phone ?? row.phone ?? "" });
      toast.success("Collaborateur enregistré ✅");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const creditActivation = async (id) => {    try {
      const { data } = await api.post(`/admin/drivers/${id}/credit-activation`);
      toast.success(`Portefeuille crédité de ${Math.round(data.credited || 200)} FCFA ✅`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const exportActivatedCsv = () => {
    const rows = activatedAccounts.items || [];
    if (!rows.length) return toast.error("Aucun compte activé à exporter.");
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Nom", "Role", "Vehicule", "Telephone", "Email", "Ville", "Active le", "Par admin"];
    const lines = rows.map((d) => [d.name || "", d.role === "passenger" ? "Passager" : "Chauffeur", d.vehicle_type || "", d.phone || "", d.email || "", d.city || "", d.activated_at ? new Date(d.activated_at).toLocaleString("fr-FR") : "", d.activated_by_admin ? "Oui" : "Non"].map(esc).join(","));
    const csv = "\uFEFF" + [header.map(esc).join(","), ...lines].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = `comptes-actives-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    toast.success(`${rows.length} compte(s) exporté(s) ✅`);
  };
  const purgeGhosts = async () => {
    if (!window.confirm(`Supprimer définitivement ${ghostCount} compte(s) chauffeur de test (sans nom ni téléphone) ? Aucun SMS ni email ne sera envoyé.`)) return;
    try {
      const { data } = await api.post("/admin/ghost-drivers/purge");
      toast.success(`${data.deleted || 0} compte(s) de test supprimé(s) ✅`);
      api.get("/admin/ghost-drivers").then((r) => setGhostCount(r.data.count || 0)).catch(() => {});
    api.get("/admin/sms-stats").then((r) => setSmsStats(r.data)).catch(() => {});
    api.get("/admin/activated-accounts").then((r) => setActivatedAccounts(r.data)).catch(() => {});
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const forceActivate = async (id) => {
    if (!window.confirm("Activer directement ce chauffeur (sans code SMS) ? Il pourra passer en ligne immédiatement, avec 3 mois gratuits.")) return;
    try {
      await api.post(`/admin/drivers/${id}/force-activate`);
      toast.success("Chauffeur activé ✅");
      api.get("/admin/activation-pending").then((r) => setActivationPending(r.data)).catch(() => {});
    api.get("/admin/ghost-drivers").then((r) => setGhostCount(r.data.count || 0)).catch(() => {});
    api.get("/admin/sms-stats").then((r) => setSmsStats(r.data)).catch(() => {});
    api.get("/admin/activated-accounts").then((r) => setActivatedAccounts(r.data)).catch(() => {});
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveBonus = async () => {
    try { const { data } = await api.put("/admin/agent-bonus", { amount: Number(bonusInput) || 0, threshold: Number(thresholdInput) || 0, om_asso: omAssoInput.trim(), momo_pay: momoPayInput.trim() }); setAgentBonus(data.amount); toast.success(t("admin.bonusSaved")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const viewRecruits = async (id) => {
    try { const { data } = await api.get(`/admin/agents/${id}/recruits`); setRecruitsModal(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const openPayout = async (agent) => {
    try { const { data } = await api.get(`/admin/agents/${agent.id}/payouts`); setPayoutModal({ agent, ...data }); setPayoutForm({ amount: String(data.due || 0), note: "" }); setPayoutNumInput(data.payout_number || agent.phone || ""); }
    catch (e) { toast.error(apiError(e)); }
  };
  const savePayoutNumber = async () => {
    try { const { data } = await api.put(`/admin/agents/${payoutModal.agent.id}/payout-number`, { number: payoutNumInput.trim() }); setPayoutModal((m) => ({ ...m, payout_number: data.payout_number })); toast.success(t("admin.numberSaved")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const unlockAgent = async (id) => {
    try { await api.post(`/admin/agents/${id}/unlock`); toast.success(t("admin.unlocked")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const approveAgent = async (id, name) => {
    if (!window.confirm(`Approuver ${name || "cet agent"} ? Il pourra alors se connecter à l'Espace Équipe.`)) return;
    try { await api.post(`/admin/agents/${id}/approve`); toast.success("Agent approuvé ✅"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remindPayout = async () => {
    try { await api.post(`/admin/agents/${payoutModal.agent.id}/remind-payout`); toast.success(t("admin.reminderSent")); }
    catch (e) { toast.error(apiError(e)); }
  };
  const downloadPayoutPdf = async (payoutId) => {
    try { await openPdf(`/admin/agents/${payoutModal.agent.id}/payouts/${payoutId}/pdf`, `prime-${payoutId.slice(0, 8)}.pdf`); }
    catch { toast.error("Erreur PDF"); }
  };
  const updateComplaint = async (id, patch) => {
    try { await api.patch(`/admin/complaints/${id}`, patch); toast.success("✓"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const approveAgentApp = async (id) => {
    try { await api.post(`/admin/agent-applications/${id}/approve`); toast.success(t("admin.appApproved")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const rejectAgentApp = async (id) => {
    try { await api.post(`/admin/agent-applications/${id}/reject`); toast.success(t("admin.appRejected")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const recordPayout = async () => {
    try {
      await api.post(`/admin/agents/${payoutModal.agent.id}/payouts`, { amount: Number(payoutForm.amount) || 0, note: payoutForm.note });
      toast.success("✓"); openPayout(payoutModal.agent);
    } catch (e) { toast.error(apiError(e)); }
  };
  const exportCsv = async () => {
    try {
      await downloadFile("/admin/agents/export", "agents-bentskin-go.csv");
    } catch (e) { toast.error(apiError(e)); }
  };
  const exportAgentActivity = async () => {
    try {
      await downloadFile("/admin/agent-activity/export", "activite-agents-bentskin-go.csv");
    } catch (e) { toast.error(apiError(e)); }
  };
  const loadDaily = (date) => api.get(`/admin/agent-activity/daily?date=${date}`).then((r) => setDailyData(r.data || { agents: [], totals: {}, bonus: 0 })).catch(() => {});
  const exportAgentDaily = async () => {
    try {
      await downloadFile(`/admin/agent-activity/daily/export?date=${dailyDate}`, `compteur-journalier-agents-${dailyDate}.csv`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const replyExpense = async (id) => {
    const text = (expReply[id] || "").trim();
    if (!text) return;
    try {
      await api.post(`/expenses/${id}/reply`, { text });
      setExpReply((s) => ({ ...s, [id]: "" }));
      const r = await api.get("/admin/expenses"); setExpenses(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  const exportLog = async () => {
    try {
      const qs = new URLSearchParams();
      if (logAction) qs.append("action", logAction);
      if (logRole) qs.append("role", logRole);
      const url = `/admin/moderation-log/export${qs.toString() ? "?" + qs.toString() : ""}`;
      await downloadFile(url, "journal-moderation.csv");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#FDF8F0]">
      <AppHeader variant="app" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <aside className="lg:w-64 shrink-0">
          <h1 className="font-head font-black text-xl tracking-tight mb-4 px-1">{t("admin.title")}</h1>
          <nav className="flex lg:flex-col gap-1.5 lg:gap-1 overflow-x-auto no-scrollbar lg:bg-white lg:border lg:border-[#ECE7DE] lg:rounded-2xl lg:p-2 lg:shadow-[0_2px_16px_rgba(17,24,20,0.04)]">
            {SECTIONS.filter((s) => {
              if (isNoldDomain() && NOLD_HIDDEN_SECTIONS.has(s.id)) return false;
              if (!isNoldDomain() && BENTSKIN_HIDDEN_SECTIONS.has(s.id)) return false;
              return true;
            }).map((s) => {
              const active = section === s.id;
              return (
              <button key={s.id} data-testid={`admin-nav-${s.id}`} onClick={() => setSection(s.id)}
                className={`group relative flex items-center gap-3 pl-4 pr-3 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all duration-200 border lg:border-0 ${active ? "bg-[#FEF1E7] text-[#D8531E] lg:bg-[#FEF1E7] border-[#D94F2B]" : "bg-white lg:bg-transparent border-[#E2DFD8] text-[#5A635D] hover:bg-[#F7F4EF] hover:text-[#1A1A1A]"}`}>
                <span className={`hidden lg:block absolute left-0 top-1/2 -translate-y-1/2 w-1 rounded-r-full transition-all duration-200 ${active ? "h-6 bg-[#F26419]" : "h-0 bg-transparent"}`} />
                <span className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${active ? "bg-[#F26419] text-white" : "bg-[#F2EEE7] text-[#75807A] group-hover:bg-[#EAE4D9]"}`}><s.icon className="w-4 h-4" /></span>
                <span className="flex-1 text-left">{t(`admin.${s.key}`)}</span>
                {s.id === "drivers" && stats?.pending_kyc > 0 && (
                  <span data-testid="kyc-pending-badge" className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#D32F2F] text-white text-[11px] font-bold">{stats.pending_kyc}</span>
                )}
                {s.id === "activation" && activationPending?.count > 0 && (
                  <span data-testid="activation-badge" className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#D32F2F] text-white text-[11px] font-bold">{activationPending.count}</span>
                )}
                {s.id === "review" && drivers.filter((d) => d.kyc?.ai_suspicious && d.kyc_status === "pending").length > 0 && (
                  <span data-testid="review-badge" className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#D32F2F] text-white text-[11px] font-bold">{drivers.filter((d) => d.kyc?.ai_suspicious && d.kyc_status === "pending").length}</span>
                )}
                {s.id === "complaints" && stats?.open_complaints > 0 && (
                  <span data-testid="complaints-badge" className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#D32F2F] text-white text-[11px] font-bold">{stats.open_complaints}</span>
                )}
                {s.id === "restaurateurs" && restaurateurs?.count > 0 && (
                  <span data-testid="restaurateurs-badge" className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#F26419] text-white text-[11px] font-bold">{restaurateurs.count}</span>
                )}
                {s.id === "team" && stats?.pending_agent_apps > 0 && (
                  <span data-testid="team-apps-badge" className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#F26419] text-[#1A1A1A] text-[11px] font-bold">{stats.pending_agent_apps}</span>
                )}
                {s.id === "expenses" && expenses?.pending_count > 0 && (
                  <span data-testid="expenses-badge" className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#F26419] text-[#1A1A1A] text-[11px] font-bold">{expenses.pending_count}</span>
                )}
                {s.id === "recharges" && recharges?.pending_count > 0 && (
                  <span data-testid="recharges-badge" className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#D32F2F] text-white text-[11px] font-bold">{recharges.pending_count}</span>
                )}
                {s.id === "contracts" && contracts?.pending_count > 0 && (
                  <span data-testid="contracts-badge" className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#F26419] text-[#1A1A1A] text-[11px] font-bold">{contracts.pending_count}</span>
                )}
                {s.id === "mail" && mailUnread > 0 && (
                  <span data-testid="mail-badge" className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#D32F2F] text-white text-[11px] font-bold">{mailUnread}</span>
                )}
              </button>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 min-w-0">
          <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
            {isSuperAdmin && (
              <>
                <button data-testid="admin-view-passenger" onClick={() => (window.location.href = "/passenger")} className="px-3 py-1.5 rounded-full bg-[#F0EBE1] text-[#1A1A1A] text-sm font-bold hover:bg-black/5">👤 Écran Passager</button>
                <button data-testid="admin-view-driver" onClick={() => (window.location.href = "/driver")} className="px-3 py-1.5 rounded-full bg-[#F0EBE1] text-[#1A1A1A] text-sm font-bold hover:bg-black/5">🏍️ Écran Chauffeur</button>
              </>
            )}
            <AdminZoneSwitch />
          </div>
          {section === "stats" && stats?.pending_recharges > 0 && (
            <button data-testid="recharge-alert-banner" onClick={() => setSection("recharges")}
              className="w-full mb-4 flex items-center gap-3 rounded-2xl bg-[#D32F2F] text-white px-5 py-4 text-left hover:opacity-95 transition-opacity">
              <Wallet className="w-5 h-5 shrink-0" />
              <span className="flex-1 font-semibold text-sm">{stats.pending_recharges} recharge{stats.pending_recharges > 1 ? "s" : ""} en attente de validation — cliquez pour vérifier</span>
              <span className="inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full bg-white text-[#D32F2F] text-xs font-bold">{stats.pending_recharges}</span>
            </button>
          )}
          {section === "stats" && stats && (
            <SeedTestsCard />
          )}

          {section === "stats" && stats && (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {(() => {
                const eur = (n) => `${(Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
                const nold = isNoldDomain();
                const revenueVal = nold ? eur(stats.europe?.stripe_total_eur || 0) : fcfa(stats.revenue);
                const commissionVal = nold ? eur(stats.europe?.commission_eur || 0) : fcfa(stats.commission);
                return [
                  { label: t("admin.revenue"), val: revenueVal, icon: TrendingUp, c: "#D94F2B" },
                  { label: t("admin.commissions"), val: commissionVal, icon: Wallet, c: "#F26419" },
                  { label: t("admin.rides"), val: stats.total_rides, icon: Route, c: "#D94F2B" },
                  { label: t("admin.pendingKyc"), val: stats.pending_kyc, icon: Clock, c: "#F26419" },
                  { label: t("admin.driversCount"), val: stats.drivers, icon: Bike, c: "#1A1A1A" },
                  { label: t("admin.approvedDrivers"), val: stats.approved_drivers ?? 0, icon: Check, c: "#16a34a" },
                  { label: t("admin.onlineNow"), val: stats.online_drivers ?? 0, icon: Bike, c: "#16a34a" },
                  { label: t("admin.passengersCount"), val: stats.passengers, icon: Users, c: "#D94F2B" },
                  { label: t("rideStatus.completed"), val: stats.completed_rides, icon: Check, c: "#D94F2B" },
                ];
              })().map((k, i) => (
                <div key={i} data-testid={`stat-${i}`} className="bg-white rounded-2xl border border-[#E2DFD8] p-5">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${k.c}20` }}><k.icon className="w-5 h-5" style={{ color: k.c }} /></div>
                  <div className="font-head font-black text-2xl mt-3">{k.val}</div>
                  <div className="text-xs text-[#75807A] mt-0.5">{k.label}</div>
                </div>
              ))}
            </div>
          )}

          {section === "stats" && stats?.europe && (
            <div data-testid="europe-panel" className="mt-6 rounded-3xl border-2 border-[#2563EB] bg-[#2563EB]/[0.04] p-5 sm:p-6">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-xl">🇪🇺</span>
                  <h3 className="font-head font-black text-lg text-[#1A1A1A]">Zone Europe · Stripe (€) — temps réel</h3>
                </div>
                <div className="inline-flex items-center gap-2 flex-wrap">
                  <button data-testid="eu-transfers-btn" onClick={async () => {
                    const now = new Date();
                    const defMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                    const m = window.prompt("Mois à consulter (YYYY-MM) — vide = tout :", defMonth);
                    if (m === null) return;
                    try {
                      const url = m ? `/restaurateur/stripe-connect/admin/transfers?month=${encodeURIComponent(m)}` : '/restaurateur/stripe-connect/admin/transfers';
                      const res = await api.get(url);
                      const d = res.data;
                      const msg = `Transfers Stripe ${m || 'tout'} :\n\n` +
                        `🍽️ Restos : ${d.counts.resto} virements = ${d.totals.resto.toFixed(2)}€\n` +
                        `🛵 Livreurs : ${d.counts.driver} virements = ${d.totals.driver.toFixed(2)}€\n` +
                        `💝 Pourboires : ${d.counts.tip} virements = ${d.totals.tip.toFixed(2)}€\n` +
                        `━━━━━━━━━━━━━━\nTotal versé : ${d.total_all_eur.toFixed(2)}€ (${d.counts.driver + d.counts.resto + d.counts.tip} transferts)`;
                      window.alert(msg);
                    } catch (e) { window.alert('Erreur : ' + (e?.response?.data?.detail || e?.message)); }
                  }} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-colors">
                    <Download className="w-4 h-4" /> Transfers Stripe
                  </button>
                  <button data-testid="eu-commissions-pdf-btn" onClick={async () => {
                    const now = new Date();
                    const defMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                    const m = window.prompt("Mois à exporter (format YYYY-MM) :", defMonth);
                    if (!m) return;
                    try {
                      await downloadPdf(`/restaurateur/admin/commissions/monthly.pdf?month=${encodeURIComponent(m)}`, `nold-go-commissions-${m}.pdf`);
                    } catch (e) { window.alert('Export PDF impossible : ' + (e?.response?.data?.detail || e?.message || 'erreur')); }
                  }} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-[#F26419] hover:bg-[#D94D0E] text-white text-sm font-bold transition-colors">
                    <Download className="w-4 h-4" /> Récap commissions (PDF)
                  </button>
                  <button data-testid="eu-export-btn" onClick={async () => {
                    try {
                      await downloadFile('/admin/europe/export', 'europe-stripe-bentskin-go.csv');
                    } catch (e) { window.alert('Export impossible : ' + (e?.response?.data?.detail || e?.message || 'erreur')); }
                  }} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-[#2563EB] hover:bg-[#1d4fd7] text-white text-sm font-bold transition-colors">
                    <Download className="w-4 h-4" /> Export CSV / Excel
                  </button>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
                {(() => {
                  const eu = stats.europe;
                  const eur = (n) => `${(Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
                  const cards = [
                    { label: "Encaissé Stripe", val: eur(eu.stripe_total_eur), sub: `${eu.paid_orders} paiement(s)`, c: "#2563EB", tid: "eu-stripe-total" },
                    { label: `Commission (${Math.round((eu.commission_rate || 0) * 100)}%)`, val: eur(eu.commission_eur), sub: "calculée automatiquement", c: "#EC407A", tid: "eu-commission" },
                    { label: "Net chauffeurs", val: eur(eu.net_eur), sub: "après commission", c: "#16a34a", tid: "eu-net" },
                    { label: "Courses en cours", val: eu.active_count, sub: "Bruxelles / Europe", c: "#1A1A1A", tid: "eu-active-count" },
                  ];
                  return cards.map((k, i) => (
                    <div key={i} data-testid={k.tid} className="bg-white rounded-2xl border border-[#E2DFD8] p-4">
                      <div className="font-head font-black text-2xl" style={{ color: k.c }}>{k.val}</div>
                      <div className="text-xs font-semibold text-[#1A1A1A] mt-1">{k.label}</div>
                      <div className="text-[11px] text-[#75807A]">{k.sub}</div>
                    </div>
                  ));
                })()}
              </div>
              <div className="mt-5">
                <div className="text-xs font-bold uppercase tracking-wide text-[#75807A] mb-2">Courses en cours à Bruxelles / Europe</div>
                {(stats.europe.active_rides || []).length === 0 ? (
                  <div data-testid="eu-active-empty" className="rounded-2xl bg-white border border-[#E2DFD8] p-4 text-sm text-[#75807A]">Aucune course Europe en cours pour le moment.</div>
                ) : (
                  <div className="space-y-2" data-testid="eu-active-list">
                    {stats.europe.active_rides.map((r) => (
                      <div key={r.id} data-testid={`eu-ride-${r.id}`} className="rounded-2xl bg-white border border-[#E2DFD8] p-4 flex items-center gap-3">
                        <span className="w-9 h-9 rounded-xl bg-[#2563EB]/10 flex items-center justify-center shrink-0">
                          {r.type === "delivery" ? <Bike className="w-4 h-4 text-[#16a34a]" /> : <Route className="w-4 h-4 text-[#2563EB]" />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-[#1A1A1A] truncate">{r.pickup} → {r.destination}</div>
                          <div className="text-[11px] text-[#75807A]">{r.passenger_name || "Passager"}{r.driver_name ? ` · ${r.driver_name}` : ""} · {r.type === "delivery" ? "Colis Vélo" : "Voiture"}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-head font-bold text-[#1A1A1A]">{(Number(r.price) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
                          <span className="inline-block mt-0.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#EC407A]/10 text-[#EC407A] uppercase tracking-wide">{r.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {section === "stats" && stats && (
            <div className="mt-4 grid lg:grid-cols-2 gap-4">
              <div className="lg:col-span-2 bg-white rounded-2xl border-2 border-[#F26419]/40 p-4 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="font-head font-bold text-base text-[#1A1A1A]">💰 Récap primes agents</h3>
                  <p className="text-xs text-[#75807A]">Exporte les primes/dépenses du mois en CSV pour ta paie</p>
                </div>
                <div className="flex items-center gap-2">
                  {[0, 1, 2].map((offset) => {
                    const d = new Date(); d.setMonth(d.getMonth() - offset);
                    const y = d.getFullYear(), m = d.getMonth() + 1;
                    const label = d.toLocaleDateString("fr-BE", { month: "long", year: "numeric" });
                    return (
                      <button key={offset}
                        type="button"
                        data-testid={`admin-agent-csv-${offset}`}
                        onClick={async () => {
                          try {
                            await downloadFile(`/admin/agents/bonus-export.csv?year=${y}&month=${m}`, `nold-go-primes-agents-${y}-${String(m).padStart(2, "0")}.csv`);
                          } catch (e) { toast.error(apiError(e)); }
                        }}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#F26419] hover:bg-[#D94D0E] text-white text-xs font-bold capitalize">
                        📥 {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="lg:col-span-2">
                <VisitorsWidget />
              </div>
              <div data-testid="chart-rides-per-day" className="bg-white rounded-2xl border border-[#E2DFD8] p-5">
                <h3 className="font-head font-bold flex items-center gap-2"><Route className="w-4 h-4 text-[#D94F2B]" /> {t("admin.perDay")}</h3>
                <div className="mt-5 flex items-end gap-2 h-40">
                  {(stats.rides_per_day || []).length === 0 && <span className="text-sm text-[#75807A]">—</span>}
                  {(stats.rides_per_day || []).map((d, i) => {
                    const max = Math.max(...stats.rides_per_day.map((x) => x.rides), 1);
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1">
                        <span className="text-xs font-bold text-[#1A1A1A]">{d.rides}</span>
                        <div className="w-full rounded-t-lg bg-gradient-to-t from-[#D94F2B] to-[#F26419]" style={{ height: `${(d.rides / max) * 100}%`, minHeight: 6 }} />
                        <span className="text-[10px] text-[#75807A]">{d.date.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div data-testid="active-drivers-list" className="bg-white rounded-2xl border border-[#E2DFD8] p-5">
                <h3 className="font-head font-bold flex items-center gap-2"><Bike className="w-4 h-4 text-[#D94F2B]" /> {t("admin.activeDrivers")} ({(stats.active_drivers || []).length})</h3>
                <div className="mt-3 space-y-2 max-h-52 overflow-y-auto no-scrollbar">
                  {(stats.active_drivers || []).map((d) => (
                    <div key={d.id} className="flex items-center justify-between border-b border-[#F0EBE1] pb-2">
                      <div>
                        <div className="font-medium text-sm">{d.name}</div>
                        <div className="text-xs text-[#75807A]">{d.vehicle || "—"} · {d.plate || "—"}</div>
                      </div>
                      <span className="flex items-center gap-1 text-sm font-bold"><Star className="w-3.5 h-3.5 fill-[#F26419] text-[#F26419]" />{Number(d.rating || 0).toFixed(1)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {!isNoldDomain() && payout && (
                <div data-testid="payout-card" className="lg:col-span-2 bg-gradient-to-br from-[#F26419] to-[#D94F2B] rounded-2xl p-5 text-[#1A1A1A]">
                  <h3 className="font-head font-black flex items-center gap-2"><Wallet className="w-5 h-5" /> {t("admin.payout")}</h3>
                  <p className="text-sm text-[#1A1A1A]/80 mt-1">{t("admin.payoutDesc")} · {fcfa(stats.commission)} {t("admin.collected")}</p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <div className="flex gap-2">
                      {["orange", "mtn"].map((op) => (
                        <button key={op} data-testid={`payout-op-${op}`} onClick={() => savePayout(op)}
                          className={`h-12 px-4 rounded-xl font-bold text-sm ${payout.operator === op ? "bg-[#1A1A1A] text-white" : "bg-white/40 text-[#1A1A1A]"}`}>
                          {op === "orange" ? "Orange Money" : "MTN MoMo"}
                        </button>
                      ))}
                    </div>
                    <input data-testid="payout-number" value={payoutNumber} onChange={(e) => setPayoutNumber(e.target.value)} placeholder="Numéro Orange Cameroun (+237...)" className="flex-1 h-12 rounded-xl px-4 text-sm border-2 border-white/50 bg-white/80 focus:outline-none" />
                    <button data-testid="payout-save" onClick={() => savePayout()} className="h-12 px-5 rounded-xl bg-[#1A1A1A] text-white font-bold text-sm">{t("common.save")}</button>
                  </div>
                </div>
              )}
              {promo && (
                <div data-testid="promo-admin-card" className="lg:col-span-2 bg-[#1A1A1A] rounded-2xl p-5 text-white">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <h3 className="font-head font-black flex items-center gap-2"><Clock className="w-5 h-5 text-[#F26419]" /> {t("admin.promoSection")}</h3>
                      <p className="mt-1 text-sm text-white/70">{t("admin.promoCommissionNote")}</p>
                      <div className="mt-2 text-sm">
                        <span data-testid="promo-status" className={`px-2 py-0.5 rounded-full font-semibold text-white ${promo.free ? "bg-[#16a34a]" : "bg-[#D32F2F]"}`}>{promo.free ? t("admin.promoActiveLabel") : t("admin.promoEndedLabel")}</span>
                        {promo.free && promo.free_until && <span className="ml-2 text-white/60">{t("admin.promoUntil")} {new Date(promo.free_until).toLocaleDateString("fr-FR")}</span>}
                      </div>
                    </div>
                    {promo.free ? (
                      <div className="flex gap-2">
                        <button data-testid="promo-extend" onClick={extendPromo} className="h-11 px-4 rounded-xl bg-white/10 hover:bg-white/20 font-semibold text-sm transition-colors">{t("admin.promoExtend")}</button>
                        <button data-testid="promo-activate" onClick={endPromo} className="h-11 px-4 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-[#1A1A1A] font-bold text-sm transition-colors">{t("admin.promoActivateNow")}</button>
                      </div>
                    ) : (
                      <button data-testid="promo-restart" onClick={restartPromo} className="h-11 px-4 rounded-xl bg-[#16a34a] hover:bg-[#15803d] text-white font-bold text-sm transition-colors">{t("admin.promoRestart")}</button>
                    )}
                  </div>
                </div>
              )}
              {whatsapp && (
                <div data-testid="whatsapp-admin-card" className="lg:col-span-2 bg-white rounded-2xl p-5 border border-[#E2DFD8]">
                  <div className="flex items-center gap-2">
                    <h3 className="font-head font-black flex items-center gap-2 text-[#1A1A1A]"><span className="text-[#25D366] text-lg">✆</span> {t("admin.waTitle")}</h3>
                    <span data-testid="whatsapp-status" className={`px-2 py-0.5 rounded-full text-xs font-semibold text-white ${whatsapp.configured ? "bg-[#25D366]" : "bg-[#75807A]"}`}>{whatsapp.configured ? t("admin.waActive") : t("admin.waInactive")}</span>
                  </div>
                  <p className="mt-1 text-sm text-[#75807A]">{t("admin.waNote")}</p>
                  <div className="mt-3 flex flex-wrap gap-2 items-center">
                    <Input data-testid="whatsapp-input" value={whatsappInput} onChange={(e) => setWhatsappInput(e.target.value)} placeholder="+14155238886" className="h-11 rounded-xl bg-white border-[#E2DFD8] max-w-xs" />
                    <Button data-testid="whatsapp-save" onClick={saveWhatsapp} className="h-11 px-5 rounded-xl bg-[#25D366] hover:bg-[#1da851] text-white font-bold">{t("admin.waSave")}</Button>
                    {whatsappInput && <Button data-testid="whatsapp-clear" onClick={() => { setWhatsappInput(""); }} className="h-11 px-4 rounded-xl bg-white border border-[#E2DFD8] text-[#75807A]">{t("admin.waClear")}</Button>}
                  </div>
                  {!whatsapp.configured && whatsapp.sms_active && <p className="mt-2 text-xs text-[#B8860B]">{t("admin.waFallback")}</p>}
                </div>
              )}

              <div data-testid="taxi-kyc-policy-card" className="lg:col-span-2 bg-white rounded-2xl p-5 border border-[#E2DFD8]">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <h3 className="font-head font-black flex items-center gap-2 text-[#1A1A1A]">📄 Documents chauffeurs (tous véhicules)</h3>
                    <p className="mt-1 text-sm text-[#75807A]">{taxiDocsRequired ? "Tous les chauffeurs doivent fournir leurs pièces (CNI, selfie, carte grise/plaque, permis pour la voiture)." : "Documents facultatifs au lancement — les chauffeurs peuvent s'inscrire sans aucune pièce. Activez l'obligation quand la start-up aura grandi."}</p>
                  </div>
                  <button data-testid="taxi-docs-toggle" onClick={toggleTaxiDocs} className={`inline-flex items-center gap-2 h-11 px-5 rounded-xl font-bold text-sm transition-colors ${taxiDocsRequired ? "bg-[#D32F2F] text-white" : "bg-[#16a34a] text-white"}`}>
                    {taxiDocsRequired ? "Documents OBLIGATOIRES" : "Documents facultatifs"}
                  </button>
                </div>
                <button data-testid="relance-inscription" onClick={relanceInscription} className="mt-3 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-[#F26419] hover:bg-[#d9540f] text-white text-sm font-bold">📣 Relancer les chauffeurs à l'inscription incomplète</button>
                <p className="mt-1.5 text-xs text-[#75807A]">Une relance automatique est aussi envoyée (WhatsApp/SMS + notification), jusqu'à 3 fois espacées de 48h.</p>
              </div>

              <div data-testid="abandoned-signups-card" className="lg:col-span-2 bg-white rounded-2xl p-5 border border-[#E2DFD8]">
                <h3 className="font-head font-black flex items-center gap-2 text-[#1A1A1A]">📋 Inscriptions abandonnées <span className="text-sm font-semibold text-[#75807A]">({incompleteDrivers.length})</span></h3>
                <p className="mt-1 text-xs text-[#75807A]">Chauffeurs qui n'ont pas finalisé leur inscription. Cliquez sur l'enveloppe pour relancer l'un d'eux.</p>
                <div className="mt-3 space-y-2 max-h-80 overflow-y-auto">
                  {incompleteDrivers.length === 0 && <p data-testid="abandoned-empty" className="text-sm text-[#75807A] py-4 text-center">Aucune inscription abandonnée 🎉</p>}
                  {incompleteDrivers.map((d) => (
                    <div key={d.id} data-testid={`abandoned-${d.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-[#E2DFD8] p-2.5">
                      <div className="min-w-0">
                        <div className="font-semibold text-[#1A1A1A] truncate">{d.name}</div>
                        <div className="text-xs text-[#75807A]">{d.phone} · {d.reminders} relance(s){d.last_reminder ? ` · dernière : ${new Date(d.last_reminder).toLocaleDateString("fr-FR")}` : ""}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button data-testid={`approve-signup-${d.id}`} onClick={() => approveSignup(d.id, d.name)} title="Approuver et activer ce chauffeur" className="inline-flex items-center gap-1 h-10 px-3 rounded-xl bg-[#16a34a] hover:bg-[#128a3e] text-white text-xs font-bold transition-colors"><Check className="w-4 h-4" /> Approuver</button>
                        <button data-testid={`wa-relance-${d.id}`} onClick={() => waRelance(d.phone, d.name)} title="Relancer sur WhatsApp (gratuit)" className="w-10 h-10 rounded-xl bg-[#25D366] hover:bg-[#1da851] text-white flex items-center justify-center transition-colors"><MessageCircle className="w-4 h-4" /></button>
                        <button data-testid={`relance-one-${d.id}`} onClick={() => relanceOne(d.id)} title="Envoyer un message de relance" className="w-10 h-10 rounded-xl bg-[#F26419] hover:bg-[#d9540f] text-white flex items-center justify-center transition-colors"><Mail className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {section === "pay" && (
            <div data-testid="admin-pay-section" className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-head font-black text-xl text-[#1A1A1A]">{isNoldDomain() ? "Transferts Nold Go Pay" : "Transferts BentSkin Go Pay"}</h2>
                  <p className="text-sm text-[#75807A]">Paie le bénéficiaire depuis ton OM/MoMo, puis marque « versé ».</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-[#75807A]">{payOrders.length} transfert(s)</span>
                  <a data-testid="admin-open-pay" href="/pay" target="_blank" rel="noopener noreferrer" className="h-10 px-4 rounded-xl bg-[#00A651] hover:bg-[#00863f] text-white text-sm font-bold inline-flex items-center gap-2 transition-colors">
                    <Banknote className="w-4 h-4" /> Envoyer de l'argent
                  </a>
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-[#E2DFD8] overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-[#F0EBE1] text-[#75807A] text-xs uppercase"><tr>
                    <th className="text-left p-3">Client</th><th className="text-left p-3">Reçoit</th><th className="text-left p-3">Bénéficiaire</th><th className="text-left p-3">Statut</th><th className="text-right p-3">Actions</th>
                  </tr></thead>
                  <tbody>
                    {payOrders.length === 0 && <tr><td colSpan={5} data-testid="pay-empty" className="p-6 text-center text-[#75807A]">Aucun transfert pour l'instant.</td></tr>}
                    {payOrders.map((o) => {
                      const paid = o.payment_status === "paid";
                      return (
                        <tr key={o.session_id} data-testid={`pay-order-${o.session_id}`} className="border-t border-[#E2DFD8]">
                          <td className="p-3"><div className="font-medium text-[#1A1A1A]">{o.nom_client || "—"}</div><div className="text-xs text-[#75807A]">{o.tel_client}</div></td>
                          <td className="p-3"><div className="font-bold text-[#16a34a]">{fcfa(o.fcfa_recu)}</div><div className="text-xs text-[#75807A]">{o.montant_eur} €</div></td>
                          <td className="p-3"><div className="font-medium text-[#1A1A1A]">{o.num_beneficiaire}</div><div className="text-xs text-[#75807A]">{o.reseau === "MoMo" ? "MTN MoMo" : "Orange Money"}</div></td>
                          <td className="p-3">
                            {!paid ? <span className="text-xs font-semibold px-2 py-1 rounded-full bg-[#F0EBE1] text-[#75807A]">En attente paiement</span>
                              : o.payout_done ? <span className="text-xs font-semibold px-2 py-1 rounded-full bg-[#16a34a]/15 text-[#16a34a]">Versé ✅</span>
                                : <span className="text-xs font-semibold px-2 py-1 rounded-full bg-[#F26419]/15 text-[#D94F2B]">À verser</span>}
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex gap-2 justify-end">
                              <button data-testid={`pay-wa-${o.session_id}`} onClick={() => waProof(o)} title="Envoyer la preuve au client sur WhatsApp" className="h-9 px-3 rounded-lg bg-[#25D366] hover:bg-[#1da851] text-white text-xs inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" /> Preuve</button>
                              {paid && !o.payout_done && <button data-testid={`pay-mark-${o.session_id}`} onClick={() => markPaidOut(o.session_id)} className="h-9 px-3 rounded-lg bg-[#16a34a] hover:bg-[#128a3e] text-white text-xs inline-flex items-center gap-1"><Check className="w-3 h-3" /> Marquer versé</button>}
                            </div>
                          </td>
                        </tr>);
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}


          {section === "paysettings" && (
            <div data-testid="admin-paysettings" className="max-w-lg space-y-6">
              <div>
                <h2 className="font-head font-black text-xl text-[#1A1A1A]">Paramètres Paiement</h2>
                <p className="text-sm text-[#75807A]">Numéros marchands affichés au client pour payer sa course par transfert manuel (MoMo / Orange Money).</p>
              </div>
              <div className="bg-white rounded-2xl border border-[#E2DFD8] p-5 space-y-4">
                <div><label className="text-xs text-[#75807A]">N° MoMo Pay</label><Input data-testid="cfg-momo" value={payCfg.momo_pay_number || ""} onChange={(e) => setPayCfg({ ...payCfg, momo_pay_number: e.target.value })} className="h-11 rounded-lg border-2 mt-1" placeholder="+237..." /></div>
                <div><label className="text-xs text-[#75807A]">N° Orange Money ASSO</label><Input data-testid="cfg-om" value={payCfg.om_asso_number || ""} onChange={(e) => setPayCfg({ ...payCfg, om_asso_number: e.target.value })} className="h-11 rounded-lg border-2 mt-1" placeholder="+237..." /></div>
                <div><label className="text-xs text-[#75807A]">Nom du marchand</label><Input data-testid="cfg-name" value={payCfg.display_name || ""} onChange={(e) => setPayCfg({ ...payCfg, display_name: e.target.value })} className="h-11 rounded-lg border-2 mt-1" /></div>
                <div><label className="text-xs text-[#75807A]">Instructions au client</label><Input data-testid="cfg-instr" value={payCfg.instructions || ""} onChange={(e) => setPayCfg({ ...payCfg, instructions: e.target.value })} className="h-11 rounded-lg border-2 mt-1" /></div>
                <Button data-testid="save-paysettings" onClick={savePayCfg} className="w-full h-11 rounded-lg bg-[#1A1A1A] text-white text-sm">Enregistrer</Button>
              </div>

              <div>
                <h2 className="font-head font-black text-xl text-[#1A1A1A]">Coordonnées bancaires Nold Go</h2>
                <p className="text-sm text-[#75807A]">IBAN affiché sur les factures PDF (frais d'activation 195 €). Modification prise en compte sur toutes les nouvelles factures générées.</p>
              </div>
              <div className="bg-white rounded-2xl border border-[#E2DFD8] p-5 space-y-4">
                <div>
                  <label className="text-xs text-[#75807A]">IBAN (Belgique)</label>
                  <Input
                    data-testid="cfg-nold-iban"
                    value={noldCompany.iban || ""}
                    onChange={(e) => setNoldCompany({ ...noldCompany, iban: e.target.value.toUpperCase() })}
                    className="h-11 rounded-lg border-2 mt-1 font-mono tracking-wider"
                    placeholder="BE00 0000 0000 0000"
                    maxLength={24}
                  />
                  <p className="text-[11px] text-[#75807A] mt-1">Format BE + 14 chiffres. Espaces facultatifs.</p>
                </div>
                <Button data-testid="save-nold-iban" onClick={saveNoldCompany} className="w-full h-11 rounded-lg bg-[#059669] text-white text-sm font-semibold">Enregistrer l'IBAN</Button>
              </div>
            </div>
          )}

          {section === "assistedmenus" && (
            <>
              <AssistedMenusAdmin />
              <PrintStatsAdmin />
            </>
          )}

          {section === "restaurateurs" && (
            <div data-testid="admin-restaurateurs" className="space-y-4">
              {/* Onglets Prospection Restaurants / Chauffeurs */}
              <div className="bg-white rounded-2xl border border-[#E2DFD8] p-4">
                <div className="flex gap-2 mb-4 border-b border-[#F0EBE1] overflow-x-auto">
                  {[
                    { k: "resto",  l: "🏪 Prospection Restaurants" },
                    { k: "driver", l: "🚴 Recrutement Chauffeurs" },
                    { k: "nicole", l: "🤖 Appels Sara Dupont" },
                  ].map((t) => (
                    <button key={t.k}
                      data-testid={`admin-prospect-tab-${t.k}`}
                      onClick={() => setProspectTab(t.k)}
                      className={`px-4 h-10 text-sm font-bold whitespace-nowrap shrink-0 transition-colors border-b-2 -mb-px ${prospectTab === t.k ? "text-[#F26419] border-[#F26419]" : "text-[#75807A] border-transparent hover:text-[#1A1A1A]"}`}>
                      {t.l}
                    </button>
                  ))}
                </div>
                {prospectTab === "resto"
                  ? <ProspectionTab importPath="/admin/prospects/import-brussels" offerPath="/agent/prospects/{id}/send-offer" nicole />
                  : prospectTab === "nicole" ? <NicoleCallsTab /> : <DriverProspectionTab />}
              </div>

              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-head font-black text-xl text-[#1A1A1A]">🍔 Dossiers Restaurateurs</h2>
                  <p className="text-sm text-[#75807A]">Nold Go · dossiers en attente de validation (TVA BCE + AFSCA)</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button data-testid="rt-admin-create-btn" onClick={() => setRtCreateOpen(true)} className="h-10 rounded-xl bg-gradient-to-r from-[#7C3AED] to-[#F26419] hover:opacity-90 text-white font-bold text-sm px-4">
                    + Créer un restaurant
                  </Button>
                  <Button data-testid="rt-admin-scrape-btn" onClick={() => setScrapeOpen(true)} className="h-10 rounded-xl bg-gradient-to-r from-[#059669] to-[#0284C7] hover:opacity-90 text-white font-bold text-sm px-4">
                    🌐 Créer depuis un site
                  </Button>
                  <div data-testid="rt-count-badge" className="inline-flex items-center gap-1.5 rounded-full bg-[#F26419]/10 border border-[#F26419]/30 text-[#F26419] text-xs font-bold px-3 py-1.5">
                    {restaurateurs.count} en attente
                  </div>
                </div>
              </div>

              {/* Pack Administratif Nold Go — téléchargement PDF (Admin) */}
              <div className="bg-white rounded-2xl border border-[#E2DFD8] p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-head font-bold text-lg text-[#1A1A1A]">📁 Pack Administratif Nold Go</h3>
                    <p className="text-xs text-[#75807A]">Documents PDF officiels — contrat, manuels, brochure commerciale</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { k: "contract", label: "Contrat template", icon: "📄" },
                    { k: "manual-resto", label: "Manuel Restaurateur", icon: "📘" },
                    { k: "manual-livreur", label: "Manuel Livreur vélo", icon: "🚲" },
                    { k: "brochure", label: "Brochure commerciale", icon: "✨" },
                  ].map((d) => (
                    <div key={d.k} className="rounded-xl border border-[#E2DFD8] hover:border-[#F26419] bg-[#FAF5EC] p-3 transition-colors">
                      <div className="text-lg">{d.icon}</div>
                      <div className="mt-1 text-xs font-semibold text-[#1A1A1A]">{d.label}</div>
                      <div className="mt-2 flex gap-1">
                        <button data-testid={`admin-doc-${d.k}-view`}
                          onClick={() => showPdf(d.label, `/restaurateur/admin/documents/${d.k}.pdf`, `nold-go-${d.k}.pdf`)}
                          className="flex-1 h-8 rounded-lg bg-[#F26419] hover:bg-[#D94D0E] text-white text-[10px] font-bold uppercase">
                          👁 Voir
                        </button>
                        <button data-testid={`admin-doc-${d.k}-download`}
                          onClick={() => downloadPdf(`/restaurateur/admin/documents/${d.k}.pdf`, `nold-go-${d.k}.pdf`)}
                          className="flex-1 h-8 rounded-lg bg-white border border-[#E2DFD8] hover:border-[#1A1A1A] text-[10px] font-bold text-[#1A1A1A] uppercase">
                          ⬇ Télécharger
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Synchro démos — un seul clic pour peupler les restaurants démo avec photos */}
              <div className="bg-gradient-to-br from-[#7C3AED]/5 to-[#F26419]/5 rounded-2xl border-2 border-[#7C3AED]/40 p-5">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <h3 className="font-head font-bold text-lg text-[#1A1A1A]">🍽️ Synchroniser les démos</h3>
                    <p className="text-xs text-[#75807A]">Crée en un clic 6 restaurants de démonstration (Chez Léon, Baobab, Trattoria, Little Saigon, Burger BXL, Cave du Roy) avec ~47 plats et photos réelles Unsplash. Idempotent (relançable sans doublon).</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button data-testid="btn-ensure-demo-resto" onClick={async () => {
                    const toastId = toast.loading("Création du compte test…");
                    try {
                      const r = await api.post("/admin/ensure-demo-resto");
                      toast.success(`✅ Compte test ${r.data.action === "created" ? "créé" : "réparé"} — connecte-toi avec demo-resto@noldgo.com / PIN 1234`, { id: toastId, duration: 10000 });
                    } catch (e) {
                      toast.error(`❌ ${apiError(e)}`, { id: toastId, duration: 8000 });
                    }
                  }} className="flex-1 h-12 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold text-base">
                    🔑 Créer le compte test resto (demo-resto / 1234)
                  </Button>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button data-testid="btn-sync-demos" onClick={async () => {
                    if (!window.confirm("Créer les 6 restaurants de démonstration + plats + photos ? (peut prendre 60-90 secondes)")) return;
                    const toastId = toast.loading("Synchronisation en cours… ~60s");
                    try {
                      const r = await api.post("/admin/demo/sync");
                      const platsCount = r?.data?.plats_demo ?? 0;
                      const restosCount = r?.data?.restaurants_demo ?? 0;
                      if (!platsCount) {
                        toast.error(`❌ Sync partielle : ${restosCount} restos mais 0 plat créé. Vérifie l'Object Storage et relance.`, { id: toastId, duration: 8000 });
                        return;
                      }
                      toast.success(`✅ ${restosCount} restos démo, ${platsCount} plats prêts`, { id: toastId });
                    } catch (e) {
                      toast.error(`❌ Échec de la synchronisation : ${apiError(e)}`, { id: toastId, duration: 8000 });
                    }
                  }} className="flex-1 h-11 rounded-xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold">
                    ✨ Synchroniser les démos
                  </Button>
                  <Button data-testid="btn-purge-demos" onClick={async () => {
                    if (!window.confirm("Supprimer TOUS les restaurants marqués 'Démo' (avec leurs plats et propriétaires) ?")) return;
                    try {
                      const r = await api.post("/admin/demo/purge");
                      toast.success(`🗑 ${r.data.restaurants} restos, ${r.data.menu_items} plats supprimés`);
                    } catch (e) { toast.error(apiError(e)); }
                  }} className="h-11 px-4 rounded-xl bg-white border-2 border-[#E2DFD8] hover:border-[#DC2626] hover:text-[#DC2626] text-[#75807A] font-bold">
                    🗑 Purger les démos
                  </Button>
                  <Button data-testid="btn-purge-demo-orders" onClick={async () => {
                    if (!window.confirm("Supprimer toutes les commandes/livraisons liées aux restaurants démo ? (Nettoie les stats)")) return;
                    try {
                      const r = await api.post("/admin/demo/purge-orders");
                      toast.success(`🗑 ${r.data.deleted} commandes démo supprimées`);
                    } catch (e) { toast.error(apiError(e)); }
                  }} className="h-11 px-4 rounded-xl bg-white border-2 border-[#E2DFD8] hover:border-[#F26419] hover:text-[#F26419] text-[#75807A] font-bold">
                    🧹 Effacer commandes démo
                  </Button>
                </div>
              </div>

              {/* Kit Marketing Nold Go — offres, guides & scripts téléphoniques */}
              <div className="bg-white rounded-2xl border-2 border-[#F26419]/40 p-5">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <h3 className="font-head font-bold text-lg text-[#1A1A1A]">🎯 Kit Marketing Prospection</h3>
                    <p className="text-xs text-[#75807A]">Offres commerciales, notices d'utilisation & scripts téléphoniques FR/NL</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {[
                    { k: "offer-restaurateur", label: "Offre Restaurateur", icon: "🍔", langs: ["fr","nl"] },
                    { k: "offer-driver",       label: "Offre Chauffeur",   icon: "🚴", langs: ["fr","nl"] },
                    { k: "guide-resto",        label: "Notice Restaurateur", icon: "📖", langs: ["fr","nl"] },
                    { k: "guide-driver",       label: "Notice Chauffeur",  icon: "📱", langs: ["fr","nl"] },
                    { k: "script-restaurateur",label: "Script tél. Resto", icon: "📞", langs: ["fr"] },
                    { k: "script-driver",      label: "Script tél. Chauffeur", icon: "📞", langs: ["fr"] },
                  ].map((d) => (
                    <div key={d.k} className="rounded-xl border border-[#E2DFD8] bg-white p-3 hover:border-[#F26419] transition-colors">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{d.icon}</span>
                        <span className="text-xs font-bold text-[#1A1A1A]">{d.label}</span>
                      </div>
                      {d.langs.map((lg) => (
                        <div key={lg} className="mt-2 flex items-center gap-1">
                          <span className="text-[10px] font-bold text-[#75807A] uppercase w-6">{lg}</span>
                          <button
                            onClick={() => showPdf(`${d.label} (${lg.toUpperCase()})`, `/admin/marketing/${d.k}.pdf?lang=${lg}`, `nold-go-${d.k}-${lg}.pdf`)}
                            data-testid={`admin-marketing-${d.k}-${lg}-view`}
                            className="flex-1 h-8 rounded-lg bg-[#F26419] hover:bg-[#D94D0E] text-white text-[10px] font-bold uppercase inline-flex items-center justify-center gap-1">
                            👁 Voir
                          </button>
                          <button
                            onClick={() => downloadPdf(`/admin/marketing/${d.k}.pdf?lang=${lg}`, `nold-go-${d.k}-${lg}.pdf`)}
                            data-testid={`admin-marketing-${d.k}-${lg}-download`}
                            className="flex-1 h-8 rounded-lg bg-white border border-[#E2DFD8] hover:border-[#1A1A1A] text-[10px] font-bold text-[#1A1A1A] uppercase inline-flex items-center justify-center gap-1">
                            ⬇
                          </button>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {restaurateurs.items.length === 0 ? (
                <div data-testid="rt-empty" className="bg-white rounded-2xl border border-[#E2DFD8] p-10 text-center text-sm text-[#75807A]">
                  Aucun dossier restaurateur en attente. 🎉
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {restaurateurs.items.map((rt) => {
                    const d = rt.restaurateur || {};
                    return (
                      <div key={rt.id} data-testid={`rt-card-${rt.id}`} className="bg-white rounded-2xl border border-[#E2DFD8] p-5 space-y-3">
                        <div className="flex justify-between items-start gap-3">
                          <div className="min-w-0">
                            <div className="font-head font-bold text-lg text-[#1A1A1A] truncate">🍔 {d.business_name || rt.name || "—"}</div>
                            <div className="text-xs text-[#75807A] mt-0.5">{rt.email}</div>
                            <div className="text-xs text-[#3B423E] mt-1">{d.address || "—"}{d.postal_code ? " · " + d.postal_code : ""}{d.city ? " · " + d.city : ""}</div>
                          </div>
                          <span className="shrink-0 rounded-full bg-[#F0EBE1] text-[#3B423E] text-[11px] font-semibold px-2.5 py-1">{rt.restaurateur_status}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div><span className="text-[#75807A]">TVA</span><div className="font-semibold text-[#1A1A1A]">{d.vat_number || "—"}</div></div>
                          <div><span className="text-[#75807A]">Téléphone</span><div className="font-semibold text-[#1A1A1A]">{d.phone || "—"}</div></div>
                          <div><span className="text-[#75807A]">Cuisine</span><div className="font-semibold text-[#1A1A1A]">{d.cuisine || "—"}</div></div>
                          <div><span className="text-[#75807A]">Reçu le</span><div className="font-semibold text-[#1A1A1A]">{d.submitted_at ? new Date(d.submitted_at).toLocaleDateString() : "—"}</div></div>
                        </div>
                        {d.afsca_doc && (
                          <a data-testid={`rt-afsca-${rt.id}`} href={d.afsca_doc} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-xs font-semibold text-[#F26419] hover:underline">
                            <FileText className="w-3.5 h-3.5" /> Voir l'attestation AFSCA
                          </a>
                        )}
                        <div className="flex flex-col gap-2 pt-2 border-t border-[#F0EBE1]">
                          <div className="flex gap-2">
                            <Button data-testid={`rt-approve-${rt.id}`} onClick={() => { setApproveId(rt.id); setApproveDays(0); }} className="flex-1 h-10 rounded-lg bg-[#16a34a] hover:bg-[#128a3e] text-white font-semibold text-sm">
                              <Check className="w-4 h-4 mr-1.5" /> Approuver
                            </Button>
                            <Button data-testid={`rt-reject-${rt.id}`} onClick={() => { setRtRejectId(rt.id); setRtRejectReason(""); }} className="flex-1 h-10 rounded-lg bg-white border-2 border-[#D32F2F] text-[#D32F2F] hover:bg-[#D32F2F]/5 font-semibold text-sm">
                              <X className="w-4 h-4 mr-1.5" /> Refuser
                            </Button>
                          </div>
                          <Button data-testid={`rt-free-${rt.id}`} onClick={() => { setRtFreeId(rt.id); setRtFreeDays(30); setRtFreeNote(""); }} className="h-10 rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#F26419] hover:opacity-90 text-white font-semibold text-sm">
                            🎁 Rendre gratuit / période d'essai
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {rtRejectId && (
                <div data-testid="rt-reject-modal" className="fixed inset-0 z-[3000] bg-black/50 flex items-center justify-center p-4" onClick={() => setRtRejectId(null)}>
                  <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()}>
                    <h3 className="font-head font-bold text-lg">Refuser le dossier</h3>
                    <textarea data-testid="rt-reject-reason" value={rtRejectReason} onChange={(e) => setRtRejectReason(e.target.value)} rows={4} placeholder="Motif du refus (envoyé au restaurateur)…" className="w-full rounded-xl border-2 border-[#E2DFD8] p-3 text-sm resize-none focus:outline-none focus:border-[#F26419]" />
                    <div className="flex gap-2">
                      <Button data-testid="rt-reject-cancel" onClick={() => setRtRejectId(null)} className="flex-1 h-11 rounded-lg bg-white border-2 border-[#E2DFD8] text-[#1A1A1A] text-sm font-semibold">Annuler</Button>
                      <Button data-testid="rt-reject-confirm" onClick={rejectRestaurateur} className="flex-1 h-11 rounded-lg bg-[#D32F2F] hover:bg-[#a32020] text-white text-sm font-semibold">Confirmer le refus</Button>
                    </div>
                  </div>
                </div>
              )}

              {rtFreeId && (
                <div data-testid="rt-free-modal" className="fixed inset-0 z-[3000] bg-black/50 flex items-center justify-center p-4" onClick={() => setRtFreeId(null)}>
                  <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()}>
                    <div>
                      <h3 className="font-head font-bold text-lg">🎁 Offrir un accès gratuit</h3>
                      <p className="text-xs text-[#75807A] mt-1">Approuve le dossier ET marque le compte comme <b>admin_free</b> — le restaurateur ne verra plus jamais de demande de paiement d'abonnement. Il continue à payer uniquement la commission de 25 % sur les livraisons.</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-[#1A1A1A]">Durée</label>
                      <div className="mt-2 grid grid-cols-4 gap-2">
                        {[
                          { d: 30, l: "30 j" },
                          { d: 60, l: "60 j" },
                          { d: 90, l: "90 j" },
                          { d: 0, l: "À vie" },
                        ].map((o) => (
                          <button key={o.d}
                            data-testid={`rt-free-preset-${o.d}`}
                            onClick={() => setRtFreeDays(o.d)}
                            className={`h-11 rounded-xl border-2 text-sm font-bold transition-colors ${Number(rtFreeDays) === o.d ? "border-[#7C3AED] bg-[#7C3AED]/10 text-[#7C3AED]" : "border-[#E2DFD8] text-[#1A1A1A] hover:border-[#7C3AED]"}`}>
                            {o.l}
                          </button>
                        ))}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs text-[#75807A]">ou personnalisé :</span>
                        <input data-testid="rt-free-days-input" type="number" min="0" value={rtFreeDays} onChange={(e) => setRtFreeDays(e.target.value)} className="w-20 h-9 rounded-lg border-2 border-[#E2DFD8] px-2 text-sm text-center" />
                        <span className="text-xs text-[#75807A]">jours (0 = à vie)</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-[#1A1A1A]">Note interne <span className="text-xs font-normal text-[#75807A]">(optionnelle)</span></label>
                      <input data-testid="rt-free-note" value={rtFreeNote} onChange={(e) => setRtFreeNote(e.target.value)} placeholder="Ex: partenaire de longue date, ambassadeur…" className="mt-1.5 w-full h-10 rounded-lg border-2 border-[#E2DFD8] px-3 text-sm" />
                    </div>
                    <div className="flex gap-2">
                      <Button data-testid="rt-free-cancel" onClick={() => setRtFreeId(null)} className="flex-1 h-11 rounded-lg bg-white border-2 border-[#E2DFD8] text-[#1A1A1A] text-sm font-semibold">Annuler</Button>
                      <Button data-testid="rt-free-confirm" onClick={grantFreeRestaurateur} className="flex-1 h-11 rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#F26419] hover:opacity-90 text-white text-sm font-bold">
                        Offrir l'accès
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {scrapeOpen && (
                <div data-testid="rt-scrape-modal" className="fixed inset-0 z-[3000] bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => { setScrapeOpen(false); setScrapePreview(null); }}>
                  <div className="bg-white rounded-3xl w-full max-w-3xl p-6 my-8" onClick={(e) => e.stopPropagation()}>
                    <h3 className="font-head font-black text-xl mb-1">🌐 Créer une vitrine depuis un site</h3>
                    <p className="text-sm text-[#75807A] mb-3">Collez l'URL du site du restaurant. On extraira automatiquement les infos et le menu. Les prix affichés incluent déjà la commission 25 % arrondie au centime supérieur.</p>
                    <div className="inline-flex rounded-xl bg-[#F0EBE1] p-1 mb-4">
                      <button data-testid="rt-scrape-mode-single" onClick={() => { setScrapeBatchMode(false); setScrapeMultiOpen(false); setScrapeBatchResults([]); setScrapeBatchProgress(null); setScrapeMultiStats(null); }} className={`px-3 h-8 rounded-lg text-xs font-bold transition-colors ${!scrapeBatchMode && !scrapeMultiOpen ? "bg-white text-[#059669] shadow-sm" : "text-[#75807A]"}`}>1 URL</button>
                      <button data-testid="rt-scrape-mode-multi" onClick={() => { setScrapeBatchMode(false); setScrapeMultiOpen(true); setScrapeBatchResults([]); setScrapeBatchProgress(null); }} className={`px-3 h-8 rounded-lg text-xs font-bold transition-colors ${scrapeMultiOpen ? "bg-white text-[#059669] shadow-sm" : "text-[#75807A]"}`}>Multi-pages (1 resto)</button>
                      <button data-testid="rt-scrape-mode-batch" onClick={() => { setScrapeBatchMode(true); setScrapeMultiOpen(false); setScrapePreview(null); setScrapeMultiStats(null); }} className={`px-3 h-8 rounded-lg text-xs font-bold transition-colors ${scrapeBatchMode ? "bg-white text-[#059669] shadow-sm" : "text-[#75807A]"}`}>Batch (N restos)</button>
                    </div>
                    {!scrapeBatchMode && !scrapeMultiOpen && (
                    <div className="flex gap-2">
                      <input value={scrapeUrl} onChange={(e) => setScrapeUrl(e.target.value)} placeholder="https://restaurant.com" data-testid="rt-scrape-url" className="flex-1 h-11 px-3 rounded-xl border-2 border-[#E2DFD8] focus:border-[#059669] outline-none text-sm" />
                      <Button data-testid="rt-scrape-fetch" disabled={scrapeBusy || !scrapeUrl.trim()} onClick={async () => {
                        setScrapeBusy(true); setScrapePreview(null); setScrapeBlockedReason(null);
                        try {
                          const { data } = await api.post("/restaurateur/admin/scrape-storefront", { url: scrapeUrl.trim() });
                          setScrapePreview(data.preview);
                          setScrapeDetectedPlatforms(data.detected_platforms || []);
                          setScrapeBlockedReason(data.blocked_reason || null);
                          if (data.blocked_reason) {
                            toast.error(`⛔ ${data.blocked_reason.site || "Site bloqué"} — voir suggestions ci-dessous`);
                          } else {
                            toast.success(`${data.preview?.menu_count || 0} plats détectés`);
                          }
                        }
                        catch (e) { toast.error(e?.response?.data?.detail || "Scraping échoué."); }
                        finally { setScrapeBusy(false); }
                      }} className="h-11 px-5 rounded-xl bg-[#059669] hover:bg-[#047857] text-white font-bold text-sm whitespace-nowrap">
                        {scrapeBusy ? "Analyse…" : "Analyser"}
                      </Button>
                    </div>
                    )}
                    {scrapeMultiOpen && (
                      <div data-testid="rt-scrape-multi" className="space-y-3">
                        <div className="text-xs text-[#3B423E] bg-[#EEF6EE] border border-[#059669]/25 rounded-lg p-2.5">
                          💡 Collez les URLs des <b>pages menu / catégories</b> d'UN SEUL restaurant (ex : Entrées, Plats, Desserts, Boissons). Les items seront fusionnés en un seul menu.
                        </div>
                        <textarea value={scrapeMultiUrls} onChange={(e) => setScrapeMultiUrls(e.target.value)} data-testid="rt-scrape-multi-urls"
                          placeholder={"https://resto.com/entrees\nhttps://resto.com/plats\nhttps://resto.com/desserts\n…"} rows={5}
                          className="w-full rounded-xl border-2 border-[#E2DFD8] focus:border-[#059669] outline-none text-sm p-3 font-mono resize-none" />
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-[#75807A]">{scrapeMultiUrls.split("\n").map(s => s.trim()).filter(s => s.startsWith("http")).length} URLs valides (max 10)</span>
                          <Button data-testid="rt-scrape-multi-fetch" disabled={scrapeBusy || scrapeMultiUrls.split("\n").filter(s => s.trim().startsWith("http")).length === 0} onClick={async () => {
                            const urls = scrapeMultiUrls.split("\n").map(s => s.trim()).filter(s => s.startsWith("http")).slice(0, 10);
                            if (urls.length === 0) return;
                            setScrapeBusy(true); setScrapePreview(null); setScrapeMultiStats(null);
                            try {
                              const { data } = await api.post("/restaurateur/admin/scrape-storefront-multi", { urls });
                              setScrapePreview(data.preview);
                              setScrapeMultiStats({ per_url: data.per_url || [] });
                              toast.success(`${data.preview?.menu_count || 0} plats fusionnés depuis ${urls.length} pages`);
                            } catch (e) { toast.error(e?.response?.data?.detail || "Scraping multi-pages échoué."); }
                            finally { setScrapeBusy(false); }
                          }} className="h-10 px-4 rounded-xl bg-[#059669] hover:bg-[#047857] text-white font-bold text-sm whitespace-nowrap">
                            {scrapeBusy ? "Analyse…" : "Analyser & fusionner"}
                          </Button>
                        </div>
                        {scrapeMultiStats && (
                          <div className="text-[11px] bg-[#FAF5EC] border border-[#E2DFD8] rounded-lg p-2 space-y-0.5 max-h-32 overflow-y-auto">
                            {scrapeMultiStats.per_url.map((s, i) => (
                              <div key={i} className={`flex items-center gap-2 ${s.ok ? "text-[#059669]" : "text-[#D32F2F]"}`}>
                                {s.ok ? "✓" : "✗"} <span className="truncate flex-1">{s.url}</span>
                                <span className="shrink-0 font-bold">{s.ok ? `${s.items} plats` : s.error}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {scrapeBatchMode && (
                      <div data-testid="rt-scrape-batch" className="space-y-3">
                        <textarea value={scrapeBatchUrls} onChange={(e) => setScrapeBatchUrls(e.target.value)} data-testid="rt-scrape-batch-urls"
                          placeholder={"https://resto-1.be\nhttps://resto-2.com\nhttps://resto-3.fr\n…"} rows={5}
                          className="w-full rounded-xl border-2 border-[#E2DFD8] focus:border-[#059669] outline-none text-sm p-3 font-mono resize-none" />
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-[#75807A]">{scrapeBatchUrls.split("\n").map(s => s.trim()).filter(s => s.startsWith("http")).length} URLs valides détectées (max 10)</span>
                          <Button data-testid="rt-scrape-batch-analyze" disabled={scrapeBusy || scrapeBatchUrls.split("\n").filter(s => s.trim().startsWith("http")).length === 0} onClick={async () => {
                            const urls = scrapeBatchUrls.split("\n").map(s => s.trim()).filter(s => s.startsWith("http")).slice(0, 10);
                            if (urls.length === 0) return;
                            setScrapeBusy(true); setScrapeBatchResults([]); setScrapeBatchProgress({ current: 0, total: urls.length, phase: "analyse" });
                            const results = [];
                            for (let i = 0; i < urls.length; i++) {
                              setScrapeBatchProgress({ current: i + 1, total: urls.length, phase: "analyse" });
                              try {
                                const { data } = await api.post("/restaurateur/admin/scrape-storefront", { url: urls[i] });
                                results.push({ url: urls[i], ok: true, preview: data.preview, selected: (data.preview?.menu_count || 0) > 0 });
                              } catch (e) {
                                results.push({ url: urls[i], ok: false, error: e?.response?.data?.detail || "Erreur", selected: false });
                              }
                              setScrapeBatchResults([...results]);
                            }
                            setScrapeBatchProgress(null); setScrapeBusy(false);
                            const okCount = results.filter(r => r.ok).length;
                            toast.success(`${okCount}/${urls.length} sites analysés`);
                          }} className="h-10 px-4 rounded-xl bg-[#059669] hover:bg-[#047857] text-white font-bold text-sm whitespace-nowrap">
                            {scrapeBusy && scrapeBatchProgress?.phase === "analyse" ? `Analyse… ${scrapeBatchProgress.current}/${scrapeBatchProgress.total}` : "Analyser tout"}
                          </Button>
                        </div>
                        {scrapeBatchResults.length > 0 && (
                          <div data-testid="rt-scrape-batch-results" className="space-y-2 max-h-80 overflow-y-auto pr-1 border-t border-[#E2DFD8] pt-3">
                            {scrapeBatchResults.map((r, i) => (
                              <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${r.ok ? (r.created ? "border-[#059669] bg-[#F0FDF4]" : "border-[#E2DFD8] bg-white") : "border-[#FEE2E2] bg-[#FEF2F2]"}`}>
                                {r.ok && !r.created && (
                                  <input type="checkbox" checked={!!r.selected} onChange={(e) => setScrapeBatchResults(prev => prev.map((x, j) => j === i ? { ...x, selected: e.target.checked } : x))} className="h-4 w-4 accent-[#059669]" />
                                )}
                                {r.created && <span className="text-[#059669] text-lg">✅</span>}
                                {!r.ok && <span className="text-[#D32F2F] text-lg">❌</span>}
                                <div className="min-w-0 flex-1">
                                  {r.ok ? (
                                    <>
                                      <div className="text-sm font-bold truncate">{r.preview?.name || "—"}</div>
                                      <div className="text-[11px] text-[#75807A] truncate">{r.preview?.menu_count || 0} plats · {r.preview?.cuisine || "—"} · <a href={r.url} target="_blank" rel="noopener" className="text-[#059669] hover:underline">source</a></div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="text-sm font-bold truncate text-[#D32F2F]">Échec</div>
                                      <div className="text-[11px] text-[#75807A] truncate">{r.url} · {r.error}</div>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {scrapeBatchResults.filter(r => r.ok && r.selected && !r.created).length > 0 && (
                          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E2DFD8]">
                            <span className="text-xs text-[#75807A]">{scrapeBatchResults.filter(r => r.ok && r.selected && !r.created).length} vitrine(s) à créer</span>
                            <Button data-testid="rt-scrape-batch-create" disabled={scrapeBusy} onClick={async () => {
                              const toCreate = scrapeBatchResults.map((r, i) => ({ ...r, _idx: i })).filter(r => r.ok && r.selected && !r.created);
                              if (toCreate.length === 0) return;
                              setScrapeBusy(true); setScrapeBatchProgress({ current: 0, total: toCreate.length, phase: "create" });
                              let done = 0, failed = 0, totalItems = 0;
                              for (let k = 0; k < toCreate.length; k++) {
                                setScrapeBatchProgress({ current: k + 1, total: toCreate.length, phase: "create" });
                                try {
                                  const { data } = await api.post("/restaurateur/admin/create-from-scrape", { preview: toCreate[k].preview, source_url: toCreate[k].url });
                                  totalItems += data.menu_items_created || 0;
                                  setScrapeBatchResults(prev => prev.map((x, j) => j === toCreate[k]._idx ? { ...x, created: true } : x));
                                  done++;
                                } catch { failed++; }
                              }
                              setScrapeBatchProgress(null); setScrapeBusy(false);
                              if (section === "restos-manage") loadRestos(restosTab, restosQuery);
                              toast.success(`✅ ${done} vitrine(s) créée(s) · ${totalItems} plats importés${failed ? ` · ${failed} échec(s)` : ""}`);
                            }} className="h-10 px-4 rounded-xl bg-[#059669] hover:bg-[#047857] text-white font-bold text-sm">
                              {scrapeBusy && scrapeBatchProgress?.phase === "create" ? `Création… ${scrapeBatchProgress.current}/${scrapeBatchProgress.total}` : `✅ Créer ${scrapeBatchResults.filter(r => r.ok && r.selected && !r.created).length} vitrine(s)`}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                    {scrapeBlockedReason && (
                      <div data-testid="rt-scrape-blocked-reason" className="mt-4 rounded-xl border-2 border-[#F59E0B] bg-[#FEF3C7] p-4">
                        <div className="flex items-start gap-3">
                          <div className="shrink-0 w-10 h-10 rounded-full bg-[#F59E0B] text-white flex items-center justify-center text-xl font-black">⛔</div>
                          <div className="flex-1 min-w-0">
                            <div className="font-head font-black text-[#7C2D12] text-base">Site bloqué par un anti-bot</div>
                            <div className="text-xs uppercase tracking-widest text-[#B45309] font-bold mt-0.5">{scrapeBlockedReason.site}</div>
                            <p className="mt-2 text-sm text-[#7C2D12]">{scrapeBlockedReason.message}</p>
                            {scrapeBlockedReason.suggestions?.length > 0 && (
                              <div className="mt-3">
                                <div className="text-xs font-bold uppercase tracking-widest text-[#7C2D12] mb-1.5">💡 Solutions</div>
                                <ul className="space-y-1.5 text-sm text-[#7C2D12]">
                                  {scrapeBlockedReason.suggestions.map((s, i) => (
                                    <li key={i} className="flex gap-2"><span className="text-[#F59E0B] font-bold">→</span><span>{s}</span></li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {scrapePreview && !scrapeBlockedReason && (
                      <div data-testid="rt-scrape-preview" className="mt-5 space-y-4">
                        {scrapeDetectedPlatforms && scrapeDetectedPlatforms.length > 0 && (
                          <div data-testid="rt-scrape-platforms" className="rounded-xl bg-[#FFF3E6] border border-[#F26419]/25 p-3">
                            <div className="text-[10px] uppercase tracking-widest font-black text-[#F26419] mb-1">🔗 Plateformes détectées</div>
                            <ul className="text-xs text-[#3B423E] space-y-1">
                              {scrapeDetectedPlatforms.map((p, i) => (
                                <li key={i} className="flex items-start justify-between gap-2">
                                  <span className="min-w-0 flex-1">
                                    <b>{p.label}</b> — <a href={p.url} target="_blank" rel="noopener" className="text-[#F26419] hover:underline">{p.url.slice(0, 60)}…</a>
                                  </span>
                                  <span className="shrink-0 text-[10px] text-[#75807A]">{p.status || "détecté"}</span>
                                </li>
                              ))}
                            </ul>
                            <p className="mt-1.5 text-[10px] text-[#75807A]">💡 Si les plats de la plateforme sont bloqués, ajoutez son URL en mode "Multi-pages" pour la scraper explicitement.</p>
                          </div>
                        )}                        <div className="rounded-2xl border-2 border-[#059669]/30 bg-[#F0FDF4] p-4">
                          <div className="text-[10px] uppercase tracking-widest font-black text-[#059669]">Aperçu</div>
                          <div className="mt-1 font-head font-black text-lg">{scrapePreview.name}</div>
                          {scrapePreview.description && <p className="text-sm text-[#3B423E] mt-1">{scrapePreview.description}</p>}
                          <div className="mt-2 flex flex-wrap gap-2 text-xs text-[#3B423E]">
                            {scrapePreview.cuisine && <span>🍽️ {scrapePreview.cuisine}</span>}
                            {scrapePreview.address && <span>📍 {scrapePreview.address}</span>}
                            {scrapePreview.phone && <span>📞 {scrapePreview.phone}</span>}
                          </div>
                          {scrapePreview.photos?.length > 0 && (
                            <div className="mt-3 flex gap-2 overflow-x-auto">
                              {scrapePreview.photos.slice(0, 6).map((p, i) => (<img key={i} src={p} alt="" className="h-20 w-28 object-cover rounded-lg" />))}
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="text-xs font-bold uppercase tracking-widest text-[#75807A] mb-2">🍕 Menu détecté ({scrapePreview.menu_items?.length || 0} plats · prix commission 25 % incluse · groupés par catégorie)</div>
                          {scrapePreview.menu_items?.length === 0 && <div className="text-sm text-[#75807A] italic">Aucun plat détecté — l'admin devra les ajouter manuellement après création.</div>}
                          {scrapePreview.menu_items?.length > 0 && (() => {
                            const items = scrapePreview.menu_items || [];
                            // Ordre catégories : celui du backend, sinon 1ʳᵉ apparition + ajout des cats introduites via édition
                            const seenCats = [];
                            items.forEach(it => { const c = it.category || "Menu"; if (!seenCats.includes(c)) seenCats.push(c); });
                            const backendOrder = (scrapePreview.categories && scrapePreview.categories.length > 0) ? scrapePreview.categories : [];
                            const order = [...backendOrder.filter(c => seenCats.includes(c)), ...seenCats.filter(c => !backendOrder.includes(c))];
                            const byCat = order.map(cat => ({ cat, items: items.map((it, idx) => ({ it, idx })).filter(x => (x.it.category || "Menu") === cat) })).filter(g => g.items.length > 0);
                            const patch = (idx, k, v) => setScrapePreview(prev => ({ ...prev, menu_items: prev.menu_items.map((it, i) => i === idx ? { ...it, [k]: v } : it) }));
                            const remove = (idx) => setScrapePreview(prev => ({ ...prev, menu_items: prev.menu_items.filter((_, i) => i !== idx) }));
                            const catDatalistId = "rt-scrape-cats-datalist";
                            return (
                              <>
                                <datalist id={catDatalistId}>
                                  {seenCats.map((c, i) => <option key={i} value={c} />)}
                                </datalist>
                                <div className="text-[10px] text-[#75807A] italic mb-2">✏️ Modifiez nom, prix (commission incluse) ou catégorie. <b>Glissez ⋮⋮ pour réordonner</b> au sein d'une même catégorie. Cliquez ❌ pour retirer un plat.</div>
                                <div className="max-h-96 overflow-y-auto pr-1 space-y-3">
                                  {byCat.map((g, gi) => (
                                    <div key={g.cat + "_" + gi} data-testid={`rt-scrape-cat-${gi}`}>
                                      <div className="text-[11px] font-black uppercase tracking-widest text-[#059669] mb-1.5 flex items-center gap-2">
                                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#059669]"></span>
                                        {g.cat}
                                        <span className="text-[10px] font-semibold text-[#75807A]">· {g.items.length}</span>
                                      </div>
                                      <ul className="space-y-1.5">
                                        {g.items.map(({ it, idx }) => (
                                          <li key={idx}
                                              draggable
                                              onDragStart={(e) => { setScrapeDragIdx(idx); try { e.dataTransfer.effectAllowed = "move"; } catch {} }}
                                              onDragOver={(e) => {
                                                if (scrapeDragIdx === null || scrapeDragIdx === idx) return;
                                                const src = scrapePreview.menu_items[scrapeDragIdx];
                                                if (!src || (src.category || "Menu") !== g.cat) return; // seulement même catégorie
                                                e.preventDefault();
                                                try { e.dataTransfer.dropEffect = "move"; } catch {}
                                              }}
                                              onDrop={(e) => {
                                                e.preventDefault();
                                                if (scrapeDragIdx === null || scrapeDragIdx === idx) { setScrapeDragIdx(null); return; }
                                                setScrapePreview(prev => {
                                                  const arr = [...prev.menu_items];
                                                  const src = arr[scrapeDragIdx];
                                                  const tgt = arr[idx];
                                                  if (!src || !tgt || (src.category || "Menu") !== (tgt.category || "Menu")) return prev;
                                                  const [moved] = arr.splice(scrapeDragIdx, 1);
                                                  const insertAt = scrapeDragIdx < idx ? idx - 1 : idx;
                                                  arr.splice(insertAt, 0, moved);
                                                  return { ...prev, menu_items: arr.map((it2, i) => ({ ...it2, position: i })) };
                                                });
                                                setScrapeDragIdx(null);
                                              }}
                                              onDragEnd={() => setScrapeDragIdx(null)}
                                              className={`p-2 rounded-lg bg-[#FDF8F0] border ${scrapeDragIdx === idx ? "border-[#F26419] opacity-60" : "border-[#E2DFD8]"} cursor-move`}
                                              data-testid={`rt-scrape-item-${idx}`}>
                                            <div className="flex items-start gap-2">
                                              <span className="text-[#75807A] text-lg leading-none pt-1 select-none" title="Glissez pour réordonner">⋮⋮</span>
                                              <input value={it.name} onChange={(e) => patch(idx, "name", e.target.value.slice(0, 120))} data-testid={`rt-scrape-item-name-${idx}`}
                                                className="flex-1 min-w-0 h-9 px-2 rounded-lg border border-[#E2DFD8] focus:border-[#059669] outline-none text-sm font-bold bg-white" />
                                              <div className="flex items-center gap-1 shrink-0">
                                                <input type="number" step="0.01" min="0" value={Number(it.price_with_commission_eur || 0).toFixed(2)}
                                                  onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v) && v >= 0) patch(idx, "price_with_commission_eur", Math.round(v * 100) / 100); }}
                                                  data-testid={`rt-scrape-item-price-${idx}`}
                                                  className="w-20 h-9 px-1.5 rounded-lg border border-[#E2DFD8] focus:border-[#F26419] outline-none text-sm text-right font-black text-[#F26419] bg-white" />
                                                <span className="text-xs font-bold text-[#F26419]">€</span>
                                              </div>
                                              <button type="button" onClick={() => remove(idx)} data-testid={`rt-scrape-item-remove-${idx}`}
                                                className="h-9 w-9 shrink-0 rounded-lg bg-white border border-[#E2DFD8] hover:bg-[#FEE2E2] hover:border-[#D32F2F] text-[#75807A] hover:text-[#D32F2F] text-sm font-bold transition-colors">×</button>
                                            </div>
                                            <div className="mt-1.5 flex items-center gap-2">
                                              <input list={catDatalistId} value={it.category || "Menu"} onChange={(e) => patch(idx, "category", e.target.value.slice(0, 50) || "Menu")}
                                                data-testid={`rt-scrape-item-cat-${idx}`}
                                                className="h-8 px-2 rounded-lg border border-[#E2DFD8] focus:border-[#059669] outline-none text-xs bg-white min-w-0 flex-1" />
                                              {it.image_url ? (
                                                <img src={it.image_url} alt="" className="h-8 w-8 rounded object-cover border border-[#E2DFD8] shrink-0" />
                                              ) : (
                                                <button type="button" data-testid={`rt-scrape-item-genai-${idx}`}
                                                  disabled={scrapeAiIdx === idx}
                                                  onClick={async () => {
                                                    setScrapeAiIdx(idx);
                                                    try {
                                                      const { data } = await api.post("/restaurateur/admin/generate-item-photo", { name: it.name, description: it.description || "" });
                                                      if (data?.data_url) { patch(idx, "image_url", data.data_url); toast.success("Photo IA générée"); }
                                                    } catch (e) { toast.error(e?.response?.data?.detail || "Génération IA échouée."); }
                                                    finally { setScrapeAiIdx(null); }
                                                  }}
                                                  className="h-8 px-2 rounded-lg bg-white border border-[#7C3AED]/40 hover:bg-[#F5F3FF] text-[#7C3AED] text-[10px] font-black uppercase tracking-widest shrink-0 disabled:opacity-60 whitespace-nowrap">
                                                  {scrapeAiIdx === idx ? "…" : "🎨 IA"}
                                                </button>
                                              )}
                                              {it.price_original_eur > 0 && (<div className="text-[10px] text-[#75807A] shrink-0">source : <span className="line-through">{Number(it.price_original_eur).toFixed(2)} €</span></div>)}
                                            </div>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ))}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                        <div>
                          <label className="text-xs font-bold text-[#75807A]">Email du restaurateur (optionnel)</label>
                          <input value={scrapeOwnerEmail} onChange={(e) => setScrapeOwnerEmail(e.target.value)} placeholder="resto@example.com" data-testid="rt-scrape-owner-email" className="w-full h-11 px-3 rounded-xl border-2 border-[#E2DFD8] focus:border-[#059669] outline-none text-sm mt-1" />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button onClick={() => { setScrapeOpen(false); setScrapePreview(null); }} className="h-11 px-5 rounded-xl bg-[#F0EBE1] hover:bg-[#E2DFD8] text-[#1A1A1A] font-bold text-sm">Annuler</Button>
                          <Button data-testid="rt-scrape-confirm" disabled={scrapeBusy} onClick={async () => {
                            setScrapeBusy(true);
                            try {
                              const { data } = await api.post("/restaurateur/admin/create-from-scrape", {
                                preview: scrapePreview,
                                owner_email: scrapeOwnerEmail.trim() || null,
                                source_url: scrapeUrl.trim() || null,
                              });
                              setScrapeOpen(false); setScrapePreview(null); setScrapeUrl(""); setScrapeOwnerEmail("");
                              if (section === "restos-manage") loadRestos(restosTab, restosQuery);
                              toast.success(`✅ Vitrine créée · ${data.menu_items_created || 0} plats importés${data.menu_items_failed ? ` (${data.menu_items_failed} ignorés)` : ""}`);
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Création échouée.");
                            } finally { setScrapeBusy(false); }
                          }} className="h-11 px-5 rounded-xl bg-[#059669] hover:bg-[#047857] text-white font-bold text-sm">
                            {scrapeBusy ? "Création…" : "✅ Créer la vitrine"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {rtCreateOpen && (
                <div data-testid="rt-create-modal" className="fixed inset-0 z-[3000] bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setRtCreateOpen(false)}>
                  <div className="bg-white rounded-2xl p-6 max-w-lg w-full space-y-4 my-8" onClick={(e) => e.stopPropagation()}>
                    <div>
                      <h3 className="font-head font-black text-xl">+ Créer un restaurant</h3>
                      <p className="text-xs text-[#75807A] mt-1">Vitrine créée directement par l'admin, en mode gratuit. Tu pourras ajouter les plats juste après.</p>
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className="text-sm font-semibold text-[#1A1A1A]">Nom du restaurant *</label>
                        <input data-testid="rt-create-name" value={rtCreateForm.name} onChange={(e) => setRtCreateForm({ ...rtCreateForm, name: e.target.value })} placeholder="Ex: Chez Marco" className="mt-1.5 w-full h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-[#1A1A1A]">Adresse complète *</label>
                        <input data-testid="rt-create-address" value={rtCreateForm.address} onChange={(e) => setRtCreateForm({ ...rtCreateForm, address: e.target.value })} placeholder="Ex: Rue Neuve 10, 1000 Bruxelles" className="mt-1.5 w-full h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-[#1A1A1A]">Description</label>
                        <textarea data-testid="rt-create-description" value={rtCreateForm.description} onChange={(e) => setRtCreateForm({ ...rtCreateForm, description: e.target.value })} rows={3} placeholder="Ex: Trattoria familiale, pâtes fraîches et pizzas au feu de bois…" className="mt-1.5 w-full rounded-xl border-2 border-[#E2DFD8] px-3 py-2 text-sm resize-none" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm font-semibold text-[#1A1A1A]">Cuisine</label>
                          <input data-testid="rt-create-cuisine" value={rtCreateForm.cuisine} onChange={(e) => setRtCreateForm({ ...rtCreateForm, cuisine: e.target.value })} placeholder="Italien, Africain…" className="mt-1.5 w-full h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
                        </div>
                        <div>
                          <label className="text-sm font-semibold text-[#1A1A1A]">Téléphone</label>
                          <input data-testid="rt-create-phone" value={rtCreateForm.phone} onChange={(e) => setRtCreateForm({ ...rtCreateForm, phone: e.target.value })} placeholder="+32 ..." className="mt-1.5 w-full h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-[#1A1A1A]">Photo / logo du restaurant</label>
                        <input data-testid="rt-create-logo-file" type="file" accept="image/*" onChange={(e) => uploadRtCreateLogo(e.target.files?.[0])} className="mt-1.5 w-full text-sm" />
                        {rtCreateForm.logo_url === "loading" && <div className="mt-2 text-xs text-[#75807A] italic">📷 Lecture de la photo…</div>}
                        {rtCreateForm.logo_url && rtCreateForm.logo_url !== "loading" && <img src={rtCreateForm.logo_url} alt="logo" className="mt-2 h-20 w-20 object-cover rounded-xl border border-[#E2DFD8]" />}
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-[#1A1A1A]">Email propriétaire <span className="text-xs font-normal text-[#75807A]">(optionnel — permet au resto de se connecter plus tard)</span></label>
                        <input data-testid="rt-create-email" type="email" value={rtCreateForm.owner_email} onChange={(e) => setRtCreateForm({ ...rtCreateForm, owner_email: e.target.value })} placeholder="proprio@example.com" className="mt-1.5 w-full h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-[#1A1A1A]">Durée de la gratuité</label>
                        <div className="mt-2 grid grid-cols-4 gap-2">
                          {[{d:0,l:"À vie"},{d:30,l:"30 j"},{d:60,l:"60 j"},{d:90,l:"90 j"}].map((o) => (
                            <button key={o.d} data-testid={`rt-create-free-${o.d}`}
                              onClick={() => setRtCreateForm({ ...rtCreateForm, free_days: o.d })}
                              className={`h-11 rounded-xl border-2 text-sm font-bold transition-colors ${Number(rtCreateForm.free_days) === o.d ? "border-[#7C3AED] bg-[#7C3AED]/10 text-[#7C3AED]" : "border-[#E2DFD8] text-[#1A1A1A] hover:border-[#7C3AED]"}`}>{o.l}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button data-testid="rt-create-cancel" onClick={() => setRtCreateOpen(false)} className="flex-1 h-11 rounded-lg bg-white border-2 border-[#E2DFD8] text-[#1A1A1A] text-sm font-semibold">Annuler</Button>
                      <Button data-testid="rt-create-confirm" onClick={createRestaurantByAdmin} disabled={rtCreateLoading} className="flex-1 h-11 rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#F26419] hover:opacity-90 text-white text-sm font-bold disabled:opacity-60">
                        {rtCreateLoading ? "Création…" : "Créer le restaurant"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* ---------- Contrats signés (Restaurateurs + Livreurs) ---------- */}
              <div data-testid="admin-signed-contracts" className="bg-white rounded-2xl border border-[#E2DFD8] p-5 mt-6">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <h3 className="font-head font-bold text-lg text-[#1A1A1A]">✍️ Contrats signés (Nold Go)</h3>
                    <p className="text-xs text-[#75807A]">
                      {noldContracts.restaurateur_count || 0} restaurateur(s) · {noldContracts.driver_count || 0} livreur(s) · vérification QR publique
                    </p>
                  </div>
                  <div className="inline-flex items-center gap-1.5 rounded-full bg-[#16a34a]/10 border border-[#16a34a]/30 text-[#16a34a] text-xs font-bold px-3 py-1.5">
                    {noldContracts.count} signé(s)
                  </div>
                </div>
                {noldContracts.count === 0 ? (
                  <div data-testid="admin-signed-empty" className="py-8 text-center text-sm text-[#75807A]">
                    Aucun contrat signé pour le moment.
                  </div>
                ) : (
                  <ul className="divide-y divide-[#F0EBE1]">
                    {noldContracts.items.map((c) => {
                      const name = c.party_type === "driver" ? c.driver_name : c.business_name;
                      const isDriver = c.party_type === "driver";
                      const verifyPath = `/verify/contract/${c.id}`;
                      const pdfUrl = `${API}/verify/contract/${c.id}.pdf`;
                      return (
                        <li key={c.id} data-testid={`admin-contract-${c.id}`} className="py-3 flex flex-wrap items-center gap-3 justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`shrink-0 rounded-full text-[10px] font-bold px-2 py-0.5 ${isDriver ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FFF3E6] text-[#7A3A00]"}`}>
                                {isDriver ? "🚴 LIVREUR" : "🍔 RESTAURATEUR"}
                              </span>
                              <span className="font-semibold text-[#1A1A1A] truncate">{name}</span>
                              <span className="font-mono text-[10px] text-[#F26419]">{c.hash_short}</span>
                            </div>
                            <div className="text-xs text-[#75807A] mt-0.5 truncate">
                              {new Date(c.signed_at).toLocaleString("fr-BE")}
                              {c.vat_number ? ` · TVA ${c.vat_number}` : ""}
                              {c.email ? ` · ${c.email}` : ""}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <a
                              data-testid={`admin-contract-open-${c.id}`}
                              href={verifyPath} target="_blank" rel="noreferrer"
                              className="inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-white border border-[#E2DFD8] hover:border-[#F26419] text-xs font-semibold text-[#1A1A1A]">
                              <ExternalLink className="w-3.5 h-3.5" /> QR
                            </a>
                            <a
                              data-testid={`admin-contract-pdf-${c.id}`}
                              href={pdfUrl} target="_blank" rel="noreferrer"
                              className="inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-[#1A1A1A] hover:bg-black text-white text-xs font-semibold">
                              <Download className="w-3.5 h-3.5" /> PDF
                            </a>
                            <button
                              data-testid={`admin-contract-cancel-${c.id}`}
                              onClick={() => cancelNoldContract(c.id, name)}
                              className="inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-white border-2 border-[#D32F2F] text-[#D32F2F] hover:bg-[#FEE2E2] text-xs font-semibold">
                              <X className="w-3.5 h-3.5" /> Annuler
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}

          {section === "livemap" && (() => {
            const driversPts = livePoints.filter((p) => p.role === "driver");
            const agentsPts = livePoints.filter((p) => p.role === "agent");
            const passengersPts = livePoints.filter((p) => p.role === "passenger");
            const isEu = (p) => (p.zone || "africa") === "europe";
            const africa = driversPts.filter((p) => !isEu(p));
            const europe = driversPts.filter((p) => isEu(p));
            const africaPax = passengersPts.filter((p) => !isEu(p));
            const europePax = passengersPts.filter((p) => isEu(p));
            const africaMap = [...africa, ...agentsPts, ...africaPax];
            const europeMap = [...europe, ...europePax];
            const vc = (list, vt) => list.filter((p) => (p.vehicle_type || "moto") === vt).length;
            const counter = (testid, emoji, label, n, color) => (
              <div key={testid} data-testid={testid} className="rounded-xl border border-[#E2DFD8] p-3 text-center">
                <div className="font-head font-black text-2xl" style={{ color }}>{n}</div>
                <div className="text-[11px] font-semibold text-[#75807A]">{emoji} {label}</div>
              </div>
            );
            const zoneCard = (testid, flag, title, driverPts, mapPts, center, counters, accent) => (
              <div data-testid={testid} className="bg-white rounded-2xl border border-[#E2DFD8] p-5 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-head font-black text-lg flex items-center gap-2">{flag} {title}</h3>
                  <span className="inline-flex items-center gap-1.5 text-xs text-[#75807A]"><span className="w-2 h-2 rounded-full bg-[#16a34a] animate-pulse" /> Temps réel · 5s</span>
                </div>
                <div data-testid={`${testid}-online`} className="rounded-xl p-3 text-white flex items-center justify-between" style={{ background: accent }}>
                  <span className="text-sm font-semibold opacity-90">Chauffeurs en ligne</span>
                  <span className="font-head font-black text-3xl">{driverPts.length}</span>
                </div>
                <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(counters.length, 4)}, minmax(0,1fr))` }}>
                  {counters}
                </div>
                <LiveMap points={mapPts} center={center} height="72vh" />
                {mapPts.length === 0 && <p className="text-xs text-[#75807A]">{t("admin.liveEmpty")}</p>}
              </div>
            );
            return (
            <div className="space-y-5" data-testid="livemap-section">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="font-head font-black text-xl tracking-tight">{t("admin.livemap")}</h2>
                <span className="inline-flex items-center gap-1.5 text-xs text-[#75807A]"><span className="w-2 h-2 rounded-full bg-[#16a34a] animate-pulse" /> {t("admin.liveRefresh")}</span>
              </div>
              <div className="grid grid-cols-1 gap-5">
                {viewZone !== "europe" && zoneCard("live-card-cameroun", "🇨🇲", "Carte Live Cameroun", africa, africaMap, { lat: 3.848, lng: 11.502 }, [
                  counter("cm-count-moto", "🏍️", "Motos", vc(africa, "moto"), "#F26419"),
                  counter("cm-count-car", "🚗", "Voitures", vc(africa, "car"), "#1976D2"),
                  counter("cm-count-colis", "🚲", "Colis/Vélo", vc(africa, "bike"), "#16a34a"),
                  counter("cm-count-agents", "🧑‍💼", "Agents", agentsPts.length, "#7C3AED"),
                  counter("cm-count-pax", "👤", "Passagers", africaPax.length, "#0EA5E9"),
                ], "#1A1A1A")}
                {viewZone !== "africa" && zoneCard("live-card-europe", "🇪🇺", "Carte Live Europe", europe, europeMap, { lat: 50.8503, lng: 4.3517 }, [
                  counter("eu-count-bike", "🚲", "Vélos", vc(europe, "bike"), "#16a34a"),
                  counter("eu-count-car", "🚗", "Voitures", vc(europe, "car"), "#1976D2"),
                  counter("eu-count-pax-vtc", "👤", "Passagers VTC", europePax.filter((p) => p.kind !== "food").length, "#0EA5E9"),
                  counter("eu-count-pax-food", "🍽", "Clients Food", europePax.filter((p) => p.kind === "food").length, "#059669"),
                ], "#2563EB")}
              </div>
              <div data-testid="live-history-card" className="bg-white rounded-2xl border border-[#E2DFD8] p-5 space-y-3">
                <h3 className="font-head font-black text-lg flex items-center gap-2">📈 Chauffeurs en ligne · dernières 24 h</h3>
                {liveHistory.length === 0 ? (
                  <p data-testid="live-history-empty" className="text-sm text-[#75807A]">Collecte des données en cours…</p>
                ) : (
                  <div style={{ width: "100%", height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={liveHistory} margin={{ top: 10, right: 16, left: -12, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEE8DE" />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#75807A" }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#75807A" }} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="africa" name="🇨🇲 Cameroun" stroke="#F26419" strokeWidth={2.5} dot={false} />
                        <Line type="monotone" dataKey="europe" name="🇪🇺 Europe" stroke="#2563EB" strokeWidth={2.5} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
            );
          })()}

          {section === "collab" && (() => {
            const items = collabData.items || [];
            const cities = [...new Set(items.map((c) => c.city).filter(Boolean))].sort();
            const types = [...new Set(items.map((c) => c.type).filter(Boolean))].sort();
            const q = collabSearch.trim().toLowerCase();
            const filtered = items.filter((c) =>
              (!collabFType || c.type === collabFType) &&
              (!collabFStatus || (c.status || "approved") === collabFStatus) &&
              (!collabFCity || c.city === collabFCity) &&
              (!q || [c.code, c.name, c.phone, c.city, c.type].some((v) => (v || "").toLowerCase().includes(q))));
            const fmtPhone = (p) => { const d = (p || "").replace(/\D/g, ""); if (!d) return "—"; if ((p || "").startsWith("+")) return p; if (d.length === 9) return "+237 " + d; return p; };
            const statusLabel = (s) => ((s || "approved") === "approved" ? "Approuvé" : "En attente");
            const setEdit = (code, field, val) => setCollabEdits((s) => ({ ...s, [code]: { ...s[code], [field]: val } }));
            const gv = (c, f) => collabEdits[c.code]?.[f] ?? c[f] ?? "";
            const exportCsv = () => {
              const rows = [["Nom", "Type", "Telephone", "Ville", "Statut", "Code Collab", "Filleuls", "Gains FCFA", "Du FCFA"]];
              filtered.forEach((c) => rows.push([c.name || "", c.type || "", fmtPhone(c.phone), c.city || "", statusLabel(c.status), c.code, c.uses || 0, c.earnings || 0, c.due || 0]));
              const csv = rows.map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n");
              const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
              const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "collaborateurs.csv";
              document.body.appendChild(a); a.click(); a.remove();
              setTimeout(() => URL.revokeObjectURL(url), 60000);
            };
            return (
            <div data-testid="collab-section">
              <h2 className="font-head font-black text-2xl text-[#1A1A1A]">Codes Collaborateurs</h2>
              <p className="mt-1 text-sm text-[#75807A]">13 codes d'activation gratuite (utilisations illimitées). Chaque chauffeur activé via un code rapporte {collabData.reward || 100} FCFA au collaborateur.</p>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white rounded-2xl border border-[#E2DFD8] p-4"><div className="text-xs text-[#75807A]">Total activations</div><div className="font-head font-black text-2xl text-[#1A1A1A]" data-testid="collab-total-activations">{collabData.total_activations || 0}</div></div>
                <div className="bg-white rounded-2xl border border-[#E2DFD8] p-4"><div className="text-xs text-[#75807A]">Total gagné</div><div className="font-head font-black text-2xl text-[#1A1A1A]" data-testid="collab-total-payout">{(collabData.total_payout || 0).toLocaleString("fr-FR")} FCFA</div></div>
                <div className="bg-white rounded-2xl border border-[#E2DFD8] p-4"><div className="text-xs text-[#75807A]">Déjà payé</div><div className="font-head font-black text-2xl text-[#75807A]" data-testid="collab-total-paid">{(collabData.total_paid || 0).toLocaleString("fr-FR")} FCFA</div></div>
                <div className="bg-white rounded-2xl border-2 border-[#D94F2B]/40 p-4"><div className="text-xs text-[#75807A]">Reste à payer</div><div className="font-head font-black text-2xl text-[#D94F2B]" data-testid="collab-total-due">{(collabData.total_due || 0).toLocaleString("fr-FR")} FCFA</div></div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[220px]"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#75807A]" /><Input data-testid="collab-search" value={collabSearch} onChange={(e) => setCollabSearch(e.target.value)} placeholder="Rechercher (nom, code, téléphone, ville)" className="pl-9 h-11 rounded-xl bg-white border-[#E2DFD8]" /></div>
                <select data-testid="collab-filter-type" value={collabFType} onChange={(e) => setCollabFType(e.target.value)} className="h-11 rounded-xl border border-[#E2DFD8] bg-white px-3 text-sm"><option value="">Tous types</option>{types.map((t) => <option key={t} value={t}>{t}</option>)}</select>
                <select data-testid="collab-filter-status" value={collabFStatus} onChange={(e) => setCollabFStatus(e.target.value)} className="h-11 rounded-xl border border-[#E2DFD8] bg-white px-3 text-sm"><option value="">Tous statuts</option><option value="approved">Approuvé</option><option value="pending">En attente</option></select>
                <select data-testid="collab-filter-city" value={collabFCity} onChange={(e) => setCollabFCity(e.target.value)} className="h-11 rounded-xl border border-[#E2DFD8] bg-white px-3 text-sm"><option value="">Toutes villes</option>{cities.map((ct) => <option key={ct} value={ct}>{ct}</option>)}</select>
                <Button data-testid="collab-export-csv" onClick={exportCsv} className="h-11 px-4 rounded-xl bg-[#1A1A1A] hover:bg-black text-white text-sm"><Download className="w-4 h-4 mr-1" />Export CSV</Button>
              </div>
              <div className="mt-4 bg-white rounded-2xl border border-[#E2DFD8] overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F0EBE1] text-[#75807A] text-xs uppercase"><tr>
                    <th className="text-left p-3">Nom</th><th className="text-left p-3">Type</th><th className="text-left p-3">Téléphone +237</th><th className="text-left p-3">Ville</th><th className="text-left p-3">Statut</th><th className="text-left p-3">Code Collab</th><th className="text-left p-3">Filleuls</th><th className="text-left p-3">Gains 100FCFA</th><th className="text-left p-3">Dû</th><th className="text-right p-3">Actions</th>
                  </tr></thead>
                  <tbody>
                    {filtered.map((c) => (
                      <tr key={c.code} data-testid={`collab-row-${c.code}`} className={`border-t border-[#E2DFD8] ${(c.status || "approved") === "approved" ? "bg-[#16a34a]/5" : "bg-[#E0912F]/10"}`}>
                        <td className="p-3"><Input data-testid={`collab-name-${c.code}`} value={gv(c, "name")} onChange={(e) => setEdit(c.code, "name", e.target.value)} placeholder="Nom" className="h-9 rounded-lg border-[#E2DFD8] min-w-[120px] bg-white" /></td>
                        <td className="p-3"><Input data-testid={`collab-type-${c.code}`} value={gv(c, "type")} onChange={(e) => setEdit(c.code, "type", e.target.value)} placeholder="Type" className="h-9 rounded-lg border-[#E2DFD8] min-w-[100px] bg-white" /></td>
                        <td className="p-3"><Input data-testid={`collab-phone-${c.code}`} value={gv(c, "phone")} onChange={(e) => setEdit(c.code, "phone", e.target.value)} placeholder="+237 6XX XXX XXX" className="h-9 rounded-lg border-[#E2DFD8] min-w-[140px] bg-white" /></td>
                        <td className="p-3"><Input data-testid={`collab-city-${c.code}`} value={gv(c, "city")} onChange={(e) => setEdit(c.code, "city", e.target.value)} placeholder="Ville" className="h-9 rounded-lg border-[#E2DFD8] min-w-[100px] bg-white" /></td>
                        <td className="p-3"><select data-testid={`collab-status-${c.code}`} value={gv(c, "status") || "approved"} onChange={(e) => setEdit(c.code, "status", e.target.value)} className="h-9 rounded-lg border border-[#E2DFD8] bg-white px-2 text-sm"><option value="approved">Approuvé</option><option value="pending">En attente</option></select></td>
                        <td className="p-3 font-bold text-[#1A1A1A] tracking-wide whitespace-nowrap">{c.code}</td>
                        <td className="p-3 font-semibold" data-testid={`collab-uses-${c.code}`}>{c.uses || 0}</td>
                        <td className="p-3 whitespace-nowrap">{(c.earnings || 0).toLocaleString("fr-FR")} FCFA</td>
                        <td className="p-3 font-bold whitespace-nowrap" data-testid={`collab-due-${c.code}`}><span className={c.due > 0 ? "text-[#D94F2B]" : "text-[#75807A]"}>{(c.due || 0).toLocaleString("fr-FR")} FCFA</span></td>
                        <td className="p-3 text-right whitespace-nowrap">
                          <Button data-testid={`collab-save-${c.code}`} onClick={() => saveCollab(c.code)} className="h-9 px-3 rounded-lg bg-white border border-[#E2DFD8] text-[#1A1A1A] text-xs mr-2">Enregistrer</Button>
                          <Button data-testid={`collab-paid-${c.code}`} disabled={!(c.due > 0)} onClick={() => markCollabPaid(c.code)} className="h-9 px-3 rounded-lg bg-[#16a34a] hover:bg-[#0f8a3c] text-white text-xs disabled:opacity-40">Marquer payé</Button>
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && <tr><td colSpan="10" className="p-8 text-center text-[#75807A]">Aucun collaborateur ne correspond aux filtres.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
            );
          })()}

          {section === "activation" && (
            <div data-testid="activation-section">
              <h2 className="font-head font-black text-2xl text-[#1A1A1A]">Validations en attente</h2>
              <p className="mt-1 text-sm text-[#75807A]">Chauffeurs ayant déclaré un paiement d'activation ({activationPending.fee || 200} FCFA). Vérifiez la réception sur Orange Money / MTN, puis créditez leur portefeuille. Ils pourront ensuite activer leur compte (débit + code SMS).</p>
              {smsStats && (
                <div data-testid="sms-stats-card" className="mt-5 rounded-2xl border border-[#E2DFD8] bg-white p-4">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-1">
                    <div className="font-head font-bold text-[#1A1A1A]">📱 Suivi SMS Twilio</div>
                    <span className="text-xs text-[#75807A]">Activation = payé par l'utilisateur · OTP = à votre charge</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {[["Aujourd'hui", "today"], ["7 jours", "week"], ["Total", "total"]].map(([label, key]) => (
                      <div key={key} data-testid={`sms-stat-${key}`} className="rounded-xl bg-[#FDF8F0] border border-[#E2DFD8] p-3">
                        <div className="text-xs font-semibold text-[#75807A] uppercase tracking-wide">{label}</div>
                        <div className="mt-1 text-2xl font-black text-[#1A1A1A]">{smsStats[key]?.total ?? 0}</div>
                        <div className="mt-1 text-xs text-[#75807A]">Activation : <b className="text-[#16a34a]">{smsStats[key]?.activation ?? 0}</b> · OTP : <b className="text-[#F26419]">{smsStats[key]?.otp ?? 0}</b>{smsStats[key]?.other ? ` · Autre : ${smsStats[key].other}` : null}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-5 bg-white rounded-2xl border border-[#E2DFD8] overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F0EBE1] text-[#75807A] text-xs uppercase"><tr>
                    <th className="text-left p-4">Chauffeur</th><th className="text-left p-4">Numéro OM/MTN (payeur)</th><th className="text-left p-4">Demandé</th><th className="text-left p-4">Portefeuille</th><th className="text-right p-4">Action</th>
                  </tr></thead>
                  <tbody>
                    {activationPending.items.map((d) => (
                      <tr key={d.id} data-testid={`activation-row-${d.id}`} className="border-t border-[#E2DFD8]">
                        <td className="p-4"><div className="flex items-center gap-2"><span className="font-semibold text-[#1A1A1A]">{d.name || "—"}</span><span data-testid={`activation-role-${d.id}`} className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${d.role === "passenger" ? "bg-[#2563EB]/15 text-[#2563EB]" : "bg-[#16a34a]/15 text-[#16a34a]"}`}>{d.role === "passenger" ? "Passager" : "Chauffeur"}</span></div><div className="text-xs text-[#75807A] break-all">{d.phone} · {d.email}</div></td>
                        <td className="p-4 font-bold text-[#1A1A1A]" data-testid={`activation-paynum-${d.id}`}>{d.activation_payment_number || "—"}</td>
                        <td className="p-4 text-xs text-[#75807A]">{d.activation_requested_at ? new Date(d.activation_requested_at).toLocaleString("fr-FR") : "—"}</td>
                        <td className="p-4">{Math.round(d.wallet || 0)} FCFA</td>
                        <td className="p-4 text-right">
                          <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                            {d.activation_fee_credited ? (
                              <span data-testid={`activation-credited-${d.id}`} className="text-xs font-semibold px-2 py-1 rounded-full bg-[#16a34a]/15 text-[#16a34a]">Crédité ✓ · en attente d'activation</span>
                            ) : (
                              <Button data-testid={`credit-activation-${d.id}`} onClick={() => creditActivation(d.id)} className="h-9 px-3 rounded-lg bg-[#16a34a] hover:bg-[#0f8a3c] text-white text-xs font-bold">Créditer Wallet {activationPending.fee || 200} FCFA</Button>
                            )}
                            <Button data-testid={`force-activate-${d.id}`} onClick={() => forceActivate(d.id)} className="h-9 px-3 rounded-lg bg-[#F26419] hover:bg-[#d94f0f] text-white text-xs font-bold">Activer directement</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {activationPending.items.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-[#75807A]">Aucune validation en attente.</td></tr>}
                  </tbody>
                </table>
              </div>
              {(activationPending.code_pending || []).length > 0 && (
                <div data-testid="activation-code-pending" className="mt-6">
                  <h3 className="font-head font-black text-lg text-[#1A1A1A]">Codes SMS d'activation en attente</h3>
                  <p className="mt-1 text-sm text-[#75807A]">Si le SMS n'arrive pas au chauffeur, communiquez-lui le code ci-dessous (valable 5 min).</p>
                  <div className="mt-3 bg-white rounded-2xl border border-[#E2DFD8] overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F0EBE1] text-[#75807A] text-xs uppercase"><tr>
                        <th className="text-left p-4">Chauffeur</th><th className="text-left p-4">Code</th><th className="text-left p-4">Expire</th><th className="text-right p-4">Action</th>
                      </tr></thead>
                      <tbody>
                        {activationPending.code_pending.map((d) => {
                          const exp = d.activation_code_expires ? new Date(d.activation_code_expires) : null;
                          const expired = exp && exp < new Date();
                          return (
                            <tr key={d.id} data-testid={`code-pending-row-${d.id}`} className="border-t border-[#E2DFD8]">
                              <td className="p-4"><div className="flex items-center gap-2"><span className="font-semibold text-[#1A1A1A]">{d.name || "—"}</span><span data-testid={`activation-role-${d.id}`} className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${d.role === "passenger" ? "bg-[#2563EB]/15 text-[#2563EB]" : "bg-[#16a34a]/15 text-[#16a34a]"}`}>{d.role === "passenger" ? "Passager" : "Chauffeur"}</span></div><div className="text-xs text-[#75807A] break-all">{d.phone} · {d.email}</div></td>
                              <td className="p-4"><span data-testid={`activation-code-${d.id}`} className="font-mono font-black text-lg tracking-widest text-[#1A1A1A]">{d.activation_code || "—"}</span>{d.activation_code && <button data-testid={`activation-code-copy-${d.id}`} onClick={() => copyText(d.activation_code, "Code d'activation")} className="ml-2 align-middle text-[#75807A] hover:text-[#D94F2B]" title="Copier le code"><Copy className="w-4 h-4 inline" /></button>}</td>
                              <td className="p-4 text-xs">{expired ? <span className="text-[#D32F2F] font-semibold">Expiré</span> : <span className="text-[#16a34a]">{exp ? exp.toLocaleTimeString("fr-FR") : "—"}</span>}</td>
                              <td className="p-4 text-right"><Button data-testid={`code-pending-activate-${d.id}`} onClick={() => forceActivate(d.id)} className="h-8 px-3 rounded-lg bg-[#16a34a] hover:bg-[#0f8a3c] text-white text-xs font-bold">Activer le compte</Button></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div data-testid="activated-accounts" className="mt-6">
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <h3 className="font-head font-black text-lg text-[#1A1A1A]">Comptes activés (Afrique)</h3>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs text-[#75807A]">Total : <b>{activatedAccounts.count}</b> · Chauffeurs : <b className="text-[#16a34a]">{activatedAccounts.drivers}</b> · Passagers : <b className="text-[#2563EB]">{activatedAccounts.passengers}</b></span>
                    <Button data-testid="export-activated-csv" onClick={exportActivatedCsv} className="h-8 px-3 rounded-lg bg-[#1A1A1A] hover:bg-black text-white text-xs font-bold">Exporter CSV</Button>
                  </div>
                </div>
                <div className="mt-3 bg-white rounded-2xl border border-[#E2DFD8] overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F0EBE1] text-[#75807A] text-xs uppercase"><tr>
                      <th className="text-left p-4">Compte</th><th className="text-left p-4">Téléphone</th><th className="text-left p-4">Activé le</th>
                    </tr></thead>
                    <tbody>
                      {activatedAccounts.items.map((d) => (
                        <tr key={d.id} data-testid={`activated-row-${d.id}`} className="border-t border-[#E2DFD8]">
                          <td className="p-4"><div className="flex items-center gap-2"><span className="font-semibold text-[#1A1A1A]">{d.name || "—"}</span><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${d.role === "passenger" ? "bg-[#2563EB]/15 text-[#2563EB]" : "bg-[#16a34a]/15 text-[#16a34a]"}`}>{d.role === "passenger" ? "Passager" : "Chauffeur"}</span>{d.role === "driver" && d.vehicle_type ? <span className="text-[10px] text-[#75807A]">{d.vehicle_type}</span> : null}</div><div className="text-xs text-[#75807A] break-all">{d.email || ""}{d.city ? ` · ${d.city}` : ""}</div></td>
                          <td className="p-4 font-semibold text-[#1A1A1A]">{d.phone || "—"}</td>
                          <td className="p-4 text-xs text-[#75807A]">{d.activated_at ? new Date(d.activated_at).toLocaleString("fr-FR") : "—"}{d.activated_by_admin ? " · par admin" : ""}</td>
                        </tr>
                      ))}
                      {activatedAccounts.items.length === 0 && <tr><td colSpan="3" className="p-8 text-center text-[#75807A]">Aucun compte activé pour l'instant.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {section === "drivers" && (() => {
            const catOf = (d) => d.vehicle_type || d.kyc?.vehicle_type || "moto";
            const CATS = [
              { id: "all", label: "Tous", cls: "bg-[#1A1A1A] text-white" },
              { id: "moto", label: "🏍️ Moto", cls: "bg-[#F26419] text-[#1A1A1A]" },
              { id: "car", label: "🚗 Voiture", cls: "bg-[#1976D2] text-white" },
              { id: "colis", label: "📦 Colis", cls: "bg-[#F26419] text-white" },
              { id: "bike", label: "🚲 Vélo", cls: "bg-[#16a34a] text-white" },
            ];
            const catMatch = (d, id) => id === "all" ? true : id === "colis" ? ["moto", "bike"].includes(catOf(d)) : catOf(d) === id;
            const catCount = (id) => drivers.filter((d) => catMatch(d, id)).length;
            const q = driverSearch.trim().toLowerCase();
            const kycOk = (d) => !driverKyc || (driverKyc === "suspended" ? d.suspended : (!d.suspended && (d.kyc_status || "not_submitted") === driverKyc));
            const actOk = (d) => !driverAct || (driverAct === "yes" ? d.activated : !d.activated);
            const cityOf = (d) => (d.city || d.kyc?.city || "").trim();
            const driverCities = [...new Set(drivers.map(cityOf).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr"));
            const cityOk = (d) => !driverCity || cityOf(d) === driverCity;
            const filteredDrivers = drivers.filter((d) => catMatch(d, driverCat) && kycOk(d) && actOk(d) && cityOk(d) && (!q || (d.name || "").toLowerCase().includes(q) || (d.phone || "").toLowerCase().includes(q) || (d.email || "").toLowerCase().includes(q) || cityOf(d).toLowerCase().includes(q)));
            const rowTint = (d) => d.suspended ? "bg-[#D32F2F]/5" : d.kyc_status === "approved" ? "bg-[#16a34a]/5" : d.kyc_status === "pending" ? "bg-[#E0912F]/10" : "";
            const exportDrivers = () => {
              const rows = [["Nom", "Email", "Telephone", "Categorie", "Statut KYC", "Active", "En ligne", "Ville", "Plaque"]];
              filteredDrivers.forEach((d) => rows.push([d.name || "", d.email || "", d.phone || "", catBadge(catOf(d)).label, d.suspended ? "Suspendu" : (d.kyc_status || ""), d.activated ? "Oui" : "Non", d.available ? "Oui" : "Non", d.city || d.kyc?.city || "", d.kyc?.vehicle_plate || ""]));
              const csv = rows.map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n");
              const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
              const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "chauffeurs.csv";
              document.body.appendChild(a); a.click(); a.remove();
              setTimeout(() => URL.revokeObjectURL(url), 60000);
            };
            const catBadge = (id) => id === "car" ? { label: "Voiture", cls: "bg-[#1976D2]/15 text-[#1976D2]" } : id === "bike" ? { label: "Vélo", cls: "bg-[#16a34a]/15 text-[#16a34a]" } : { label: "Moto", cls: "bg-[#B8860B]/15 text-[#B8860B]" };
            const catStats = (id) => { const list = drivers.filter((d) => catMatch(d, id)); return { online: list.filter((d) => d.available).length, approved: list.filter((d) => d.kyc_status === "approved").length }; };
            const SUMMARY = [
              { id: "moto", label: "🏍️ Moto", cls: "bg-[#F26419]/20 text-[#B8860B]" },
              { id: "car", label: "🚗 Voiture", cls: "bg-[#1976D2]/15 text-[#1976D2]" },
              { id: "colis", label: "📦 Colis", cls: "bg-[#F26419]/15 text-[#D94F2B]" },
              { id: "bike", label: "🚲 Vélo", cls: "bg-[#16a34a]/15 text-[#16a34a]" },
            ];
            return (
            <div>
              {/* Outils Nold Food — Journal des refus + Déblocage des livreurs coincés */}
              <div data-testid="drivers-nold-tools" className="mb-4 flex items-center gap-2 flex-wrap">
                <Button data-testid="drivers-refusals-btn"
                  onClick={() => setRefusalsOpen(true)}
                  title="Journal des refus livreur (manuel + timeout 30 s)"
                  className="h-10 rounded-xl bg-white border-2 border-[#D32F2F] hover:bg-[#D32F2F]/5 text-[#D32F2F] font-bold text-sm px-4">
                  🚫 Journal refus
                </Button>
                <Button data-testid="drivers-unblock-btn"
                  onClick={async () => {
                    if (!window.confirm("Débloquer tous les livreurs coincés ?\n\nCela clôture les commandes fantômes de +3h qui gèlent les livreurs en 'busy' (statut ready_for_pickup jamais fermé), les rend à nouveau disponibles pour l'auto-assign, et relance l'assignation des commandes en attente.\n\nOpération sûre et réversible dans l'historique.")) return;
                    try {
                      const { data } = await api.post("/admin/food/unblock-stuck-drivers");
                      toast.success(`🧹 ${data.unblocked_rides} ride(s) débloquée(s) · ${data.freed_drivers} livreur(s) libéré(s) · ${data.retried_assignments} commande(s) réassignée(s)`);
                    } catch (e) { toast.error(apiError(e)); }
                  }}
                  title="Clôture les rides ready_for_pickup fantômes (+3h) et libère les livreurs coincés"
                  className="h-10 rounded-xl bg-white border-2 border-[#0EA5E9] hover:bg-[#0EA5E9]/5 text-[#0EA5E9] font-bold text-sm px-4">
                  🧹 Débloquer livreurs
                </Button>
              </div>
              <div data-testid="cat-summary" className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {SUMMARY.map((c) => { const s = catStats(c.id); return (
                  <div key={c.id} data-testid={`cat-summary-${c.id}`} className="bg-white rounded-xl border border-[#E2DFD8] p-3">
                    <div className={`inline-block text-xs font-semibold px-2 py-1 rounded-full mb-2 ${c.cls}`}>{c.label}</div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="font-bold text-[#16a34a]">● {s.online} en ligne</span>
                      <span className="text-[#75807A]">{s.approved} approuvés</span>
                    </div>
                  </div>
                ); })}
              </div>
              {driverCities.length > 0 && (
                <div data-testid="city-recruit-panel" className="mb-4 rounded-2xl border border-[#E2DFD8] bg-white p-4">
                  <div className="flex items-center gap-2 font-bold text-[#1A1A1A] mb-3"><MapPin className="w-4 h-4 text-[#D94F2B]" />Recrutement par ville <span className="text-xs font-normal text-[#75807A]">· cliquez pour filtrer</span></div>
                  <div className="flex flex-wrap gap-2">
                    {driverCities.map((ct) => {
                      const list = drivers.filter((d) => cityOf(d) === ct);
                      const online = list.filter((d) => d.available).length;
                      const approved = list.filter((d) => d.kyc_status === "approved").length;
                      return (
                        <button key={ct} data-testid={`city-stat-${ct}`} onClick={() => setDriverCity(driverCity === ct ? "" : ct)}
                          className={`text-left rounded-xl border px-3 py-2 transition-colors ${driverCity === ct ? "border-[#D94F2B] bg-[#FFF3EC]" : "border-[#E2DFD8] bg-[#FDF8F0] hover:border-[#D94F2B]"}`}>
                          <div className="text-sm font-bold text-[#1A1A1A]">{ct} <span className="font-normal text-[#75807A]">· {list.length}</span></div>
                          <div className="text-xs mt-0.5"><span className="font-semibold text-[#16a34a]">● {online} en ligne</span> <span className="text-[#75807A]">· {approved} approuvés</span></div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div data-testid="driver-cat-filter" className="flex flex-wrap gap-2 mb-3">
                {CATS.map((c) => (
                  <button key={c.id} data-testid={`driver-cat-${c.id}`} onClick={() => setDriverCat(c.id)}
                    className={`h-9 px-4 rounded-full text-xs font-bold transition-colors ${driverCat === c.id ? c.cls : "bg-white border border-[#E2DFD8] text-[#75807A]"}`}>
                    {c.label} <span className="opacity-70">({catCount(c.id)})</span>
                  </button>
                ))}
              </div>
              {(() => {
                const norm = (n) => (n || "").trim().toLowerCase().replace(/\s+/g, " ");
                const normP = (p) => { const d = (p || "").replace(/\D/g, ""); return d.length >= 9 ? d.slice(-9) : d; };
                const byName = {}, byPhone = {};
                drivers.forEach((d) => {
                  const nn = norm(d.name); if (nn && nn.length >= 3) (byName[nn] = byName[nn] || []).push(d);
                  const np = normP(d.phone); if (np && np.length >= 8) (byPhone[np] = byPhone[np] || []).push(d);
                });
                const groups = [];
                Object.entries(byName).forEach(([k, m]) => { if (m.length > 1) groups.push({ key: "n:" + k, reason: "même nom", label: m[0].name, members: m }); });
                Object.entries(byPhone).forEach(([k, m]) => { if (m.length > 1) groups.push({ key: "p:" + k, reason: "même téléphone", label: m[0].phone, members: m }); });
                if (groups.length === 0) return null;
                return (
                  <div data-testid="dup-panel" className="mb-4 rounded-2xl border border-[#E0912F]/40 bg-[#FFF7EA] p-4">
                    <div className="flex items-center gap-2 font-bold text-[#B8860B]"><ShieldAlert className="w-4 h-4" />{groups.length} doublon(s) probable(s) détecté(s)</div>
                    <p className="mt-1 text-xs text-[#75807A]">Chauffeurs partageant le même nom ou téléphone. <b>Fusionner</b> = garder un seul compte en transférant portefeuille + historique du doublon. <b>Supprimer</b> = effacer définitivement le compte en trop (aucun transfert). Une confirmation est demandée.</p>
                    <div className="mt-3 space-y-3">
                      {groups.map((g) => (
                        <div key={g.key} data-testid={`dup-group-${g.key}`} className="rounded-xl bg-white border border-[#E2DFD8] p-3">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="text-xs font-semibold text-[#75807A]">Regroupés par {g.reason} · <span className="text-[#1A1A1A]">{g.label}</span></div>
                            <Button data-testid={`dup-wa-${g.key}`} onClick={() => waRelanceDup(g)} title="Relancer les comptes en double sur WhatsApp pour les inviter à fusionner" className="h-8 px-3 rounded-lg bg-[#25D366] hover:bg-[#1da851] text-white text-xs shrink-0"><MessageCircle className="w-3 h-3 mr-1" />Relance WhatsApp</Button>
                          </div>
                          <div className="space-y-1">
                            {g.members.map((d) => (
                              <div key={d.id} className="flex items-center justify-between gap-2 rounded-lg bg-[#FDF8F0] px-3 py-2">
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold text-[#1A1A1A] truncate">{d.name || "—"} <span className="text-xs font-normal text-[#75807A]">· {Math.round(d.wallet || 0)} FCFA · {d.kyc_status || "—"}</span></div>
                                  <div className="text-xs text-[#75807A] break-all">{d.email || d.phone} · créé {d.created_at ? new Date(d.created_at).toLocaleDateString("fr-FR") : "—"}</div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <Button data-testid={`dup-merge-${d.id}`} onClick={() => { setMergeSrc(d); setMergeTargetId(""); setMergeQuery(g.label || ""); }} className="h-8 px-3 rounded-lg bg-white border border-[#6D28D9] text-[#6D28D9] text-xs shrink-0"><GitMerge className="w-3 h-3 mr-1" />Fusionner</Button>
                                  <Button data-testid={`dup-delete-${d.id}`} onClick={() => openMod("delete", "driver", d)} title="Supprimer définitivement ce compte en double" className="h-8 px-3 rounded-lg bg-[#D32F2F] hover:bg-[#b71c1c] text-white text-xs shrink-0"><Trash2 className="w-3 h-3 mr-1" />Supprimer</Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {ghostCount > 0 && (
                <div data-testid="ghost-purge-panel" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#E2DFD8] bg-[#FDF8F0] p-4">
                  <div className="text-sm text-[#3B423E]"><span className="font-bold text-[#1A1A1A]">{ghostCount}</span> compte(s) chauffeur de test détecté(s) (sans nom ni téléphone). Nettoyez avant le lancement.</div>
                  <Button data-testid="purge-ghosts-btn" onClick={purgeGhosts} className="h-9 px-3 rounded-lg bg-[#D32F2F] hover:bg-[#b71c1c] text-white text-xs font-bold"><Trash2 className="w-3 h-3 mr-1" />Purger les comptes de test ({ghostCount})</Button>
                </div>
              )}
              <div className="mb-4 flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#75807A]" />
                  <Input data-testid="driver-search" value={driverSearch} onChange={(e) => setDriverSearch(e.target.value)} placeholder="Rechercher un chauffeur (nom, téléphone ou email)" className="pl-9 h-11 rounded-xl bg-white border-[#E2DFD8]" />
                </div>
                <select data-testid="driver-filter-kyc" value={driverKyc} onChange={(e) => setDriverKyc(e.target.value)} className="h-11 rounded-xl border border-[#E2DFD8] bg-white px-3 text-sm"><option value="">Tous statuts</option><option value="approved">Approuvé</option><option value="pending">En attente</option><option value="rejected">Rejeté</option><option value="not_submitted">Non soumis</option><option value="suspended">Suspendu</option></select>
                <select data-testid="driver-filter-activation" value={driverAct} onChange={(e) => setDriverAct(e.target.value)} className="h-11 rounded-xl border border-[#E2DFD8] bg-white px-3 text-sm"><option value="">Activation : tous</option><option value="yes">Activé</option><option value="no">Non activé</option></select>
                <select data-testid="driver-filter-city" value={driverCity} onChange={(e) => setDriverCity(e.target.value)} className="h-11 rounded-xl border border-[#E2DFD8] bg-white px-3 text-sm"><option value="">Toutes villes</option>{driverCities.map((ct) => <option key={ct} value={ct}>{ct}</option>)}</select>
                <Button data-testid="driver-export-csv" onClick={exportDrivers} className="h-11 px-4 rounded-xl bg-[#1A1A1A] hover:bg-black text-white text-sm"><Download className="w-4 h-4 mr-1" />Export CSV</Button>
                <PinLookupTool />
              </div>
              <div className="bg-white rounded-2xl border border-[#E2DFD8] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F0EBE1] text-[#75807A] text-xs uppercase"><tr>
                  <th className="text-left p-4">Nom</th><th className="text-left p-4">Catégorie</th><th className="text-left p-4">Ville</th><th className="text-left p-4">Tél</th><th className="text-left p-4">KYC</th><th className="text-right p-4">Actions</th>
                </tr></thead>
                <tbody>
                  {filteredDrivers.map((d) => (
                    <tr key={d.id} data-testid={`driver-row-${d.id}`} className={`border-t border-[#E2DFD8] ${rowTint(d)}`}>
                      <td className="p-4 font-medium"><button data-testid={`driver-name-${d.id}`} onClick={() => setDossier({ type: "driver", row: d })} className="text-left font-medium text-[#1A1A1A] hover:text-[#D94F2B] hover:underline transition-colors">{d.name || "—"}</button>{d.email && <div data-testid={`driver-email-${d.id}`} className="text-xs text-[#75807A] break-all">{d.email}</div>}<div className="text-xs text-[#75807A]">{d.kyc?.vehicle_plate || ""}</div></td>
                      <td className="p-4"><span data-testid={`driver-cat-badge-${d.id}`} className={`text-xs font-semibold px-2 py-1 rounded-full ${catBadge(catOf(d)).cls}`}>{catBadge(catOf(d)).label}</span></td>
                      <td className="p-4"><span data-testid={`driver-city-${d.id}`} className="text-[#1A1A1A]">{cityOf(d) || <span className="text-[#75807A]">—</span>}</span></td>
                      <td className="p-4">{d.phone}</td>
                      <td className="p-4"><span className="text-xs font-semibold px-2 py-1 rounded-full bg-[#F0EBE1] capitalize">{d.kyc_status || "—"}</span>{d.kyc?.ai_suspicious && <span data-testid={`ai-flag-${d.id}`} className="ml-1 inline-block text-xs font-semibold px-2 py-1 rounded-full bg-[#D32F2F]/15 text-[#D32F2F]">⚠️ IA</span>}{d.suspended && <span data-testid={`suspended-badge-${d.id}`} className="ml-1 inline-block text-xs font-semibold px-2 py-1 rounded-full bg-[#D32F2F]/15 text-[#D32F2F]">🚫 {t("admin.suspended")}</span>}</td>
                      <td className="p-4 text-right">
                        <div className="flex gap-2 justify-end">
                          {d.kyc && (
                            <Button data-testid={`view-docs-${d.id}`} onClick={() => setReviewDriver(d)} className="h-9 px-3 rounded-lg bg-[#1A1A1A] text-white text-xs"><Eye className="w-3 h-3 mr-1" />{t("admin.viewDocs")}</Button>
                          )}
                          <Button data-testid={`dossier-driver-${d.id}`} onClick={() => setDossier({ type: "driver", row: d })} className="h-9 px-3 rounded-lg bg-white border border-[#D94F2B] text-[#D94F2B] text-xs"><FolderOpen className="w-3 h-3 mr-1" />Dossier</Button>
                          {d.kyc_status === "pending" ? (
                            <>
                              <Button data-testid={`approve-${d.id}`} onClick={() => validate(d.id, true)} className="h-9 px-3 rounded-lg bg-[#F26419] text-white text-xs"><Check className="w-3 h-3 mr-1" />{t("admin.approve")}</Button>
                              <Button data-testid={`reject-${d.id}`} onClick={() => validate(d.id, false)} className="h-9 px-3 rounded-lg bg-white border border-[#D32F2F] text-[#D32F2F] text-xs"><X className="w-3 h-3 mr-1" />{t("admin.reject")}</Button>
                            </>
                          ) : (!d.kyc && <span className="text-xs text-[#75807A]">—</span>)}
                          {d.suspended ? (
                            <Button data-testid={`unsuspend-${d.id}`} onClick={() => openMod("unsuspend", "driver", d)} className="h-9 px-3 rounded-lg bg-white border border-[#16a34a] text-[#16a34a] text-xs"><Check className="w-3 h-3 mr-1" />{t("admin.unsuspend")}</Button>
                          ) : (
                            <Button data-testid={`suspend-${d.id}`} onClick={() => openMod("suspend", "driver", d)} className="h-9 px-3 rounded-lg bg-white border border-[#B8860B] text-[#B8860B] text-xs"><Ban className="w-3 h-3 mr-1" />{t("admin.suspend")}</Button>
                          )}
                          <Button data-testid={`merge-driver-${d.id}`} onClick={() => { setMergeSrc(d); setMergeTargetId(""); setMergeQuery(""); }} className="h-9 px-3 rounded-lg bg-white border border-[#6D28D9] text-[#6D28D9] text-xs"><GitMerge className="w-3 h-3 mr-1" />Fusionner</Button>
                          <Button data-testid={`delete-driver-${d.id}`} onClick={() => openMod("delete", "driver", d)} className="h-9 px-3 rounded-lg bg-[#D32F2F] text-white text-xs"><Trash2 className="w-3 h-3 mr-1" />{t("admin.deleteProfile")}</Button>
                          <Button data-testid={`wa-driver-${d.id}`} onClick={() => waRelance(d.phone, d.name)} title="Contacter sur WhatsApp (gratuit)" className="h-9 px-3 rounded-lg bg-[#25D366] hover:bg-[#1da851] text-white text-xs"><MessageCircle className="w-3 h-3 mr-1" />WhatsApp</Button>
                          {isNoldDomain() && d.kyc_status === "approved" && (
                            <Button data-testid={`bc-driver-${d.id}`}
                              onClick={() => {
                                const qs = new URLSearchParams({
                                  name: d.name || "",
                                  title: "Livreur Nold Go",
                                  email: d.email || "contact@noldgo.com",
                                  phone: d.phone || "+32 492 84 23 54",
                                  website: "noldgo.com",
                                }).toString();
                                window.open(`${API}/marketing/business-card.pdf?${qs}`, "_blank");
                              }}
                              title="Générer la carte de visite Nold Go au nom du chauffeur"
                              className="h-9 px-3 rounded-lg bg-[#F26419] hover:bg-[#D85312] text-white text-xs">
                              <QrCode className="w-3 h-3 mr-1" />Carte de visite
                            </Button>
                          )}
                        </div>
                        {d.kyc_status === "approved" && (() => {
                          const exp = d.subscription_expires_at;
                          const active = exp && new Date(exp) > new Date();
                          return (
                            <div className="mt-2 flex items-center justify-end gap-2">
                              <span data-testid={`sub-badge-${d.id}`} className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${active ? "bg-[#16a34a]/15 text-[#16a34a]" : "bg-[#D32F2F]/15 text-[#D32F2F]"}`}>{active ? `${t("admin.subActive")} · ${new Date(exp).toLocaleDateString("fr-FR")}` : t("admin.subExpired")}</span>
                              <Button data-testid={`activate-sub-${d.id}`} onClick={() => activateSub(d.id)} className="h-8 px-3 rounded-lg bg-[#16a34a] text-white text-[11px]"><Check className="w-3 h-3 mr-1" />{t("admin.activateSub")}</Button>
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                  {filteredDrivers.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-[#75807A]">Aucun chauffeur dans cette catégorie</td></tr>}
                </tbody>
              </table>
              </div>
            </div>
            );
          })()}
          {section === "carpool_verif" && <CarpoolVerifAdmin />}
          {section === "social" && <SocialAgentAdmin />}


          {section === "review" && (
            <div className="bg-white rounded-2xl border border-[#E2DFD8] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F0EBE1] text-[#75807A] text-xs uppercase"><tr>
                  <th className="text-left p-4">Nom</th><th className="text-left p-4">Motif IA</th><th className="text-right p-4">Actions</th>
                </tr></thead>
                <tbody>
                  {drivers.filter((d) => d.kyc?.ai_suspicious && d.kyc_status === "pending").map((d) => (
                    <tr key={d.id} data-testid={`review-row-${d.id}`} className="border-t border-[#E2DFD8]">
                      <td className="p-4 font-medium">{d.name || "—"}<div className="text-xs text-[#75807A]">{d.phone}</div></td>
                      <td className="p-4 text-xs text-[#D32F2F] max-w-xs">
                        {Object.values(d.kyc?.ai_review || {}).map((r, i) => (r.reason && !r.skipped && !r.error) ? <div key={i}>• {r.reason}</div> : null)}
                        {d.kyc?.selfie_review && !d.kyc.selfie_review.error && !d.kyc.selfie_review.is_match && <div>• {t("admin.selfieMatch")} ✗</div>}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex gap-2 justify-end">
                          <Button data-testid={`review-view-${d.id}`} onClick={() => setReviewDriver(d)} className="h-9 px-3 rounded-lg bg-[#1A1A1A] text-white text-xs"><Eye className="w-3 h-3 mr-1" />{t("admin.viewDocs")}</Button>
                          <Button data-testid={`review-approve-${d.id}`} onClick={() => validate(d.id, true)} className="h-9 px-3 rounded-lg bg-[#F26419] text-white text-xs"><Check className="w-3 h-3 mr-1" />{t("admin.approve")}</Button>
                          <Button data-testid={`review-reject-${d.id}`} onClick={() => validate(d.id, false)} className="h-9 px-3 rounded-lg bg-white border border-[#D32F2F] text-[#D32F2F] text-xs"><X className="w-3 h-3 mr-1" />{t("admin.reject")}</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {drivers.filter((d) => d.kyc?.ai_suspicious && d.kyc_status === "pending").length === 0 && <tr><td colSpan="3" className="p-8 text-center text-[#75807A]">{t("admin.reviewEmpty")}</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {reviewDriver && (
            <div data-testid="kyc-review-modal" onClick={() => setReviewDriver(null)} className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
              <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-2xl max-h-[88vh] overflow-y-auto p-6 relative">
                <button data-testid="kyc-review-close" onClick={() => setReviewDriver(null)} className="absolute top-4 right-4 w-9 h-9 rounded-xl hover:bg-[#F0EBE1] flex items-center justify-center"><X className="w-5 h-5 text-[#75807A]" /></button>
                <h3 className="font-head font-bold text-xl text-[#1A1A1A]">{t("admin.docsTitle")}</h3>
                <div className="mt-1 text-sm text-[#75807A]">{reviewDriver.name} · {reviewDriver.phone}</div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                  <span className="px-2 py-0.5 rounded-full bg-[#F0EBE1] font-semibold capitalize">{reviewDriver.kyc_status}</span>
                  <span className="px-2 py-0.5 rounded-full bg-[#F26419]/25 text-[#8a6d00] font-semibold">{reviewDriver.vehicle_type === "car" ? t("delivery.modeCar") : t("delivery.modeMoto")}</span>
                  <span className="text-[#75807A]">{reviewDriver.kyc?.vehicle_model} · {reviewDriver.kyc?.vehicle_plate}</span>
                </div>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { key: "selfie", label: t("drv.selfie") },
                    { key: "license_doc", label: t("drv.license") },
                    { key: "id_card_doc", label: t("drv.idCardFront") },
                    { key: "id_card_back_doc", label: t("drv.idCardBack") },
                    { key: "vehicle_doc", label: t("drv.vehicle") },
                  ].map((doc) => {
                    const src = reviewDriver.kyc?.[doc.key];
                    const isImg = src && (src.startsWith("data:image") || !src.startsWith("data:"));
                    return (
                      <div key={doc.key} data-testid={`doc-${doc.key}`} className="rounded-xl border border-[#E2DFD8] overflow-hidden">
                        <div className="px-3 py-2 text-xs font-semibold text-[#3B423E] bg-[#F0EBE1] flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-[#D94F2B]" />{doc.label}</div>
                        {src ? (
                          isImg ? (
                            <a href={src} target="_blank" rel="noreferrer"><img src={src} alt={doc.label} className="w-full h-40 object-cover hover:opacity-90 transition-opacity" /></a>
                          ) : (
                            <a href={src} target="_blank" rel="noreferrer" className="flex items-center justify-center h-40 text-[#D94F2B] text-sm font-semibold underline">{t("admin.openDoc")}</a>
                          )
                        ) : (
                          <div className="h-40 flex items-center justify-center text-[#75807A] text-sm">{t("admin.noDoc")}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {(reviewDriver.driver_photo || reviewDriver.photo) && (
                  <div className="mt-4 rounded-xl border-2 border-[#16a34a]/40 overflow-hidden" data-testid="driver-live-selfie">
                    <div className="px-3 py-2 text-xs font-semibold text-[#16a34a] bg-[#16a34a]/10 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Selfie identité (pris en direct)</div>
                    <a href={reviewDriver.driver_photo || reviewDriver.photo} target="_blank" rel="noreferrer"><img src={reviewDriver.driver_photo || reviewDriver.photo} alt="selfie live" className="w-full h-48 object-cover hover:opacity-90 transition-opacity" /></a>
                  </div>
                )}
                <div className="mt-4" data-testid="selfie-history">
                  <div className="text-xs font-semibold text-[#3B423E] flex items-center gap-1.5"><Video className="w-3.5 h-3.5 text-[#D94F2B]" /> Historique des selfies périodiques {selfieHistory?.count ? `(${selfieHistory.count})` : ""}</div>
                  {selfieHistory === null ? (
                    <div className="mt-2 text-xs text-[#75807A]">Chargement…</div>
                  ) : (selfieHistory.items || []).length === 0 ? (
                    <div data-testid="selfie-history-empty" className="mt-2 text-xs text-[#75807A]">Aucun selfie périodique enregistré pour ce chauffeur.</div>
                  ) : (
                    <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {selfieHistory.items.map((h) => (
                        <button key={h.id} data-testid={`selfie-history-${h.id}`} onClick={() => openImage(`/admin/selfie-history/${h.id}`)} className="rounded-lg border border-[#E2DFD8] overflow-hidden text-left hover:border-[#D94F2B] transition-colors">
                          <div className="h-20 bg-[#F0EBE1] flex items-center justify-center text-[#75807A]"><Eye className="w-4 h-4" /></div>
                          <div className="px-1.5 py-1 text-[10px] text-[#75807A]">{(h.created_at || "").slice(0, 10)}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {reviewDriver.kyc?.ai_review && (
                  <div data-testid="ai-review" className="mt-5 rounded-xl border border-[#E2DFD8] p-4">
                    <div className="flex items-center gap-2 font-semibold text-[#1A1A1A]">
                      <FileText className="w-4 h-4 text-[#D94F2B]" /> {t("admin.aiReview")}
                      {reviewDriver.kyc?.ai_suspicious
                        ? <span className="text-xs px-2 py-0.5 rounded-full bg-[#D32F2F]/15 text-[#D32F2F] font-semibold">{t("admin.aiSuspicious")}</span>
                        : <span className="text-xs px-2 py-0.5 rounded-full bg-[#16a34a]/15 text-[#16a34a] font-semibold">{t("admin.aiOk")}</span>}
                    </div>
                    <div className="mt-3 space-y-2 text-sm">
                      {Object.entries(reviewDriver.kyc.ai_review).map(([k, r]) => (
                        <div key={k} className="flex items-start justify-between gap-3 border-t border-[#F0EBE1] pt-2">
                          <span className="text-[#75807A] capitalize">{k.replace("_doc", "").replace("_", " ")}</span>
                          <span className="text-right max-w-[65%]">
                            {r.skipped ? <em className="text-[#75807A]">{r.reason}</em>
                              : r.error ? <em className="text-[#D32F2F]">IA indisponible</em>
                              : <><b className={r.is_expected && r.looks_authentic ? "text-[#16a34a]" : "text-[#D32F2F]"}>{r.document_type} · {Math.round((r.confidence || 0) * 100)}%</b><div className="text-xs text-[#75807A]">{r.reason}</div></>}
                          </span>
                        </div>
                      ))}
                      {reviewDriver.kyc?.selfie_review && !reviewDriver.kyc.selfie_review.error && (
                        <div className="flex items-start justify-between gap-3 border-t border-[#F0EBE1] pt-2">
                          <span className="text-[#75807A]">{t("admin.selfieMatch")}</span>
                          <span className="text-right max-w-[65%]">
                            <b className={reviewDriver.kyc.selfie_review.is_match ? "text-[#16a34a]" : "text-[#D32F2F]"}>{reviewDriver.kyc.selfie_review.is_match ? "✓" : "✗"} {Math.round((reviewDriver.kyc.selfie_review.confidence || 0) * 100)}%</b>
                            <div className="text-xs text-[#75807A]">{reviewDriver.kyc.selfie_review.reason}</div>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {reviewDriver.kyc_status === "pending" && (
                  <div className="mt-6 flex gap-3">
                    <Button data-testid="review-approve" onClick={() => { validate(reviewDriver.id, true); setReviewDriver(null); }} className="flex-1 h-12 rounded-xl bg-[#F26419] text-white font-bold"><Check className="w-4 h-4 mr-1" />{t("admin.approve")}</Button>
                    <Button data-testid="review-reject" onClick={() => { validate(reviewDriver.id, false); setReviewDriver(null); }} className="flex-1 h-12 rounded-xl bg-white border-2 border-[#D32F2F] text-[#D32F2F] font-bold"><X className="w-4 h-4 mr-1" />{t("admin.reject")}</Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {section === "team" && (
            <div className="space-y-4">
              {/* Agenda RDV admin */}
              <div data-testid="admin-agenda-card" className="bg-white rounded-2xl border-2 border-[#F26419]/40 p-5">
                <h3 className="font-head font-bold flex items-center gap-2 text-[#1A1A1A]">📅 Agenda — Rendez-vous</h3>
                <p className="mt-1 text-xs text-[#75807A]">Notez vos rendez-vous restaurateurs, chauffeurs, clients. Rappel visuel des prochains RDV.</p>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Input data-testid="appt-title" value={apptForm.title} onChange={(e) => setApptForm({ ...apptForm, title: e.target.value })} placeholder="Titre (ex: RDV Chez Léon)" className="h-10 rounded-xl border-[#E2DFD8]" />
                  <Input data-testid="appt-at" type="datetime-local" value={apptForm.at} onChange={(e) => setApptForm({ ...apptForm, at: e.target.value })} className="h-10 rounded-xl border-[#E2DFD8]" />
                  <select data-testid="appt-kind" value={apptForm.kind} onChange={(e) => setApptForm({ ...apptForm, kind: e.target.value })} className="h-10 rounded-xl border border-[#E2DFD8] px-3 text-sm bg-white">
                    <option value="prospection">🎯 Prospection</option>
                    <option value="resto">🍽 Restaurant</option>
                    <option value="livreur">🚴 Livreur</option>
                    <option value="client">👤 Client</option>
                    <option value="autre">📌 Autre</option>
                  </select>
                </div>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Input data-testid="appt-with" value={apptForm.with_name} onChange={(e) => setApptForm({ ...apptForm, with_name: e.target.value })} placeholder="Avec qui (nom)" className="h-10 rounded-xl border-[#E2DFD8]" />
                  <Input data-testid="appt-phone" value={apptForm.phone} onChange={(e) => setApptForm({ ...apptForm, phone: e.target.value })} placeholder="Téléphone" className="h-10 rounded-xl border-[#E2DFD8]" />
                  <Input data-testid="appt-location" value={apptForm.location} onChange={(e) => setApptForm({ ...apptForm, location: e.target.value })} placeholder="Lieu / adresse" className="h-10 rounded-xl border-[#E2DFD8]" />
                </div>
                <Textarea data-testid="appt-notes" value={apptForm.notes} onChange={(e) => setApptForm({ ...apptForm, notes: e.target.value })} placeholder="Notes libres…" className="mt-2 rounded-xl border-[#E2DFD8] min-h-[60px]" />
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-[#75807A] shrink-0">📧 Envoyer à :</span>
                  <Input data-testid="appt-notify-email" type="email" value={apptForm.notify_email} onChange={(e) => setApptForm({ ...apptForm, notify_email: e.target.value })} placeholder="email de l'agent / collaborateur (facultatif)" className="flex-1 h-10 rounded-xl border-[#E2DFD8]" />
                </div>
                <div className="mt-2 flex justify-end">
                  <Button data-testid="appt-save" onClick={saveAppt} disabled={apptSaving} className="h-10 px-4 rounded-xl bg-[#F26419] hover:bg-[#D94F2B] text-white font-bold">{apptSaving ? "Enregistrement…" : "+ Enregistrer le RDV"}</Button>
                </div>

                {appts.length > 0 && (
                  <div className="mt-5 space-y-2">
                    <h4 className="text-xs font-bold text-[#75807A] uppercase tracking-wider">Prochains rendez-vous ({appts.length})</h4>
                    {appts.map((a) => {
                      const dt = a.at ? new Date(a.at) : null;
                      const isDone = a.status === "done";
                      return (
                        <div key={a.id} data-testid={`appt-${a.id}`} className={`flex items-start justify-between gap-2 rounded-lg border px-3 py-2 ${isDone ? "bg-[#F0EBE1] border-[#E2DFD8] opacity-60" : "bg-white border-[#F26419]/30"}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-sm font-bold ${isDone ? "line-through text-[#75807A]" : "text-[#1A1A1A]"}`}>{a.title}</span>
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FFF3E6] text-[#7A3A00] font-bold uppercase">{a.kind || "autre"}</span>
                            </div>
                            <div className="mt-0.5 text-xs text-[#75807A]">
                              📅 {dt ? dt.toLocaleString("fr-BE", { dateStyle: "medium", timeStyle: "short" }) : a.at}
                              {a.with_name && <> · 👤 {a.with_name}</>}
                              {a.phone && <> · 📞 <a href={`tel:${a.phone}`} className="text-[#F26419] hover:underline">{a.phone}</a></>}
                              {a.location && <> · 📍 {a.location}</>}
                            </div>
                            {a.notes && <p className="mt-1 text-xs text-[#3B423E] whitespace-pre-line">{a.notes}</p>}
                          </div>
                          <div className="flex flex-col gap-1 shrink-0">
                            {!isDone && <Button data-testid={`appt-done-${a.id}`} onClick={() => markDone(a.id)} className="h-7 px-2 rounded-lg bg-[#16a34a] hover:bg-[#128a3e] text-white text-[10px]">✓ Fait</Button>}
                            <Button data-testid={`appt-delete-${a.id}`} onClick={() => deleteAppt(a.id)} className="h-7 px-2 rounded-lg bg-white border border-[#E2DFD8] text-[#75807A] hover:border-[#DC2626] hover:text-[#DC2626] text-[10px]">Supprimer</Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Admins partenaires (accès complet) */}
              <div data-testid="admin-partners-card" className="bg-white rounded-2xl border-2 border-[#7C3AED]/60 p-5">
                <h3 className="font-head font-bold flex items-center gap-2 text-[#1A1A1A]"><UserPlus className="w-4 h-4 text-[#7C3AED]" /> Admins partenaires (accès complet)</h3>
                <p className="mt-1 text-xs text-[#75807A]">Invitez un partenaire à disposer du même accès admin que vous. Il pourra se connecter depuis n'importe quel ordinateur avec son email + code OTP.</p>

                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                  <Input data-testid="admin-invite-email" value={adminInviteEmail} onChange={(e) => setAdminInviteEmail(e.target.value)} placeholder="email@partenaire.com" className="flex-1 h-10 rounded-xl border-[#E2DFD8]" />
                  <Input data-testid="admin-invite-name" value={adminInviteName} onChange={(e) => setAdminInviteName(e.target.value)} placeholder="Nom (facultatif)" className="sm:w-52 h-10 rounded-xl border-[#E2DFD8]" />
                  <Button data-testid="admin-invite-btn" onClick={inviteAdminPartner} disabled={adminInviting} className="h-10 px-4 rounded-xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold">
                    {adminInviting ? "Envoi…" : "Inviter comme admin"}
                  </Button>
                </div>

                {(adminPartners.env_admins?.length > 0 || adminPartners.extra_admins?.length > 0) && (
                  <div className="mt-4 space-y-2">
                    {adminPartners.env_admins?.map((e) => (
                      <div key={e} className="flex items-center justify-between rounded-lg bg-[#FDF8F0] px-3 py-2 text-sm">
                        <div><b>{e}</b> <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#F0EBE1] text-[#75807A]">FONDATEUR · env</span></div>
                        <span className="text-xs text-[#75807A]">Non révocable</span>
                      </div>
                    ))}
                    {adminPartners.extra_admins?.map((a) => (
                      <div key={a.email} className="flex items-center justify-between rounded-lg bg-white border border-[#E2DFD8] px-3 py-2 text-sm">
                        <div><b>{a.email}</b> <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#EDE9FE] text-[#7C3AED] font-bold">PARTENAIRE</span> <span className="ml-2 text-xs text-[#75807A]">{a.name}</span></div>
                        <Button data-testid={`admin-revoke-${a.email}`} onClick={() => revokeAdminPartner(a.email)} className="h-8 px-3 rounded-lg bg-white border border-[#E2DFD8] text-[#75807A] hover:border-[#F26419] hover:text-[#F26419] text-xs">Révoquer</Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {agentApps.filter((a) => a.status === "pending").length > 0 && (
                <div data-testid="agent-apps-card" className="bg-white rounded-2xl border-2 border-[#F26419]/60 p-5">
                  <h3 className="font-head font-bold flex items-center gap-2"><UserPlus className="w-4 h-4 text-[#D94F2B]" /> {t("admin.agentApps")} <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-[#F26419]/25 text-[#B8860B] font-bold">{agentApps.filter((a) => a.status === "pending").length}</span></h3>
                  <div className="mt-3 space-y-2">
                    {agentApps.filter((a) => a.status === "pending").map((a) => (
                      <div key={a.id} data-testid={`agent-app-${a.id}`} className="flex items-start justify-between gap-3 border-t border-[#F0EBE1] pt-3">
                        <div className="min-w-0">
                          <div className="font-semibold text-[#1A1A1A]">{a.name} <span className="text-xs font-normal text-[#75807A]">· {a.phone}</span></div>
                          <div className="text-xs text-[#75807A]">{a.zone || "—"} · {new Date(a.created_at).toLocaleDateString("fr-FR")}</div>
                          {a.message && <p className="mt-1 text-sm text-[#3B423E]">{a.message}</p>}
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                          <Button data-testid={`agent-app-approve-${a.id}`} onClick={() => approveAgentApp(a.id)} className="h-9 px-3 rounded-lg bg-[#16a34a] text-white text-xs">{t("admin.appApprove")}</Button>
                          <Button data-testid={`agent-app-reject-${a.id}`} onClick={() => rejectAgentApp(a.id)} className="h-9 px-3 rounded-lg bg-white border border-[#E2DFD8] text-[#75807A] text-xs">{t("admin.appReject")}</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {leaderboard.length > 0 && (
                <div data-testid="team-leaderboard" className="bg-white rounded-2xl border border-[#E2DFD8] p-5">
                  <h3 className="font-head font-bold flex items-center gap-2"><Trophy className="w-4 h-4 text-[#F26419]" /> {t("admin.leaderboard")}</h3>
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    {leaderboard.slice(0, 3).map((a) => (
                      <div key={a.id} className="rounded-xl bg-[#FDF8F0] p-4 text-center">
                        <div className={`mx-auto w-9 h-9 rounded-full flex items-center justify-center font-black text-white ${a.rank === 1 ? "bg-[#F26419]" : a.rank === 2 ? "bg-[#C0C0C0]" : "bg-[#CD7F32]"}`}>{a.rank}</div>
                        <div className="mt-2 text-sm font-semibold truncate">{a.name}</div>
                        {a.rank === 1 && a.recruited_month > 0 && <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#F26419] to-[#D94F2B] text-white px-2 py-0.5 text-[10px] font-bold"><Trophy className="w-2.5 h-2.5" /> {t("admin.topBadge")}</div>}
                        <div className="text-[11px] text-[#75807A] truncate">{a.zone || "—"}</div>
                        <div className="mt-1 font-head font-black text-xl">{a.recruited_month}</div>
                        <div className="text-[11px] text-[#75807A]">{t("admin.ridersMonth")}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Bonus + Add agent */}
              <div className="grid lg:grid-cols-2 gap-4">
                <div data-testid="agent-bonus-card" className="bg-white rounded-2xl border border-[#E2DFD8] p-5">
                  <label className="text-sm font-semibold text-[#1A1A1A]">{t("admin.bonusLabel")}</label>
                  <div className="mt-2 flex gap-2">
                    <Input data-testid="agent-bonus-input" type="number" value={bonusInput} onChange={(e) => setBonusInput(e.target.value)} className="h-12 rounded-xl border-2" />
                    <Button data-testid="agent-bonus-save" onClick={saveBonus} className="h-12 px-5 rounded-xl bg-[#1A1A1A] text-white font-bold">{t("admin.saveBonus")}</Button>
                  </div>
                  <p className="mt-2 text-xs text-[#75807A]">1 inscription = {fcfa(agentBonus)}</p>
                  <label className="mt-3 block text-sm font-semibold text-[#1A1A1A]">{t("admin.thresholdLabel")}</label>
                  <Input data-testid="payout-threshold-input" type="number" value={thresholdInput} onChange={(e) => setThresholdInput(e.target.value)} className="mt-2 h-12 rounded-xl border-2" />
                  <div className="mt-4 pt-4 border-t border-[#F0EBE1]">
                    <label className="block text-sm font-semibold text-[#1A1A1A]">{t("admin.payoutAccountsLabel")}</label>
                    <div className="mt-2 space-y-2">
                      <Input data-testid="om-asso-input" value={omAssoInput} onChange={(e) => setOmAssoInput(e.target.value)} placeholder={t("admin.omAssoLabel")} className="h-11 rounded-xl border-2" />
                      <Input data-testid="momo-pay-input" value={momoPayInput} onChange={(e) => setMomoPayInput(e.target.value)} placeholder={t("admin.momoPayLabel")} className="h-11 rounded-xl border-2" />
                      <Button data-testid="payout-accounts-save" onClick={saveBonus} className="w-full h-11 rounded-xl bg-[#1A1A1A] text-white font-bold">{t("admin.saveBonus")}</Button>
                    </div>
                    <p className="mt-2 text-xs text-[#75807A]">{t("admin.payoutAccountsNote")}</p>
                  </div>
                </div>
                <div data-testid="add-agent-card" className="bg-white rounded-2xl border border-[#E2DFD8] p-5">
                  <label className="text-sm font-semibold text-[#1A1A1A]">{t("admin.addAgent")}</label>
                  <div className="mt-2 space-y-2">
                    <Input data-testid="new-agent-phone" value={newAgent.phone} onChange={(e) => setNewAgent({ ...newAgent, phone: e.target.value })} placeholder={t("admin.agentPhonePh")} className="h-11 rounded-xl border-2" />
                    <Input data-testid="new-agent-email" type="email" value={newAgent.email || ""} onChange={(e) => setNewAgent({ ...newAgent, email: e.target.value })} placeholder="Email (connexion Espace Équipe)" className="h-11 rounded-xl border-2" />
                    <div className="flex gap-2">
                      <Input data-testid="new-agent-name" value={newAgent.name} onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })} placeholder={t("admin.agentNamePh")} className="h-11 rounded-xl border-2" />
                      <Input data-testid="new-agent-zone" value={newAgent.zone} onChange={(e) => setNewAgent({ ...newAgent, zone: e.target.value })} placeholder={t("admin.agentZonePh")} className="h-11 rounded-xl border-2" />
                    </div>
                    <Button data-testid="add-agent-btn" onClick={addAgent} className="w-full h-11 rounded-xl bg-[#F26419] text-white font-bold"><UserPlus className="w-4 h-4 mr-1" />{t("admin.add")}</Button>
                  </div>
                </div>
              </div>

              {/* Messages de l'équipe (fil des agents) */}
              <div data-testid="admin-team-feed" className="bg-white rounded-2xl border border-[#E2DFD8] p-5">
                <h3 className="font-head font-bold flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#D94F2B]" /> Messages de l'équipe ({teamFeed.length})</h3>
                <p className="mt-1 text-xs text-[#75807A]">Comptes-rendus et messages laissés par vos agents. Répondez-leur ici.</p>
                <div className="mt-3 flex gap-2">
                  <Input data-testid="admin-team-feed-input" value={teamMsg} onChange={(e) => setTeamMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postTeamMsg()} placeholder="Répondre à l'équipe…" className="h-11 rounded-xl border-2 flex-1" />
                  <Button data-testid="admin-team-feed-post" onClick={postTeamMsg} className="h-11 px-4 rounded-xl bg-[#F26419] text-white font-bold">Envoyer</Button>
                </div>
                <div className="mt-3 space-y-2 max-h-96 overflow-y-auto">
                  {teamFeed.length === 0 && <p data-testid="admin-team-feed-empty" className="text-sm text-[#75807A] py-4 text-center">Aucun message de l'équipe pour le moment.</p>}
                  {teamFeed.map((m) => (
                    <div key={m.id} data-testid={`admin-feed-${m.id}`} className="group rounded-xl border border-[#E2DFD8] p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-[#1A1A1A]">{m.author_name}{m.author_role === "admin" && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#1A1A1A] text-white">Admin</span>}{m.author_zone && <span className="ml-1 text-xs font-normal text-[#75807A]">· {m.author_zone}</span>}</div>
                          <p className="mt-1 text-sm text-[#3B423E] whitespace-pre-wrap">{m.text}</p>
                          <div className="mt-1 text-[10px] text-[#75807A]">{new Date(m.created_at).toLocaleString("fr-FR")}</div>
                        </div>
                        <button data-testid={`admin-feed-del-${m.id}`} onClick={() => deleteTeamMsg(m.id)} className="text-[#D32F2F] opacity-60 hover:opacity-100 transition-opacity shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Accès responsable — code hebdomadaire */}
              {teamCode && (
                <div data-testid="team-code-card" className="bg-[#1A1A1A] rounded-2xl p-5 text-white">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="font-head font-bold flex items-center gap-2"><KeyRound className="w-4 h-4 text-[#F26419]" /> Code d'accès responsable (Espace Équipe)</h3>
                      <p className="mt-1 text-xs text-white/60">Pour vous connecter à l'espace Équipe sans SMS (numéro étranger). Change chaque semaine · {teamCode.week}</p>
                    </div>
                    <div data-testid="team-code-value" className="font-head font-black text-3xl tracking-[0.3em] text-[#F26419] bg-white/5 rounded-xl px-4 py-2">{teamCode.code}</div>
                  </div>
                </div>
              )}

              {/* BENTSKIN MEET — réunions vidéo */}
              <div data-testid="admin-meet-card" className="bg-white rounded-2xl border-2 border-[#D94F2B]/40 p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-head font-bold flex items-center gap-2"><Video className="w-4 h-4 text-[#D94F2B]" /> BentSkin Meet — réunions vidéo</h3>
                    <p className="mt-1 text-xs text-[#75807A]">Lancez une réunion vidéo avec vos agents (caméra, micro, chat, partage d'écran).</p>
                  </div>
                  <Button data-testid="admin-launch-meeting" onClick={() => setMeetModal(true)} className="h-11 px-5 rounded-xl bg-[#F26419] hover:bg-[#d9540f] text-white font-bold"><Video className="w-4 h-4 mr-1" /> Lancer une réunion</Button>
                </div>
                <div className="mt-4 space-y-2 max-h-80 overflow-y-auto">
                  {meetings.length === 0 && <p data-testid="admin-meetings-empty" className="text-sm text-[#75807A] py-4 text-center">Aucune réunion pour le moment.</p>}
                  {meetings.map((m) => (
                    <div key={m.id} data-testid={`admin-meeting-${m.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#E2DFD8] p-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-[#1A1A1A]">{m.title}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${m.status === "scheduled" ? "bg-[#16a34a]/15 text-[#16794a]" : "bg-[#F0EBE1] text-[#75807A]"}`}>{m.status === "scheduled" ? "Active" : "Terminée"}</span>
                        </div>
                        <div className="text-xs text-[#75807A] mt-0.5">{m.invited_count} invité(s) · {m.target === "zone" ? `Zone : ${m.zone}` : "Tous les agents"} · {new Date(m.scheduled_at).toLocaleString("fr-FR")}</div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {m.status === "scheduled" && (
                          <>
                            <button data-testid={`admin-meeting-join-${m.id}`} onClick={() => setActiveMeeting(m.id)} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#1A1A1A] text-white text-xs font-semibold"><Video className="w-3.5 h-3.5" /> Démarrer</button>
                            <button data-testid={`admin-meeting-end-${m.id}`} onClick={() => endMeeting(m.id)} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-white border border-[#D32F2F] text-[#D32F2F] text-xs font-semibold">Terminer</button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-[#E2DFD8] overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-[#E2DFD8]">
                  <h3 className="font-head font-bold flex items-center gap-2"><UserPlus className="w-4 h-4 text-[#D94F2B]" /> {t("admin.teamTitle")} ({agents.length})</h3>
                  <div className="flex gap-2">
                    <Button data-testid="agents-remind-all" onClick={remindAll} className="h-10 px-4 rounded-xl bg-[#F26419] text-white text-sm"><MessageSquare className="w-4 h-4 mr-1" />Relancer tous</Button>
                    <Button data-testid="agents-export" onClick={exportCsv} className="h-10 px-4 rounded-xl bg-[#1A1A1A] text-white text-sm"><Download className="w-4 h-4 mr-1" />{t("admin.exportExcel")}</Button>
                  </div>
                </div>
                <div className="p-4 border-b border-[#E2DFD8]">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#75807A]" />
                    <Input data-testid="agent-search" value={agentSearch} onChange={(e) => setAgentSearch(e.target.value)} placeholder={t("admin.agentSearchPh")} className="pl-9 h-11 rounded-xl bg-white border-[#E2DFD8]" />
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F0EBE1] text-[#75807A] text-xs uppercase"><tr>
                      <th className="text-left p-4">{t("admin.agentCol")}</th>
                      <th className="text-left p-4">{t("admin.zoneCol")}</th>
                      <th className="text-left p-4">Statut Contrat</th>
                      <th className="text-right p-4">{t("admin.ridersMonth")}</th>
                      <th className="text-right p-4">{t("admin.ridersTotal")}</th>
                      <th className="text-right p-4">{t("admin.bonusCol")}</th>
                      <th className="text-right p-4">{t("admin.dueCol")}</th>
                      <th className="text-right p-4">Actions</th>
                    </tr></thead>
                    <tbody>
                      {agents.filter((a) => { const q = agentSearch.trim().toLowerCase(); return !q || (a.name || "").toLowerCase().includes(q) || (a.phone || "").toLowerCase().includes(q); }).map((a) => (
                        <tr key={a.id} data-testid={`agent-row-${a.id}`} className="border-t border-[#E2DFD8]">
                          <td className="p-4 font-medium"><button data-testid={`agent-name-${a.id}`} onClick={() => setDossier({ type: "agent", row: a })} className="text-left font-medium text-[#1A1A1A] hover:text-[#D94F2B] hover:underline transition-colors">{a.name || "—"}</button>{a.suspended && <span className="ml-1 inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#D32F2F]/15 text-[#D32F2F]">🚫 {t("admin.suspended")}</span>}{a.approved === false && <span data-testid={`agent-pending-${a.id}`} className="ml-1 inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#D32F2F]/15 text-[#D32F2F]">⏳ En attente d'approbation</span>}{a.locked && <span data-testid={`agent-locked-${a.id}`} className="ml-1 inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#B8860B]/15 text-[#B8860B]">🔒 {t("admin.lockedBadge")}</span>}<div className="text-xs text-[#75807A]">{a.phone || "—"} · <span className="font-semibold text-[#D94F2B]">{a.agent_code}</span>{a.access_code && <> · <span data-testid={`agent-access-code-${a.id}`} className="font-mono font-bold text-[#1A1A1A]">🔑 {a.access_code}</span> <button data-testid={`agent-code-copy-${a.id}`} onClick={() => copyText(a.access_code, "Code agent")} className="text-[#75807A] hover:text-[#D94F2B] align-middle" title="Copier le code"><Copy className="w-3 h-3 inline" /></button> <button data-testid={`agent-code-regen-${a.id}`} onClick={() => regenAgentCode(a.id)} className="text-[#75807A] hover:text-[#D94F2B] align-middle" title="Générer un nouveau code"><RefreshCw className="w-3 h-3 inline" /></button></>}</div><div className="mt-1 flex items-center gap-1"><input data-testid={`agent-email-input-${a.id}`} type="email" defaultValue={a.contact_email || ""} onChange={(e) => setAgentEmailInputs((m) => ({ ...m, [a.id]: e.target.value }))} placeholder="email de connexion" className="h-8 w-52 rounded-lg border border-[#E2DFD8] px-2 text-xs" /><button data-testid={`agent-email-save-${a.id}`} onClick={() => saveAgentEmail(a.id)} className="h-8 px-2 rounded-lg bg-[#1A1A1A] text-white text-[11px] font-semibold">OK</button>{!a.contact_email && <span data-testid={`agent-email-missing-${a.id}`} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#B8860B]/15 text-[#B8860B]">email manquant</span>}</div>{(() => { const ll = a.last_login_at; const days = ll ? Math.floor((Date.now() - new Date(ll).getTime()) / 86400000) : null; const active = ll && days <= 14; return (<div className="mt-1 flex items-center gap-1.5" data-testid={`agent-login-status-${a.id}`}><span className={`inline-block w-2 h-2 rounded-full ${active ? "bg-[#16a34a]" : "bg-[#E0912F]"}`} /><span className="text-[11px] text-[#75807A]">{ll ? (active ? `Actif · vu il y a ${days <= 0 ? "aujourd'hui" : days + " j"}` : `Dormant · vu il y a ${days} j`) : "Jamais connecté"}</span></div>); })()}</td>
                          <td className="p-4">{a.zone || "—"}</td>
                          <td className="p-4"><span data-testid={`contract-badge-${a.id}`} className={`text-xs px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${a.contract_status === "signed" ? "bg-[#16a34a]/15 text-[#16794a]" : a.contract_status === "blocked" ? "bg-[#D32F2F]/15 text-[#D32F2F]" : "bg-[#F26419]/25 text-[#B8860B]"}`}>{a.contract_status === "signed" ? "✅ Signé" : a.contract_status === "blocked" ? "🔒 Bloqué" : "⏳ En attente"}</span></td>
                          <td className="p-4 text-right font-bold">{a.recruited_month}</td>
                          <td className="p-4 text-right font-bold">{a.recruited_total}</td>
                          <td className="p-4 text-right">{fcfa(a.bonus_total)}</td>
                          <td className="p-4 text-right" data-testid={`agent-due-${a.id}`}>{a.over_threshold ? <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-[#D32F2F]/15 text-[#D32F2F]">⚠️ {fcfa(a.due)}</span> : <span className="font-semibold">{fcfa(a.due ?? 0)}</span>}</td>
                          <td className="p-4 text-right">
                            <div className="flex gap-2 justify-end">
                              {a.approved === false && (
                                <Button data-testid={`agent-approve-${a.id}`} onClick={() => approveAgent(a.id, a.name)} className="h-9 px-3 rounded-lg bg-[#16a34a] text-white text-xs font-bold"><Check className="w-3 h-3 mr-1" />Approuver</Button>
                              )}
                              <Button data-testid={`agent-view-${a.id}`} onClick={() => viewRecruits(a.id)} className="h-9 px-3 rounded-lg bg-[#1A1A1A] text-white text-xs"><Eye className="w-3 h-3 mr-1" />{t("admin.viewList")}</Button>
                              <Button data-testid={`agent-badge-${a.id}`} onClick={() => printAgentBadge(a)} className="h-9 px-3 rounded-lg bg-white border border-[#1A1A1A] text-[#1A1A1A] text-xs"><QrCode className="w-3 h-3 mr-1" />Badge</Button>
                              <Button data-testid={`agent-payout-${a.id}`} onClick={() => openPayout(a)} className="h-9 px-3 rounded-lg bg-[#16a34a] text-white text-xs"><Wallet className="w-3 h-3 mr-1" />{t("admin.prime")}</Button>
                              {a.contact_email && !a.last_login_at && (
                                <Button data-testid={`agent-remind-login-${a.id}`} onClick={() => remindLogin(a.id)} className="h-9 px-3 rounded-lg bg-white border border-[#F26419] text-[#D94F2B] text-xs"><MessageSquare className="w-3 h-3 mr-1" />Relancer connexion</Button>
                              )}
                              {a.locked && (
                                <Button data-testid={`agent-unlock-${a.id}`} onClick={() => unlockAgent(a.id)} className="h-9 px-3 rounded-lg bg-[#B8860B] text-white text-xs"><Unlock className="w-3 h-3 mr-1" />{t("admin.unlock")}</Button>
                              )}
                              {a.suspended ? (
                                <Button data-testid={`agent-unsuspend-${a.id}`} onClick={() => openMod("unsuspend", "agent", a)} className="h-9 px-3 rounded-lg bg-white border border-[#16a34a] text-[#16a34a] text-xs"><Check className="w-3 h-3 mr-1" />{t("admin.unsuspend")}</Button>
                              ) : (
                                <Button data-testid={`agent-suspend-${a.id}`} onClick={() => openMod("suspend", "agent", a)} className="h-9 px-3 rounded-lg bg-white border border-[#B8860B] text-[#B8860B] text-xs"><Ban className="w-3 h-3 mr-1" />{t("admin.suspend")}</Button>
                              )}
                              <Button data-testid={`dossier-agent-${a.id}`} onClick={() => setDossier({ type: "agent", row: a })} className="h-9 px-3 rounded-lg bg-white border border-[#D94F2B] text-[#D94F2B] text-xs"><FolderOpen className="w-3 h-3 mr-1" />Dossier</Button>
                              <Button data-testid={`agent-remove-${a.id}`} onClick={() => openMod("delete", "agent", a)} className="h-9 px-3 rounded-lg bg-[#D32F2F] text-white text-xs"><Trash2 className="w-3 h-3 mr-1" />{t("admin.deleteProfile")}</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {agents.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-[#75807A]">{t("admin.noAgents")}</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {payoutModal && (
            <div data-testid="payout-modal" onClick={() => setPayoutModal(null)} className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
              <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md max-h-[85vh] overflow-y-auto p-6 relative">
                <button data-testid="payout-close" onClick={() => setPayoutModal(null)} className="absolute top-4 right-4 w-9 h-9 rounded-xl hover:bg-[#F0EBE1] flex items-center justify-center"><X className="w-5 h-5 text-[#75807A]" /></button>
                <h3 className="font-head font-bold text-xl text-[#1A1A1A]">{t("admin.primeTitle")}</h3>
                <div className="mt-1 text-sm text-[#75807A]">{payoutModal.agent?.name || payoutModal.agent?.agent_code}</div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-xl bg-[#FDF8F0] p-3"><div className="font-head font-black">{fcfa(payoutModal.bonus_total)}</div><div className="text-[11px] text-[#75807A]">{t("admin.bonusTotal")}</div></div>
                  <div className="rounded-xl bg-[#16a34a]/10 p-3"><div className="font-head font-black text-[#16a34a]">{fcfa(payoutModal.total_paid)}</div><div className="text-[11px] text-[#75807A]">{t("admin.paidTotal")}</div></div>
                  <div className="rounded-xl bg-[#F26419]/10 p-3"><div className="font-head font-black text-[#D94F2B]" data-testid="payout-due">{fcfa(payoutModal.due)}</div><div className="text-[11px] text-[#75807A]">{t("admin.due")}</div></div>
                </div>
                <div data-testid="payout-pay-via" className="mt-4 rounded-2xl border-2 border-[#16a34a]/30 bg-[#16a34a]/5 p-4">
                  <label className="text-xs font-bold uppercase tracking-wide text-[#16a34a]">{t("admin.payVia")}</label>
                  <div className="mt-2 flex gap-2">
                    <input data-testid="payout-number-input" value={payoutNumInput} onChange={(e) => setPayoutNumInput(e.target.value)} placeholder={t("admin.agentPayNumber")} className="flex-1 h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm font-semibold" />
                    <Button data-testid="payout-number-save" onClick={savePayoutNumber} className="h-11 px-4 rounded-xl bg-[#16a34a] text-white text-sm font-bold">{t("admin.saveNumber")}</Button>
                  </div>
                  {(payoutModal.om_asso || payoutModal.momo_pay) && (
                    <div className="mt-3">
                      <div className="text-[11px] font-semibold text-[#75807A]">{t("admin.payFromLabel")} :</div>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {payoutModal.om_asso && <span data-testid="pay-from-om" className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-[#F26419]/15 text-[#D94F2B]">OM Asso · {payoutModal.om_asso}</span>}
                        {payoutModal.momo_pay && <span data-testid="pay-from-momo" className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-[#F26419]/25 text-[#B8860B]">MoMo Pay · {payoutModal.momo_pay}</span>}
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-4 space-y-2">
                  <input data-testid="payout-amount" type="number" value={payoutForm.amount} onChange={(e) => setPayoutForm({ ...payoutForm, amount: e.target.value })} placeholder={t("admin.payoutAmount")} className="w-full h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
                  <input data-testid="payout-note" value={payoutForm.note} onChange={(e) => setPayoutForm({ ...payoutForm, note: e.target.value })} placeholder={t("admin.payoutNote")} className="w-full h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
                  <Button data-testid="payout-record" onClick={recordPayout} className="w-full h-11 rounded-xl bg-[#16a34a] text-white font-bold">{t("admin.payoutRecord")}</Button>
                  <Button data-testid="payout-remind" onClick={remindPayout} className="w-full h-11 rounded-xl bg-[#25D366] hover:bg-[#1da851] text-white font-bold"><span className="mr-1">✆</span>{t("admin.remindPayout")}</Button>
                </div>
                <div className="mt-4 space-y-2">
                  {(payoutModal.payouts || []).length === 0 && <p className="text-sm text-[#75807A] text-center py-4">{t("admin.payoutEmpty")}</p>}
                  {(payoutModal.payouts || []).map((p) => (
                    <div key={p.id} className="flex items-center justify-between border-b border-[#F0EBE1] pb-2">
                      <div><div className="text-sm font-semibold">{fcfa(p.amount)}</div><div className="text-[11px] text-[#75807A]">{p.note || "—"}</div></div>
                      <div className="text-right">
                        <div className="text-[11px] text-[#75807A]">{new Date(p.at).toLocaleDateString("fr-FR")}</div>
                        <button data-testid={`payout-pdf-${p.id}`} onClick={() => downloadPayoutPdf(p.id)} className="mt-1 text-[11px] font-semibold text-[#1A1A1A] underline hover:text-[#D94F2B]">{t("admin.primePdf")}</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}


          {recruitsModal && (
            <div data-testid="recruits-modal" onClick={() => setRecruitsModal(null)} className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
              <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6 relative">
                <button data-testid="recruits-close" onClick={() => setRecruitsModal(null)} className="absolute top-4 right-4 w-9 h-9 rounded-xl hover:bg-[#F0EBE1] flex items-center justify-center"><X className="w-5 h-5 text-[#75807A]" /></button>
                <h3 className="font-head font-bold text-xl text-[#1A1A1A]">{t("admin.recruitsOf")} {recruitsModal.agent?.name || recruitsModal.agent?.agent_code}</h3>
                <div className="mt-1 text-sm text-[#75807A]">{recruitsModal.agent?.zone} · {recruitsModal.recruits?.length || 0}</div>
                <div className="mt-4 space-y-2">
                  {(recruitsModal.recruits || []).map((d) => (
                    <div key={d.id} className="flex items-center justify-between border-b border-[#F0EBE1] pb-2">
                      <div>
                        <div className="font-medium text-sm">{d.name || "—"}</div>
                        <div className="text-xs text-[#75807A]">{d.phone} · {t("admin.recruitedOn")} {new Date(d.agent_recruited_at || d.created_at).toLocaleDateString("fr-FR")}</div>
                      </div>
                      <span className="text-xs font-semibold px-2 py-1 rounded-full bg-[#F0EBE1] capitalize">{d.kyc_status || "—"}</span>
                    </div>
                  ))}
                  {(recruitsModal.recruits || []).length === 0 && <p className="text-sm text-[#75807A] text-center py-6">{t("admin.noRecruits")}</p>}
                </div>
              </div>
            </div>
          )}

          {dossier && (() => {
            const r = dossier.row; const isDrv = dossier.type === "driver";
            const wa = `https://wa.me/${(r.phone || "").replace(/[^0-9]/g, "")}`;
            const F = ({ k, v }) => (<div><div className="text-[10px] uppercase tracking-wide text-[#75807A]">{k}</div><div className="font-semibold text-[#1A1A1A] text-sm break-words">{(v === undefined || v === null || v === "") ? "—" : String(v)}</div></div>);
            return (
            <div data-testid="dossier-modal" onClick={() => setDossier(null)} className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
              <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6">
                <div className="flex items-center gap-3">
                  {(r.photo || r.driver_photo) ? <img src={r.photo || r.driver_photo} alt="" className="w-16 h-16 rounded-2xl object-cover" /> : <span className="w-16 h-16 rounded-2xl bg-[#F26419] text-white flex items-center justify-center font-head font-black text-2xl">{(r.name || "?")[0].toUpperCase()}</span>}
                  <div className="min-w-0">
                    <h3 className="font-head font-black text-xl text-[#1A1A1A] truncate">{r.name || "—"}</h3>
                    <div className="text-sm text-[#75807A]">{isDrv ? t("hero.driver") : "Agent"} · {r.phone}</div>
                    {r.email && <div data-testid="dossier-email" className="text-xs text-[#75807A] break-all">{r.email}</div>}
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-4">
                  {isDrv ? (<>
                    <F k="Type véhicule" v={r.vehicle_type || r.kyc?.vehicle_type || "moto"} />
                    <F k="Statut KYC" v={r.kyc_status} />
                    <F k="Véhicule" v={r.kyc?.vehicle_model || r.vehicle_model} />
                    <F k="Plaque" v={r.kyc?.vehicle_plate || r.plate} />
                    <F k="Note (avis)" v={r.rating} />
                    <F k="Abonnement" v={r.subscription_expires_at ? new Date(r.subscription_expires_at).toLocaleDateString() : "—"} />
                    <F k="Suspendu" v={r.suspended ? "Oui" : "Non"} />
                    <F k="Alerte IA" v={r.kyc?.ai_suspicious ? "⚠️ Oui" : "Non"} />
                  </>) : (<>
                    <F k="Code agent" v={r.agent_code} />
                    <F k="Zone" v={r.zone} />
                    <F k="Recrutés (mois)" v={r.recruited_month} />
                    <F k="Recrutés (total)" v={r.recruited_total} />
                    <F k="Prime cumulée" v={fcfa(r.bonus_total || 0)} />
                    <F k="Dû actuel" v={fcfa(r.due || 0)} />
                    <F k="PIN défini" v={r.has_pin ? "Oui" : "Non"} />
                    <F k="Suspendu / Bloqué" v={`${r.suspended ? "Suspendu" : "Actif"}${r.locked ? " · 🔒" : ""}`} />
                  </>)}
                  <F k="Créé le" v={r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"} />
                </div>
                {isDrv && r.kyc && (
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    {["selfie", "license", "id_front", "id_back"].map((key) => (r.kyc[key] ? <img key={key} src={r.kyc[key]} alt={key} className="w-full h-20 object-cover rounded-lg border border-[#E2DFD8]" /> : null))}
                  </div>
                )}
                <div className="mt-6 border-t border-[#F0EBE1] pt-4">
                  <div className="flex items-center gap-2 text-sm font-bold text-[#1A1A1A]"><FileText className="w-4 h-4 text-[#D94F2B]" /> {t("admin.notesTitle")}</div>
                  <div className="mt-3 flex gap-2 items-stretch">
                    <textarea data-testid="dossier-note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2}
                      placeholder={t("admin.notePh")}
                      className="flex-1 rounded-xl border-2 border-[#E2DFD8] px-3 py-2 text-sm resize-none focus:outline-none focus:border-[#D94F2B]" />
                    <Button data-testid="dossier-note-add" onClick={addNote} disabled={noteSaving || !noteText.trim()}
                      className="px-4 rounded-xl bg-[#F26419] text-white text-sm font-semibold">{noteSaving ? "..." : t("admin.noteAdd")}</Button>
                  </div>
                  <div className="mt-3 space-y-2 max-h-52 overflow-y-auto">
                    {notes.length === 0 && <p data-testid="dossier-note-empty" className="text-sm text-[#75807A]">{t("admin.noteEmpty")}</p>}
                    {notes.map((n) => (
                      <div key={n.id} data-testid={`dossier-note-${n.id}`} className="group rounded-xl bg-[#FDF8F0] border border-[#F0EBE1] p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-[11px] font-semibold text-[#D94F2B]">{new Date(n.created_at).toLocaleString("fr-FR")}</div>
                          <button data-testid={`dossier-note-del-${n.id}`} onClick={() => deleteNote(n.id)} className="text-[#D32F2F] opacity-60 hover:opacity-100 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                        <p className="mt-1 text-sm text-[#1A1A1A] whitespace-pre-wrap">{n.text}</p>
                        <div className="mt-1 text-[10px] text-[#75807A]">{n.author_name}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  <a data-testid="dossier-call" href={`tel:${r.phone || ""}`} className="h-10 px-4 rounded-lg bg-[#008a3a] text-white text-sm font-semibold flex items-center justify-center gap-2">Appeler</a>
                  <a data-testid="dossier-whatsapp" href={wa} target="_blank" rel="noreferrer" className="h-10 px-4 rounded-lg bg-[#25D366] text-white text-sm font-semibold flex items-center justify-center gap-2">WhatsApp</a>
                  {isDrv && r.kyc && <Button data-testid="dossier-view-docs" onClick={() => { setReviewDriver(r); setDossier(null); }} className="h-10 px-4 rounded-lg bg-[#1A1A1A] text-white text-sm">{t("admin.viewDocs")}</Button>}
                  <Button data-testid="dossier-close" onClick={() => setDossier(null)} className="h-10 px-4 rounded-lg bg-white border border-[#E2DFD8] text-[#1A1A1A] text-sm ml-auto">{t("admin.modCancel")}</Button>
                </div>
              </div>
            </div>
            );
          })()}

          {mergeSrc && (() => {
            const q = mergeQuery.trim().toLowerCase();
            const targets = drivers.filter((d) => d.id !== mergeSrc.id && (!q || [d.name, d.email, d.phone].some((v) => (v || "").toLowerCase().includes(q))));
            const sel = drivers.find((d) => d.id === mergeTargetId);
            return (
            <div data-testid="merge-modal" onClick={() => setMergeSrc(null)} className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
              <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6">
                <h3 className="font-head font-bold text-xl text-[#1A1A1A] flex items-center gap-2"><GitMerge className="w-5 h-5 text-[#6D28D9]" />Fusionner ce doublon</h3>
                <div className="mt-3 rounded-xl bg-[#FDF3F3] border border-[#F1C7C7] p-3">
                  <div className="text-[10px] uppercase tracking-wide text-[#D32F2F] font-semibold">Compte supprimé (doublon)</div>
                  <div className="font-semibold text-[#1A1A1A]">{mergeSrc.name || "—"}</div>
                  <div className="text-xs text-[#75807A] break-all">{mergeSrc.email || mergeSrc.phone}</div>
                  <div className="text-xs text-[#75807A] mt-1">Portefeuille à transférer : <b>{Math.round(mergeSrc.wallet || 0)} FCFA</b></div>
                </div>
                <div className="mt-4 text-sm font-semibold text-[#1A1A1A]">Compte à conserver (le vrai)</div>
                <div className="relative mt-2">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#75807A]" />
                  <Input data-testid="merge-target-search" value={mergeQuery} onChange={(e) => setMergeQuery(e.target.value)} placeholder="Rechercher par nom, email ou téléphone" className="pl-9 h-11 rounded-xl bg-white border-[#E2DFD8]" />
                </div>
                <div className="mt-2 space-y-1 max-h-56 overflow-y-auto">
                  {targets.map((d) => (
                    <button key={d.id} data-testid={`merge-target-${d.id}`} onClick={() => setMergeTargetId(d.id)} className={`w-full text-left rounded-xl border p-3 transition-colors ${mergeTargetId === d.id ? "border-[#6D28D9] bg-[#6D28D9]/5" : "border-[#E2DFD8] hover:border-[#6D28D9]/50"}`}>
                      <div className="font-semibold text-sm text-[#1A1A1A]">{d.name || "—"}</div>
                      <div className="text-xs text-[#75807A] break-all">{d.email || d.phone}</div>
                    </button>
                  ))}
                  {targets.length === 0 && <p className="text-sm text-[#75807A] p-2">Aucun autre chauffeur trouvé.</p>}
                </div>
                <p className="mt-3 text-xs text-[#75807A]">Le portefeuille, le crédit et tout l'historique (courses, transactions, documents) du doublon seront transférés vers le compte sélectionné, puis le doublon sera supprimé.</p>
                <div className="mt-5 flex gap-3 justify-end">
                  <Button data-testid="merge-cancel" onClick={() => setMergeSrc(null)} className="h-11 px-5 rounded-xl bg-white border border-[#E2DFD8] text-[#1A1A1A]">Annuler</Button>
                  <Button data-testid="merge-confirm" disabled={!mergeTargetId || mergeBusy} onClick={doMerge} className="h-11 px-5 rounded-xl bg-[#6D28D9] text-white font-bold disabled:opacity-40">{mergeBusy ? "Fusion…" : `Fusionner vers ${sel ? (sel.name || "ce compte") : "…"}`}</Button>
                </div>
              </div>
            </div>
            );
          })()}

          {modAction && (
            <div data-testid="mod-modal" onClick={() => setModAction(null)} className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
              <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md p-6">
                <h3 className="font-head font-bold text-xl text-[#1A1A1A]">
                  {modAction.type === "delete" ? t("admin.modDeleteTitle") : modAction.type === "suspend" ? t("admin.modSuspendTitle") : t("admin.modUnsuspendTitle")}
                </h3>
                <p className="mt-1 text-sm text-[#75807A]">{modAction.name}</p>
                {modAction.type === "delete" && <p className="mt-2 text-sm text-[#D32F2F]">{t("admin.modDeleteWarn")}</p>}
                {modAction.type !== "unsuspend" && (
                  <div className="mt-4">
                    <label className="text-xs text-[#75807A]">{t("admin.modReason")}</label>
                    <textarea data-testid="mod-reason" value={modAction.reason} onChange={(e) => setModAction({ ...modAction, reason: e.target.value })} rows={3} className="mt-1 w-full rounded-xl border-2 border-[#E2DFD8] p-3 text-sm focus:outline-none focus:border-[#D94F2B]" />
                  </div>
                )}
                <div className="mt-5 flex gap-3 justify-end">
                  <Button data-testid="mod-cancel" onClick={() => setModAction(null)} className="h-11 px-5 rounded-xl bg-white border border-[#E2DFD8] text-[#1A1A1A]">{t("admin.modCancel")}</Button>
                  <Button data-testid="mod-confirm" onClick={confirmMod} className={`h-11 px-5 rounded-xl text-white font-bold ${modAction.type === "delete" ? "bg-[#D32F2F]" : modAction.type === "unsuspend" ? "bg-[#16a34a]" : "bg-[#F26419]"}`}>{t("admin.modConfirm")}</Button>
                </div>
              </div>
            </div>
          )}

          {section === "agentactivity" && (
            <div data-testid="agent-activity-section" className="space-y-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="font-head font-black text-2xl text-[#1A1A1A]">Activité des agents</h2>
                  <p className="mt-1 text-sm text-[#75807A]">Qui travaille vraiment : nombre de connexions, heure de la dernière connexion et dernier lieu (GPS). Cliquez sur « Historique » pour voir le détail.</p>
                </div>
                <Button data-testid="export-agent-activity-csv" onClick={exportAgentActivity} className="h-10 px-4 rounded-xl bg-[#1A1A1A] hover:bg-black text-white text-sm font-bold shrink-0"><Download className="w-4 h-4 mr-1.5" /> Exporter CSV</Button>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: "Agents", val: agentActivity.summary?.total ?? 0, c: "#1A1A1A" },
                  { label: "Connectés aujourd'hui", val: agentActivity.summary?.connected_today ?? 0, c: "#16a34a" },
                  { label: "Connectés (7j)", val: agentActivity.summary?.connected_week ?? 0, c: "#2563EB" },
                  { label: `Inactifs (${agentActivity.summary?.inactivity_days ?? 7}j+)`, val: agentActivity.summary?.inactive ?? 0, c: "#D32F2F" },
                ].map((k) => (
                  <div key={k.label} data-testid={`agent-summary-${k.label}`} className="rounded-2xl bg-white border border-[#E2DFD8] p-4">
                    <div className="text-xs font-semibold text-[#75807A] uppercase tracking-wide">{k.label}</div>
                    <div className="mt-1 font-head font-black text-3xl" style={{ color: k.c }}>{k.val}</div>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-2xl border border-[#E2DFD8] p-5" data-testid="agent-daily-card">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-head font-black text-lg text-[#1A1A1A]">Compteur journalier</h3>
                    <p className="text-xs text-[#75807A]">Ce que chaque agent a fait le jour choisi — pour payer au juste. Gain = chauffeurs recrutés × prime ({fcfa(dailyData.bonus || 0)}/recrue).</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input data-testid="agent-daily-date" type="date" value={dailyDate} onChange={(e) => { setDailyDate(e.target.value); loadDaily(e.target.value); }} className="h-10 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm font-semibold" />
                    <Button data-testid="export-agent-daily-csv" onClick={exportAgentDaily} className="h-10 px-4 rounded-xl bg-[#1A1A1A] text-white text-sm font-bold"><Download className="w-4 h-4 mr-1.5" /> CSV du jour</Button>
                  </div>
                </div>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead className="bg-[#F0EBE1] text-[#75807A] text-xs uppercase"><tr>
                      <th className="text-left p-3">Agent</th>
                      <th className="text-center p-3">Connexions</th>
                      <th className="text-center p-3">Chauffeurs recrutés</th>
                      <th className="text-center p-3">Passagers amenés</th>
                      <th className="text-right p-3">Gains</th>
                    </tr></thead>
                    <tbody>
                      {(dailyData.agents || []).map((a) => (
                        <tr key={a.id} data-testid={`agent-daily-row-${a.id}`} className="border-t border-[#E2DFD8]">
                          <td className="p-3"><div className="font-semibold text-[#1A1A1A]">{a.name}</div><div className="text-xs text-[#75807A]">{a.agent_code || "—"}</div></td>
                          <td className="p-3 text-center font-bold">{a.connexions}</td>
                          <td className="p-3 text-center font-bold text-[#16a34a]">{a.recruited}</td>
                          <td className="p-3 text-center font-bold text-[#2563EB]">{a.passengers}</td>
                          <td className="p-3 text-right font-head font-black" data-testid={`agent-daily-gain-${a.id}`}>{fcfa(a.gain)}</td>
                        </tr>
                      ))}
                      {(dailyData.agents || []).length === 0 && <tr><td colSpan="5" className="p-6 text-center text-[#75807A]">Aucun agent.</td></tr>}
                    </tbody>
                    {dailyData.totals && (
                      <tfoot><tr className="border-t-2 border-[#1A1A1A] font-bold bg-[#FDF8F0]" data-testid="agent-daily-totals">
                        <td className="p-3">TOTAL du jour</td>
                        <td className="p-3 text-center">{dailyData.totals.connexions || 0}</td>
                        <td className="p-3 text-center text-[#16a34a]">{dailyData.totals.recruited || 0}</td>
                        <td className="p-3 text-center text-[#2563EB]">{dailyData.totals.passengers || 0}</td>
                        <td className="p-3 text-right font-head font-black">{fcfa(dailyData.totals.gain || 0)}</td>
                      </tr></tfoot>
                    )}
                  </table>
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-[#E2DFD8] overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead className="bg-[#F0EBE1] text-[#75807A] text-xs uppercase"><tr>
                    <th className="text-left p-4">Agent</th>
                    <th className="text-left p-4">Connexions</th>
                    <th className="text-left p-4">Dernière connexion</th>
                    <th className="text-left p-4">Dernier lieu</th>
                    <th className="text-right p-4"></th>
                  </tr></thead>
                  <tbody>
                    {(agentActivity.agents || []).map((a) => (
                      <Fragment key={a.id}>
                      <tr data-testid={`agent-activity-row-${a.id}`} className="border-t border-[#E2DFD8]">
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-[#1A1A1A]">{a.name}</span>
                            {a.online && <span data-testid={`agent-online-${a.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#16a34a]/15 text-[#16a34a]"><span className="w-1.5 h-1.5 rounded-full bg-[#16a34a] animate-pulse" /> En ligne</span>}
                            {!a.online && a.days_inactive == null && <span data-testid={`agent-inactive-${a.id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#D32F2F]/10 text-[#D32F2F]">Jamais connecté</span>}
                            {!a.online && a.days_inactive != null && a.days_inactive >= (agentActivity.summary?.inactivity_days ?? 7) && <span data-testid={`agent-inactive-${a.id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#D32F2F]/10 text-[#D32F2F]">Inactif {a.days_inactive}j</span>}
                          </div>
                          <div className="text-xs text-[#75807A]">{a.agent_code || "—"}{a.phone ? ` · ${a.phone}` : ""}</div>
                        </td>
                        <td className="p-4">
                          <div className="font-head font-black text-lg text-[#1A1A1A]" data-testid={`agent-logins-total-${a.id}`}>{a.logins_total}</div>
                          <div className="text-xs text-[#75807A]">Aujourd'hui : <b className="text-[#16a34a]">{a.logins_today}</b> · 7j : <b>{a.logins_week}</b></div>
                        </td>
                        <td className="p-4 text-xs text-[#3B423E]" data-testid={`agent-last-login-${a.id}`}>{a.last_login_at ? new Date(a.last_login_at).toLocaleString("fr-FR") : "Jamais connecté"}</td>
                        <td className="p-4 text-xs">
                          {a.last_lat != null && a.last_lng != null ? (
                            <a data-testid={`agent-map-${a.id}`} href={`https://www.google.com/maps?q=${a.last_lat},${a.last_lng}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#2563EB] font-semibold hover:underline"><MapPin className="w-3.5 h-3.5" /> Voir sur la carte</a>
                          ) : <span className="text-[#75807A]">Position inconnue</span>}
                          {a.location_updated_at && <div className="text-[#75807A] mt-0.5">{new Date(a.location_updated_at).toLocaleString("fr-FR")}{a.location_accuracy ? ` · ±${Math.round(a.location_accuracy)}m` : ""}</div>}
                        </td>
                        <td className="p-4 text-right">
                          {(a.recent || []).length > 0 && (
                            <button data-testid={`agent-history-toggle-${a.id}`} onClick={() => setExpandedAgent(expandedAgent === a.id ? null : a.id)} className="text-xs font-bold text-[#D94F2B] hover:underline">{expandedAgent === a.id ? "Masquer" : "Historique"}</button>
                          )}
                        </td>
                      </tr>
                      {expandedAgent === a.id && (
                        <tr data-testid={`agent-history-${a.id}`} className="bg-[#FDF8F0]">
                          <td colSpan="5" className="p-4">
                            <div className="text-xs font-bold text-[#75807A] uppercase mb-2">15 dernières connexions</div>
                            <div className="space-y-1.5">
                              {(a.recent || []).map((r, i) => (
                                <div key={i} className="flex items-center gap-3 text-xs flex-wrap">
                                  <Clock className="w-3.5 h-3.5 text-[#75807A]" />
                                  <span className="text-[#1A1A1A] font-medium">{new Date(r.at).toLocaleString("fr-FR")}</span>
                                  <span className="px-1.5 py-0.5 rounded-full bg-[#F2EEE7] text-[#75807A]">{r.method === "code" ? "Code" : r.method === "pin" ? "PIN" : r.method === "email" ? "Email" : r.method}</span>
                                  {r.lat != null && r.lng != null ? (
                                    <a href={`https://www.google.com/maps?q=${r.lat},${r.lng}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#2563EB] hover:underline"><MapPin className="w-3 h-3" /> lieu</a>
                                  ) : <span className="text-[#75807A]">sans position</span>}
                                  {r.ip && <span className="text-[#B0B0B0]">{r.ip}</span>}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    ))}
                    {(agentActivity.agents || []).length === 0 && <tr><td colSpan="5" className="p-8 text-center text-[#75807A]">Aucun agent pour l'instant.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {section === "log" && (
            <div className="bg-white rounded-2xl border border-[#E2DFD8] overflow-hidden">
              <div className="p-4 border-b border-[#E2DFD8] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <h3 className="font-head font-bold flex items-center gap-2"><FileText className="w-4 h-4 text-[#D94F2B]" /> {t("admin.logTitle")} ({moderationLog.length})</h3>
                <div className="flex gap-2">
                  <select data-testid="log-filter-action" value={logAction} onChange={(e) => { setLogAction(e.target.value); loadLog(e.target.value, logRole); }} className="h-9 rounded-lg border border-[#E2DFD8] px-2 text-sm bg-white">
                    <option value="">{t("admin.logFilterAction")}: {t("admin.logAll")}</option>
                    <option value="suspend">{t("admin.logSuspend")}</option>
                    <option value="unsuspend">{t("admin.logUnsuspend")}</option>
                    <option value="delete">{t("admin.logDelete")}</option>
                  </select>
                  <select data-testid="log-filter-role" value={logRole} onChange={(e) => { setLogRole(e.target.value); loadLog(logAction, e.target.value); }} className="h-9 rounded-lg border border-[#E2DFD8] px-2 text-sm bg-white">
                    <option value="">{t("admin.logFilterRole")}: {t("admin.logAll")}</option>
                    <option value="driver">{t("admin.drivers")}</option>
                    <option value="agent">{t("admin.team")}</option>
                  </select>
                  <Button data-testid="log-export" onClick={exportLog} className="h-9 px-4 rounded-lg bg-[#1A1A1A] text-white text-sm"><Download className="w-4 h-4 mr-1" />{t("admin.exportExcel")}</Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F0EBE1] text-[#75807A] text-xs uppercase"><tr>
                    <th className="text-left p-4">{t("admin.logDate")}</th>
                    <th className="text-left p-4">{t("admin.logAction")}</th>
                    <th className="text-left p-4">{t("admin.logTarget")}</th>
                    <th className="text-left p-4">{t("admin.logMotif")}</th>
                  </tr></thead>
                  <tbody>
                    {moderationLog.map((l) => {
                      const color = l.action === "delete" ? "bg-[#D32F2F]/15 text-[#D32F2F]" : l.action === "unsuspend" ? "bg-[#16a34a]/15 text-[#16a34a]" : "bg-[#F26419]/25 text-[#B8860B]";
                      const label = l.action === "delete" ? t("admin.logDelete") : l.action === "unsuspend" ? t("admin.logUnsuspend") : t("admin.logSuspend");
                      return (
                        <tr key={l.id} data-testid={`log-row-${l.id}`} className="border-t border-[#E2DFD8]">
                          <td className="p-4 whitespace-nowrap text-[#75807A]">{new Date(l.at).toLocaleString("fr-FR")}</td>
                          <td className="p-4"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${color}`}>{label}</span></td>
                          <td className="p-4 font-medium">{l.name || "—"}<div className="text-xs text-[#75807A]">{l.phone || l.target_id} · {l.target_role === "agent" ? t("admin.team") : t("admin.drivers")}</div></td>
                          <td className="p-4 text-[#3B423E] max-w-xs">{l.reason || "—"}</td>
                        </tr>
                      );
                    })}
                    {moderationLog.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-[#75807A]">{t("admin.logEmpty")}</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {section === "rides" && (
            <div className="bg-white rounded-2xl border border-[#E2DFD8] overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F0EBE1] text-[#75807A] text-xs uppercase"><tr>
                  <th className="text-left p-4">Trajet</th><th className="text-left p-4">Prix</th><th className="text-left p-4">Paiement</th><th className="text-left p-4">Statut</th>
                </tr></thead>
                <tbody>
                  {rides.filter(r => r.type !== "food").map((r) => (
                    <tr key={r.id} className="border-t border-[#E2DFD8]">
                      <td className="p-4">{r.pickup} → {r.destination}<div className="text-xs text-[#75807A]">{r.passenger_name}</div></td>
                      <td className="p-4 font-medium">{fcfa(r.price)}</td>
                      <td className="p-4">{(r.cash_payment || r.payment_method === "cash" || r.payment_method_final === "cash") ? <span data-testid={`admin-cash-badge-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#16a34a]/15 text-[#16a34a] px-2 py-1 text-xs font-bold">Payé en Cash</span> : t(`pay.${r.payment_method}`)}</td>
                      <td className="p-4"><span className="text-xs font-semibold px-2 py-1 rounded-full bg-[#F0EBE1]">{t(`rideStatus.${r.status}`)}</span></td>
                    </tr>
                  ))}
                  {rides.filter(r => r.type !== "food").length === 0 && <tr><td colSpan="4" className="p-8 text-center text-[#75807A]">—</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {section === "restos-manage" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-head font-black text-2xl">🍽️ Vitrines Food</h2>
                  <p className="text-sm text-[#75807A]">Gérer les brouillons et les fiches publiées sur noldgo.com</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button data-testid="restos-backfill-btn"
                    onClick={async () => {
                      if (!window.confirm("Rendre visibles tous les restos déjà approuvés mais actuellement cachés ?\n\nCette action publie et active les fiches manquantes. Elle est sûre (idempotente).")) return;
                      try {
                        const { data } = await api.post("/restaurateur/admin/backfill-publish-approved");
                        toast.success(`✓ ${data.updated} fiche(s) publiée(s) sur ${data.scanned} approuvée(s)`);
                        loadRestos(restosTab, restosQuery);
                      } catch (e) { toast.error(apiError(e)); }
                    }}
                    title="Publie les restos déjà approuvés mais cachés (backfill)"
                    className="h-10 rounded-xl bg-white border-2 border-[#16a34a] hover:bg-[#16a34a]/5 text-[#16a34a] font-bold text-sm px-4">
                    🔄 Backfill publications
                  </Button>
                  <Button data-testid="restos-geocode-btn"
                    onClick={async () => {
                      if (!window.confirm("Géocoder tous les restaurants sans coordonnées GPS ?\n\nCela permet à l'auto-assign livreur de fonctionner. Opération sûre (idempotente, ne touche pas les restos déjà géocodés).")) return;
                      try {
                        const { data } = await api.post("/admin/restaurants/backfill-geocode");
                        toast.success(`🗺️ ${data.updated} resto(s) géocodé(s) sur ${data.scanned} · échecs: ${data.geocode_failed} · sans adresse: ${data.no_address}`);
                      } catch (e) { toast.error(apiError(e)); }
                    }}
                    title="Complète pickup_lat/lng manquants pour tous les restaurants"
                    className="h-10 rounded-xl bg-white border-2 border-[#7C3AED] hover:bg-[#7C3AED]/5 text-[#7C3AED] font-bold text-sm px-4">
                    🗺️ Géocoder restos
                  </Button>
                  <Button data-testid="restos-add-btn" onClick={() => setRtCreateOpen(true)} className="h-10 rounded-xl bg-gradient-to-r from-[#7C3AED] to-[#F26419] hover:opacity-90 text-white font-bold text-sm px-4">
                    + Créer un restaurant
                  </Button>
                </div>
              </div>

              {/* Tabs Brouillons / En ligne / Tous */}
              <div className="flex items-center gap-2 flex-wrap">
                {[
                  { id: "draft", label: "📝 Brouillons", color: "bg-[#F59E0B]/15 text-[#B45309] border-[#F59E0B]/40" },
                  { id: "published", label: "🟢 En ligne", color: "bg-[#16a34a]/15 text-[#16a34a] border-[#16a34a]/40" },
                  { id: "no-photo", label: "📷 Sans photo", color: "bg-[#D32F2F]/15 text-[#D32F2F] border-[#D32F2F]/40" },
                  { id: "all", label: "Tous", color: "bg-[#F0EBE1] text-[#1A1A1A] border-[#E2DFD8]" },
                ].map((t) => (
                  <button key={t.id} data-testid={`restos-tab-${t.id}`}
                    onClick={() => setRestosTab(t.id)}
                    className={`h-10 rounded-xl border-2 px-4 text-sm font-bold transition-all ${restosTab === t.id ? `${t.color} shadow-sm` : "bg-white text-[#75807A] border-[#E2DFD8] hover:border-[#7C3AED]"}`}>
                    {t.label}
                  </button>
                ))}
                <input
                  data-testid="restos-search"
                  value={restosQuery}
                  onChange={(e) => setRestosQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") loadRestos(restosTab, restosQuery); }}
                  placeholder="Rechercher un restaurant…"
                  className="h-10 flex-1 min-w-[200px] rounded-xl border-2 border-[#E2DFD8] px-3 text-sm"
                />
              </div>

              {/* Liste */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {restosLoading ? (
                  <div className="col-span-full text-center py-8 text-[#75807A]">Chargement…</div>
                ) : restosList.items.length === 0 ? (
                  <div className="col-span-full text-center py-8 text-[#75807A] italic">
                    {restosTab === "draft" ? "Aucun brouillon pour l'instant." : restosTab === "published" ? "Aucun restaurant en ligne." : restosTab === "no-photo" ? "🎉 Tous les restaurants ont une photo !" : "Aucun restaurant."}
                  </div>
                ) : restosList.items.map((r) => (
                  <div key={r.id} data-testid={`resto-card-${r.id}`}
                    className="bg-white rounded-2xl border border-[#E2DFD8] p-4 hover:border-[#F26419] hover:shadow-md transition-all cursor-pointer"
                    onClick={() => setRtCreatedResto({ id: r.id, name: r.name, published: r.published })}>
                    <div className="flex items-start gap-3">
                      {r.logo_url ? (
                        <img src={r.logo_url} alt={r.name} className="w-14 h-14 rounded-xl object-cover border border-[#E2DFD8] flex-shrink-0" />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-[#F0EBE1] flex items-center justify-center text-2xl flex-shrink-0">🍽️</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-head font-bold text-base truncate">{r.name}</div>
                          {r.published ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-[#16a34a]/15 text-[#16a34a] px-2 py-0.5 text-[10px] font-black uppercase whitespace-nowrap">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#16a34a]" /> En ligne
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-[#F59E0B]/15 text-[#B45309] px-2 py-0.5 text-[10px] font-black uppercase whitespace-nowrap">
                              📝 Brouillon
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-[#75807A] truncate mt-0.5">{r.cuisine || "—"} · {r.city || "Bruxelles"}</div>
                        <div className="text-xs text-[#75807A] truncate">{r.address}</div>
                        {r.owner_email && (
                          <div data-testid={`resto-owner-email-${r.id}`} className="text-[11px] text-[#0369A1] font-mono truncate mt-0.5" title={r.owner_email}>
                            👤 {r.owner_email}
                          </div>
                        )}
                        <div className="text-xs text-[#F26419] font-semibold mt-1.5">🍽️ {r.n_items} plat{r.n_items > 1 ? "s" : ""}</div>
                        {!r.has_cover && (
                          <div data-testid={`resto-no-cover-${r.id}`} className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-[#D32F2F]/10 text-[#D32F2F] px-2 py-0.5 text-[10px] font-black uppercase tracking-wide">
                            📷 Photo manquante
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2 mt-2" data-testid={`resto-actions-${r.id}`}>
                          <button data-testid={`resto-edit-${r.id}`}
                            onClick={(e) => { e.stopPropagation(); setRtCreatedResto({ id: r.id, name: r.name, published: r.published }); }}
                            className="h-8 rounded-lg bg-[#F26419] hover:bg-[#D94F2B] text-white text-xs font-bold px-3 shrink-0">
                            ✏️ Modifier
                          </button>
                          <button data-testid={`resto-featured-${r.id}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await api.patch(`/restaurateur/admin/restaurant/${r.id}`, { featured: !r.featured });
                                toast.success(r.featured ? `« ${r.name} » retiré de la vedette` : `⭐ « ${r.name} » mis en vedette sur noldgo.com`);
                                loadRestos(restosTab, restosQuery);
                              } catch (err) { toast.error(apiError(err)); }
                            }}
                            title={r.featured ? "Retirer de la vedette" : "Mettre en vedette (visible sur la home noldgo.com)"}
                            className={`h-8 rounded-lg text-xs font-bold px-2 border-2 shrink-0 ${r.featured ? "bg-[#FEF3C7] border-[#F59E0B] text-[#B45309]" : "bg-white border-[#F59E0B] text-[#F59E0B] hover:bg-[#FEF3C7]"}`}>
                            {r.featured ? "⭐" : "☆"}
                          </button>
                          <select data-testid={`resto-commission-${r.id}`}
                            value={r.commission_rate_pct || 25}
                            onClick={(e) => e.stopPropagation()}
                            onChange={async (e) => {
                              e.stopPropagation();
                              const rate = Number(e.target.value);
                              try {
                                await api.patch(`/restaurateur/admin/restaurant/${r.id}`, { commission_rate_pct: rate });
                                toast.success(`Commission « ${r.name} » → ${rate} %`);
                                loadRestos(restosTab, restosQuery);
                              } catch (err) { toast.error(apiError(err)); }
                            }}
                            title="Taux de commission Nold Go"
                            className="h-8 rounded-lg bg-white border-2 border-[#16a34a] text-[#16a34a] hover:bg-[#F0FDF4] text-xs font-bold px-2 cursor-pointer shrink-0">
                            <option value={15}>15 %</option>
                            <option value={20}>20 %</option>
                            <option value={25}>25 %</option>
                            <option value={30}>30 %</option>
                          </select>
                          <button data-testid={`resto-invite-${r.id}`}
                            onClick={(e) => { e.stopPropagation(); setInviteResto({ id: r.id, name: r.name }); setInviteEmail(""); setInviteLastUrl(null); }}
                            title="Envoyer une invitation au restaurateur pour qu'il active son compte"
                            className="h-8 rounded-lg bg-white border-2 border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED]/5 text-xs font-bold px-3 shrink-0">
                            📧 Inviter
                          </button>
                          <button data-testid={`resto-test-order-${r.id}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm(`Créer une commande TEST payée + acceptée sur « ${r.name} » ?\n\nLe restaurateur pourra tester immédiatement le bouton "Commande prête".`)) return;
                              try {
                                const { data } = await api.post(`/restaurateur/admin/create-test-order/${r.id}`);
                                toast.success(`🧪 Commande test créée sur ${data.restaurant_name} — le resto peut cliquer "Commande prête"`);
                              } catch (err) { toast.error(apiError(err)); }
                            }}
                            title="Créer une commande TEST payée + acceptée pour tester le bouton 'Commande prête'"
                            className="h-8 rounded-lg bg-white border-2 border-[#16a34a] text-[#16a34a] hover:bg-[#16a34a]/5 text-xs font-bold px-3 shrink-0">
                            🧪 Test order
                          </button>
                          <button data-testid={`resto-handover-${r.id}`}
                            onClick={(e) => { e.stopPropagation(); setHandoverResto({ id: r.id, name: r.name, address: r.address || "" }); }}
                            title="Générer le PDF de remise de matériel à faire signer par le restaurateur"
                            className="h-8 rounded-lg bg-white border-2 border-[#0EA5E9] text-[#0EA5E9] hover:bg-[#0EA5E9]/5 text-xs font-bold px-3 shrink-0">
                            📦 Matériel
                          </button>
                          <button data-testid={`resto-flyer-${r.id}`}
                            onClick={(e) => { e.stopPropagation(); setFlyerResto({ id: r.id, name: r.name }); setFlyerEmail(""); }}
                            title="Prospectus PDF automne A5 (menu complet + logo + QR + photo cover)"
                            className="h-8 rounded-lg bg-white border-2 border-[#B45309] text-[#B45309] hover:bg-[#B45309]/5 text-xs font-bold px-3 shrink-0">
                            📄 Prospectus
                          </button>
                          <button data-testid={`resto-video-${r.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setVideoResto({ id: r.id, name: r.name });
                              setVideoJob(null); setVideoEmail(""); setVideoPrompt("");
                              // Sora 2 désactivé par défaut : diaporama-only génère en ~60s et ne plante
                              // jamais (Sora peut mettre 5+ min et échouer côté OpenAI).
                              setVideoUseSora(false);
                            }}
                            title="Générer un spot publicitaire vidéo 30s (diaporama menu — activez Sora 2 pour intro cinématique IA)"
                            className="h-8 rounded-lg bg-white border-2 border-[#F26419] text-[#F26419] hover:bg-[#F26419]/5 text-xs font-bold px-3 shrink-0">
                            🎬 Spot vidéo
                          </button>
                          <button data-testid={`resto-set-email-${r.id}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              const cur = r.owner_email || "";
                              const val = window.prompt(`Email du restaurateur pour ${r.name} :`, cur);
                              if (!val || val.trim() === cur) return;
                              try {
                                const { data } = await api.patch(`/restaurateur/admin/${r.id}/owner-email`, { email: val.trim() });
                                toast.success(`Email mis à jour : ${data.new_email}`);
                                loadRestos(restosTab, restosQuery);
                              } catch (err) { toast.error(apiError(err)); }
                            }}
                            title="Modifier l'email du restaurateur (celui qui reçoit les alertes imprimante, factures, etc.)"
                            className="h-8 rounded-lg bg-white border-2 border-[#0369A1] text-[#0369A1] hover:bg-[#0369A1]/5 text-xs font-bold px-3 shrink-0">
                            ✉️ Email
                          </button>
                          {r.is_demo && (
                            <button data-testid={`resto-printer-test-${r.id}`}
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const { data } = await api.post(`/restaurateur/admin/${r.id}/printer-test-alert`);
                                  toast.success(`🖨️ Alerte test envoyée à ${data.email_to}`);
                                } catch (err) { toast.error(apiError(err)); }
                              }}
                              title="Envoie un email de test « imprimante déconnectée » au restaurateur (démo uniquement)"
                              className="h-8 rounded-lg bg-white border-2 border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED]/5 text-xs font-bold px-3 shrink-0">
                              🖨️ Test alerte
                            </button>
                          )}
                          {!r.is_demo && r.owner_email && !r.owner_email.startsWith("resto-") && (
                            <button data-testid={`resto-printer-test-${r.id}`}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm(`Envoyer un email TEST d'alerte imprimante à ${r.owner_email} ?`)) return;
                                try {
                                  const { data } = await api.post(`/restaurateur/admin/${r.id}/printer-test-alert`);
                                  toast.success(`🖨️ Alerte test envoyée à ${data.email_to}`);
                                } catch (err) { toast.error(apiError(err)); }
                              }}
                              title="Envoie un email de test « imprimante déconnectée » au restaurateur (avec confirmation)"
                              className="h-8 rounded-lg bg-white border-2 border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED]/5 text-xs font-bold px-3 shrink-0">
                              🖨️ Test alerte
                            </button>
                          )}
                          <button data-testid={`resto-return-${r.id}`}
                            onClick={(e) => { e.stopPropagation(); setReturnResto({ id: r.id, name: r.name }); }}
                            title="Générer le PV de restitution matériel (fin de collaboration)"
                            className="h-8 rounded-lg bg-white border-2 border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626]/5 text-xs font-bold px-3 shrink-0">
                            📤 Reprise
                          </button>
                          {r.owner_user_id && (
                            <button data-testid={`resto-free-${r.id}`}
                              onClick={(e) => { e.stopPropagation(); setRtFreeId(r.owner_user_id); setRtFreeDays(0); setRtFreeNote(""); }}
                              title="Ajuster la durée de gratuité (À vie / 30 / 60 / 90 j)"
                              className="h-8 rounded-lg bg-white border-2 border-[#16a34a] text-[#16a34a] hover:bg-[#16a34a]/5 text-xs font-bold px-2 shrink-0">
                              💰
                            </button>
                          )}
                          {(!r.published || r.created_by_admin) && (
                            <button data-testid={`resto-delete-${r.id}`}
                              onClick={async (e) => {
                                e.stopPropagation();
                                const warn = r.published ? "\n\n⚠️ Ce restaurant est PUBLIÉ. La suppression le retirera immédiatement du site." : "";
                                const confirmName = window.prompt(`⚠️ Suppression définitive.${warn}\n\nPour confirmer la suppression de « ${r.name} » et de ses ${r.n_items} plat(s), tape le nom du restaurant :`);
                                if (!confirmName || confirmName.trim() !== r.name.trim()) {
                                  if (confirmName !== null) toast.error("Nom incorrect. Suppression annulée.");
                                  return;
                                }
                                try {
                                  await api.delete(`/restaurateur/admin/restaurant/${r.id}`);
                                  toast.success("Restaurant supprimé");
                                  loadRestos(restosTab, restosQuery);
                                } catch (err) { toast.error(apiError(err)); }
                              }}
                              className="h-8 rounded-lg bg-white border-2 border-[#D32F2F] text-[#D32F2F] hover:bg-[#D32F2F]/5 text-xs font-bold px-3 shrink-0">
                              🗑️
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {section === "foodorders" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-head font-black text-2xl">🍔 Commandes Nold Go Food</h2>
                <div className="text-sm text-[#75807A]">{foodOrders.length} commande(s)</div>
              </div>
              <div className="bg-white rounded-2xl border border-[#E2DFD8] overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F0EBE1] text-[#75807A] text-xs uppercase"><tr>
                    <th className="text-left p-4">Date</th>
                    <th className="text-left p-4">Client</th>
                    <th className="text-left p-4">Restaurant</th>
                    <th className="text-left p-4">Livraison</th>
                    <th className="text-right p-4">Total</th>
                    <th className="text-left p-4">Paiement</th>
                    <th className="text-left p-4">Statut</th>
                    <th className="text-center p-4">📷 Preuve</th>
                  </tr></thead>
                  <tbody>
                    {foodOrders.map((r) => {
                      const paid = r.payment_status === "paid";
                      const proofUrl = r.proof_photo_url
                        ? `${process.env.REACT_APP_BACKEND_URL}/api${r.proof_photo_url.startsWith("/api") ? r.proof_photo_url.slice(4) : r.proof_photo_url}`
                        : null;
                      return (
                        <tr key={r.id} data-testid={`food-order-${r.id}`} className="border-t border-[#E2DFD8]">
                          <td className="p-4 text-xs text-[#75807A]">{r.created_at ? new Date(r.created_at).toLocaleString("fr-BE", {dateStyle:"short", timeStyle:"short"}) : "—"}</td>
                          <td className="p-4">
                            <div className="font-medium">{r.customer_name || "—"}</div>
                            <div className="text-xs text-[#75807A]">{r.customer_email}</div>
                          </td>
                          <td className="p-4">{r.restaurant_name || "—"}</td>
                          <td className="p-4 text-xs">{r.delivery_address || "—"}</td>
                          <td className="p-4 text-right font-bold text-[#F26419]">{(r.total_eur || 0).toFixed(2)} €</td>
                          <td className="p-4">
                            {paid
                              ? <span className="inline-flex items-center gap-1 rounded-full bg-[#16a34a]/15 text-[#16a34a] px-2 py-1 text-xs font-bold">✓ Payée</span>
                              : <span className="inline-flex items-center gap-1 rounded-full bg-[#F26419]/15 text-[#F26419] px-2 py-1 text-xs font-bold">⏳ En attente</span>}
                          </td>
                          <td className="p-4"><span className="text-xs font-semibold px-2 py-1 rounded-full bg-[#F0EBE1]">{r.status || "—"}</span></td>
                          <td className="p-4 text-center">
                            {proofUrl ? (
                              <button
                                data-testid={`food-order-proof-${r.id}`}
                                onClick={() => setProofOrder({ id: r.id, restaurant_name: r.restaurant_name, delivered_at: r.delivered_at, url: proofUrl })}
                                className="inline-block w-12 h-12 rounded-lg overflow-hidden border-2 border-[#16a34a] hover:border-[#0f7a37] hover:scale-105 transition"
                                title="Voir la photo de preuve de livraison"
                              >
                                <img src={proofUrl} alt="Preuve" className="w-full h-full object-cover" loading="lazy" />
                              </button>
                            ) : (
                              <span className="text-xs text-[#75807A]">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {foodOrders.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-[#75807A]">Aucune commande food pour l'instant.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {section === "accounting" && (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h2 className="font-head font-black text-2xl">{t("admin.accounting")}</h2>
                <Button data-testid="ai-report-btn" onClick={generateAiReport} disabled={aiLoading} className="h-11 px-5 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold">
                  <TrendingUp className="w-4 h-4 mr-2" />{aiLoading ? t("admin.acctAnalyzing") : t("admin.acctGenerate")}
                </Button>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { k: "acctRevenue", v: acct?.total_revenue, cls: "text-[#16a34a]" },
                  { k: "acctSub", v: acct?.sub_revenue, cls: "text-[#1A1A1A]" },
                  { k: "acctCommission", v: acct?.commission_revenue, cls: "text-[#1A1A1A]" },
                  { k: "acctExpense", v: acct?.agent_expense, cls: "text-[#D32F2F]" },
                  { k: "acctNet", v: acct?.net, cls: "text-[#D94F2B]" },
                  { k: "acctGmv", v: acct?.gmv, cls: "text-[#75807A]" },
                ].map((c) => (
                  <div key={c.k} data-testid={`acct-${c.k}`} className="bg-white rounded-2xl border border-[#E2DFD8] p-4">
                    <div className="text-xs text-[#75807A]">{t(`admin.${c.k}`)}</div>
                    <div className={`mt-1 font-head font-black text-xl ${c.cls}`}>{(c.v || 0).toLocaleString("fr-FR")} <span className="text-xs font-semibold">FCFA</span></div>
                  </div>
                ))}
              </div>
              {acct?.europe && (
                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-2"><span className="text-lg">🇪🇺</span><h3 className="font-head font-black text-lg">Comptabilité Europe (EUR)</h3></div>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                    {[
                      { key: "gmv", label: "Volume courses (GMV)", v: acct.europe.gmv_eur, cls: "text-[#2563EB]", eur: true },
                      { key: "com", label: "Commissions", v: acct.europe.commission_eur, cls: "text-[#16a34a]", eur: true },
                      { key: "rides", label: "Courses payées", v: acct.europe.rides, cls: "text-[#1A1A1A]", eur: false },
                    ].map((c) => (
                      <div key={c.key} data-testid={`acct-eu-${c.key}`} className="bg-white rounded-2xl border border-[#2563EB]/30 p-4">
                        <div className="text-xs text-[#75807A]">{c.label}</div>
                        <div className={`mt-1 font-head font-black text-xl ${c.cls}`}>{(c.v || 0).toLocaleString("fr-FR")}{c.eur && <span className="text-xs font-semibold"> €</span>}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-4 text-sm text-[#75807A]">
                <span>{t("admin.acctRides")}: <b className="text-[#1A1A1A]">{acct?.rides || 0}</b></span>
                <span>{t("admin.acctActiveSubs")}: <b className="text-[#1A1A1A]">{acct?.active_subs || 0}</b></span>
                <span>{t("admin.drivers")}: <b className="text-[#1A1A1A]">{acct?.drivers || 0}</b></span>
                <span>{t("admin.agentApps")}: <b className="text-[#1A1A1A]">{acct?.agents || 0}</b></span>
              </div>
              <div className="mt-5 bg-[#1A1A1A] text-white rounded-2xl p-5">
                <div className="flex items-center gap-2 text-[#F26419] font-bold uppercase text-xs tracking-widest"><TrendingUp className="w-4 h-4" /> {t("admin.acctAiTitle")}</div>
                {aiReport ? (
                  <div data-testid="ai-report-text" className="mt-3 text-white/90 text-sm whitespace-pre-wrap leading-relaxed">{aiReport}</div>
                ) : (
                  <p className="mt-3 text-white/60 text-sm">{t("admin.acctAiEmpty")}</p>
                )}
              </div>
            </div>
          )}

          {section === "fraud" && (
            <div data-testid="fraud-section" className="bg-white rounded-2xl border border-[#E2DFD8] p-5">
              <div className="flex items-center gap-2 mb-4"><ShieldAlert className="w-5 h-5 text-[#D32F2F]" /><h3 className="font-head font-bold text-lg">Alertes fraude — Selfie ↔ CNI suspects</h3></div>
              {!fraud ? (
                <p className="text-sm text-[#75807A]">Chargement…</p>
              ) : (fraud.items || []).length === 0 ? (
                <p data-testid="fraud-empty" className="text-sm text-[#75807A] py-10 text-center">Aucune alerte de fraude en attente. 🎉</p>
              ) : (
                <div className="space-y-3">
                  {fraud.items.map((f) => (
                    <div key={f.id} data-testid={`fraud-item-${f.id}`} className="flex items-center gap-4 rounded-2xl border border-[#E2DFD8] p-3">
                      <img src={f.selfie} alt={f.name} className="w-14 h-14 rounded-xl object-cover bg-[#F0EBE1] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-[#1A1A1A] truncate">{f.name || "—"}</div>
                        <div className="text-xs text-[#D32F2F] font-semibold">Correspondance IA {f.confidence != null ? `${Math.round(f.confidence * 100)}%` : "faible"}</div>
                        {f.reason && <div className="text-xs text-[#75807A] truncate">{f.reason}</div>}
                        <div className="text-[10px] text-[#B7B2A8]">{(f.created_at || "").slice(0, 16).replace("T", " ")}</div>
                      </div>
                      <button data-testid={`fraud-view-cni-${f.id}`} onClick={() => openImage(`/admin/passenger-id-card/${f.user_id}`)} className="text-xs font-semibold text-[#2563EB] underline shrink-0">Voir CNI</button>
                      <button data-testid={`fraud-resolve-${f.id}`} onClick={async () => { try { await api.post(`/admin/fraud-alerts/${f.id}/resolve`); toast.success("Alerte marquée résolue ✅"); const r = await api.get("/admin/fraud-alerts"); setFraud(r.data); } catch (e) { toast.error(apiError(e)); } }} className="h-9 px-3 rounded-lg bg-[#16a34a] text-white text-xs font-bold shrink-0 hover:bg-[#15803d]">Marquer résolu</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {section === "selfies" && (
            <div data-testid="admin-selfies" className="space-y-4">
              <h2 className="font-head font-black text-xl text-[#1A1A1A]">Selfies passagers {selfies?.count ? `(${selfies.count})` : ""}</h2>
              <div className="flex gap-2 max-w-sm">
                <input data-testid="selfie-search" value={selfieQuery} onChange={(e) => setSelfieQuery(e.target.value)} placeholder="Rechercher par nom…" className="flex-1 h-11 rounded-xl border-2 border-[#E2DFD8] px-4 text-sm" />
                <Button data-testid="selfie-search-btn" onClick={() => api.get(`/admin/passenger-selfies${selfieQuery ? `?q=${encodeURIComponent(selfieQuery)}` : ""}`).then((r) => setSelfies(r.data)).catch(() => {})} className="h-11 px-4 rounded-xl bg-[#1A1A1A] text-white text-sm font-semibold">Chercher</Button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {(selfies?.items || []).map((u) => (
                  <div key={u.id} data-testid={`selfie-card-${u.id}`} className="bg-white rounded-2xl border border-[#E2DFD8] overflow-hidden">
                    <img src={`${API}/profile-photos/${u.id}`} alt={u.name} onError={(e) => { e.currentTarget.style.opacity = 0.2; }} className="w-full h-40 object-cover bg-[#F0EBE1]" />
                    <div className="p-3">
                      <div className="font-semibold text-sm text-[#1A1A1A] truncate">{u.name || "—"}</div>
                      <div className="text-xs text-[#75807A]">{u.phone || ""}</div>
                      {u.city && <div className="text-xs text-[#75807A]">{u.city}</div>}
                      {u.id_review && !u.id_review.error && (
                        <div data-testid={`selfie-idmatch-${u.id}`} className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${u.id_suspicious ? "bg-[#D32F2F]/15 text-[#D32F2F]" : "bg-[#16a34a]/15 text-[#16a34a]"}`}>
                          {u.id_suspicious ? "⚠️ Selfie ↔ CNI suspect" : "✓ Selfie ↔ CNI"}{u.id_review.confidence != null ? ` · ${Math.round(u.id_review.confidence * 100)}%` : ""}
                        </div>
                      )}
                      {u.has_id_card && (
                        <button data-testid={`selfie-idcard-${u.id}`} onClick={() => openImage(`/admin/passenger-id-card/${u.id}`)} className="mt-1 block text-xs font-semibold text-[#2563EB] underline">Voir la pièce d'identité</button>
                      )}
                    </div>
                  </div>
                ))}
                {(!selfies?.items || selfies.items.length === 0) && (
                  <div className="col-span-full p-8 text-center text-[#75807A]">Aucun selfie passager pour le moment.</div>
                )}
              </div>
            </div>
          )}

          {section === "recharges" && (
            <div data-testid="admin-recharges" className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="font-head font-black text-xl text-[#1A1A1A]">Recharges portefeuille</h2>
                {recharges?.pending_count > 0 && (
                  <span className="px-3 py-1.5 rounded-xl bg-[#D32F2F]/15 text-[#D32F2F] font-semibold text-sm">{recharges.pending_count} en attente · {(recharges.pending_total || 0).toLocaleString("fr-FR")} FCFA</span>
                )}
                <button data-testid="recharge-export-csv" onClick={() => downloadFile("/admin/wallet-recharges/export.csv", "recharges.csv")} className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1A1A1A] text-white text-sm font-semibold hover:opacity-90 transition-opacity">
                  <Download className="w-4 h-4" /> Export CSV
                </button>
              </div>
              <p className="text-sm text-[#75807A]">Validez chaque recharge <b>après avoir vérifié le versement sur votre téléphone</b> (Orange Money / MTN MoMo). Le solde du passager n'augmente qu'après votre approbation.</p>
              <div className="flex gap-2">
                {[{ id: "pending", label: "En attente" }, { id: "all", label: "Toutes" }].map((f) => (
                  <button key={f.id} data-testid={`recharge-filter-${f.id}`} onClick={() => setRechargeFilter(f.id)}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${rechargeFilter === f.id ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "bg-white text-[#5A635D] border-[#E2DFD8] hover:bg-[#F7F4EF]"}`}>
                    {f.label}{f.id === "pending" && recharges?.pending_count > 0 ? ` (${recharges.pending_count})` : ""}
                  </button>
                ))}
              </div>
              <div className="bg-white rounded-2xl border border-[#E2DFD8] overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F0EBE1] text-[#75807A] text-xs uppercase"><tr>
                    <th className="text-left p-4">Passager</th><th className="text-left p-4">Méthode</th><th className="text-left p-4">Montant</th><th className="text-left p-4">ID transaction</th><th className="text-left p-4">Statut</th><th className="text-left p-4">Action</th>
                  </tr></thead>
                  <tbody>
                    {(() => {
                      const rows = (recharges?.items || []).filter((x) => rechargeFilter === "all" || x.status === "pending");
                      if (rows.length === 0) return (<tr><td colSpan="6" className="p-8 text-center text-[#75807A]">{rechargeFilter === "pending" ? "Aucune recharge en attente 🎉" : "Aucune recharge pour le moment."}</td></tr>);
                      return rows.map((x) => (
                      <tr key={x.id} data-testid={`recharge-row-${x.id}`} className="border-t border-[#E2DFD8]">
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <img src={`${API}/profile-photos/${x.user_id}`} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-9 h-9 rounded-full object-cover bg-[#F0EBE1] shrink-0" data-testid={`recharge-selfie-${x.id}`} />
                            <div>
                              <div className="font-medium text-[#1A1A1A] flex items-center gap-1.5">{x.passenger_name || "—"}{x.recharger_role === "driver" && <span data-testid={`recharge-role-${x.id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#F26419]/20 text-[#D94F2B]">Chauffeur</span>}</div>
                              <div className="text-xs text-[#75807A]">{x.passenger_phone || x.sender_phone || ""}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">{x.method === "mtn_momo" ? "MTN MoMo" : "Orange Money"}</td>
                        <td className="p-4 font-bold text-[#1A1A1A]">{(x.amount || 0).toLocaleString("fr-FR")} FCFA</td>
                        <td className="p-4">
                          <div className="font-mono text-xs">{x.transaction_id || "—"}</div>
                          {x.proof_path && (
                            <button data-testid={`recharge-proof-${x.id}`} onClick={() => openImage(`/admin/wallet-recharges/${x.id}/proof`)} className="mt-1 text-xs font-semibold text-[#2563EB] underline">Voir le reçu</button>
                          )}
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-lg text-xs font-semibold ${x.status === "pending" ? "bg-[#B8860B]/15 text-[#B8860B]" : x.status === "rejected" ? "bg-[#D32F2F]/15 text-[#D32F2F]" : "bg-[#16a34a]/15 text-[#16a34a]"}`}>
                            {x.status === "pending" ? "En attente" : x.status === "rejected" ? "Refusée" : "Validée"}
                          </span>
                          {x.receipt_no && <div className="mt-1 text-[10px] text-[#75807A]">Reçu #{x.receipt_no}</div>}
                        </td>
                        <td className="p-4">
                          {x.status === "pending" ? (
                            <div className="flex gap-2">
                              <Button data-testid={`recharge-approve-${x.id}`} onClick={() => approveRecharge(x.id)} className="h-9 px-3 rounded-lg bg-[#16a34a] hover:bg-[#15803d] text-white text-xs font-semibold">Approuver</Button>
                              <Button data-testid={`recharge-reject-${x.id}`} onClick={() => rejectRecharge(x.id)} className="h-9 px-3 rounded-lg bg-white border border-[#D32F2F] text-[#D32F2F] hover:bg-[#D32F2F] hover:text-white text-xs font-semibold">Refuser</Button>
                            </div>
                          ) : <span className="text-xs text-[#75807A]">—</span>}
                        </td>
                      </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {section === "txns" && (
            <div className="bg-white rounded-2xl border border-[#E2DFD8] overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F0EBE1] text-[#75807A] text-xs uppercase"><tr>
                  <th className="text-left p-4">Montant</th><th className="text-left p-4">Méthode</th><th className="text-left p-4">Type</th><th className="text-left p-4">Statut</th>
                </tr></thead>
                <tbody>
                  {txns.map((tx) => (
                    <tr key={tx.id} className="border-t border-[#E2DFD8]">
                      <td className="p-4 font-medium">{fcfa(tx.amount)}</td>
                      <td className="p-4 capitalize">{tx.method}</td>
                      <td className="p-4">{tx.type === "withdrawal" ? "Retrait" : "Course"}</td>
                      <td className="p-4"><span className="text-xs font-semibold px-2 py-1 rounded-full bg-[#F0EBE1]">{tx.status}</span></td>
                    </tr>
                  ))}
                  {txns.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-[#75807A]">—</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {section === "complaints" && (
            <div>
              <h2 className="font-head font-black text-2xl mb-4">{t("admin.complaints")}</h2>
              <div className="space-y-3">
                {complaints.length === 0 && <div data-testid="no-complaints" className="bg-white rounded-2xl border border-[#E2DFD8] p-8 text-center text-[#75807A]">{t("admin.noComplaints")}</div>}
                {complaints.map((c) => (
                  <div key={c.id} data-testid={`complaint-${c.id}`} className="bg-white rounded-2xl border border-[#E2DFD8] p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-[#1A1A1A]">{c.name || "Anonyme"}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#F0EBE1] capitalize">{c.category}</span>
                          <span data-testid={`complaint-status-${c.id}`} className={`text-xs px-2 py-0.5 rounded-full font-semibold ${c.status === "resolved" ? "bg-[#16a34a]/15 text-[#16a34a]" : c.status === "processing" ? "bg-[#F26419]/25 text-[#B8860B]" : "bg-[#D32F2F]/15 text-[#D32F2F]"}`}>{t(`admin.cStatus_${c.status}`)}</span>
                        </div>
                        <div className="text-xs text-[#75807A] mt-0.5">{c.phone || "—"}{c.ride_ref ? ` · Réf: ${c.ride_ref}` : ""} · {new Date(c.created_at).toLocaleString("fr-FR")}</div>
                        <p className="mt-2 text-sm text-[#3B423E] whitespace-pre-wrap">{c.message}</p>
                        {c.resolution && <p className="mt-2 text-xs text-[#16a34a]">✔ {c.resolution}</p>}
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        {c.status !== "processing" && c.status !== "resolved" && <Button data-testid={`complaint-processing-${c.id}`} onClick={() => updateComplaint(c.id, { status: "processing" })} className="h-9 px-3 rounded-lg bg-[#F26419] text-[#1A1A1A] text-xs">{t("admin.cMarkProcessing")}</Button>}
                        {c.status !== "resolved" && <Button data-testid={`complaint-resolve-${c.id}`} onClick={() => updateComplaint(c.id, { status: "resolved", resolution: "Traité par l'équipe" })} className="h-9 px-3 rounded-lg bg-[#16a34a] text-white text-xs">{t("admin.cResolve")}</Button>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {section === "contracts" && (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="font-head font-black text-2xl">Contrats signés (agents)</h2>
                  <p className="text-sm text-[#75807A]">Uniquement les agents recrutés. Les chauffeurs (partenaires) ne signent pas de contrat.</p>
                </div>
                {contracts && (
                  <div className="flex gap-3 text-sm">
                    <span className="px-3 py-1.5 rounded-xl bg-[#16a34a]/15 text-[#16794a] font-semibold">Signés : {contracts.signed_count}</span>
                    <span className="px-3 py-1.5 rounded-xl bg-[#F26419]/20 text-[#B8860B] font-semibold">En attente : {contracts.pending_count}</span>
                  </div>
                )}
              </div>
              {contracts && (() => {
                const tot = (contracts.signed_count || 0) + (contracts.pending_count || 0);
                const pct = tot ? Math.round((contracts.signed_count / tot) * 100) : 0;
                const low = pct < 70;
                return (
                  <div data-testid="compliance-card" className={`mb-4 bg-white rounded-2xl border p-5 ${low ? "border-[#D32F2F]/40" : "border-[#E2DFD8]"}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-[#1A1A1A]">Conformité — taux de signature {low && <span data-testid="compliance-alert" className="ml-1 text-xs px-2 py-0.5 rounded-full font-bold bg-[#D32F2F]/15 text-[#D32F2F]">⚠️ À relancer</span>}</span>
                      <span data-testid="compliance-pct" className={`font-head font-black text-2xl ${low ? "text-[#D32F2F]" : "text-[#16794a]"}`}>{pct}%</span>
                    </div>
                    <div className="mt-2 h-2.5 rounded-full bg-[#F0EBE1] overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${low ? "bg-[#D32F2F]" : "bg-[#16a34a]"}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="mt-2 text-xs text-[#75807A]">{contracts.signed_count} agent(s) signé(s) sur {tot} · {contracts.pending_count} en attente</div>
                  </div>
                );
              })()}
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Button data-testid="contracts-export-zip" onClick={exportContracts} className="h-9 px-4 rounded-lg bg-[#1A1A1A] text-white text-sm font-semibold"><Download className="w-4 h-4 mr-1" />Exporter tout (ZIP)</Button>
                {contracts?.pending_count > 0 && (
                  <Button data-testid="contracts-remind-all" onClick={remindAll} className="h-9 px-4 rounded-lg bg-[#F26419] hover:bg-[#d9540f] text-white text-sm font-semibold">📣 Relance groupée ({contracts.pending_count})</Button>
                )}
                <div className="flex items-center gap-2 text-sm text-[#75807A]">
                  <span>Durée CDD :</span>
                  <select data-testid="contract-duration-select" value={contractDuration} onChange={(e) => saveDuration(Number(e.target.value))} className="h-9 rounded-lg border border-[#E2DFD8] px-2 bg-white text-[#1A1A1A] font-semibold">
                    <option value={6}>6 mois</option><option value={12}>12 mois</option><option value={24}>24 mois</option>
                  </select>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#75807A]">
                  <span>Relance auto :</span>
                  <select data-testid="reminder-days-select" value={reminderDays} onChange={(e) => saveReminderDays(Number(e.target.value))} className="h-9 rounded-lg border border-[#E2DFD8] px-2 bg-white text-[#1A1A1A] font-semibold">
                    <option value={3}>3 jours</option><option value={5}>5 jours</option><option value={7}>7 jours</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2 mb-4">
                {[{ id: "all", label: "Tous" }, { id: "signed", label: "Signé" }, { id: "pending", label: "En attente" }].map((f) => (
                  <button key={f.id} data-testid={`contract-filter-${f.id}`} onClick={() => setContractFilter(f.id)}
                    className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${contractFilter === f.id ? "bg-[#1A1A1A] text-white" : "bg-white border border-[#E2DFD8] text-[#75807A]"}`}>{f.label}</button>
                ))}
              </div>
              <div className="space-y-3">
                {(() => {
                  const all = contracts?.items || [];
                  const list = contractFilter === "all" ? all : all.filter((x) => x.status === contractFilter);
                  if (list.length === 0) return <div data-testid="no-contracts" className="bg-white rounded-2xl border border-[#E2DFD8] p-8 text-center text-[#75807A]">Aucun contrat dans cette catégorie.</div>;
                  return list.map((x) => (
                    <div key={x.agent_id} data-testid={`contract-${x.agent_id}`} className="bg-white rounded-2xl border border-[#E2DFD8] p-5 flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-[#1A1A1A]">{x.agent_name || "Agent"}</span>
                          {x.agent_code && <span className="text-xs px-2 py-0.5 rounded-full bg-[#F0EBE1] text-[#D94F2B] font-semibold">{x.agent_code}</span>}
                          <span data-testid={`contract-status-${x.agent_id}`} className={`text-xs px-2 py-0.5 rounded-full font-semibold ${x.status === "signed" ? "bg-[#16a34a]/15 text-[#16794a]" : "bg-[#F26419]/25 text-[#B8860B]"}`}>{x.status === "signed" ? "Signé" : "En attente"}</span>
                          {x.expiring_soon && <span data-testid={`contract-expiring-${x.agent_id}`} className="text-xs px-2 py-0.5 rounded-full font-semibold bg-[#D32F2F]/15 text-[#D32F2F]">⏰ Échéance proche</span>}
                          {x.renew_count > 0 && <span data-testid={`contract-renewcount-${x.agent_id}`} className="text-xs px-2 py-0.5 rounded-full font-semibold bg-[#16a34a]/10 text-[#16794a]">🔄 Renouvelé {x.renew_count}×</span>}
                        </div>
                        <div className="text-xs text-[#75807A] mt-0.5">{x.phone || "—"}{x.signed_at ? ` · Signé le ${new Date(x.signed_at).toLocaleString("fr-FR")}` : ""}{x.expires_at ? ` · Échéance ${new Date(x.expires_at).toLocaleDateString("fr-FR")}` : ""}{x.ip ? ` · IP ${x.ip}` : ""}</div>
                      </div>
                      {x.status === "signed" ? (
                        <div className="flex gap-2 shrink-0">
                          <button data-testid={`contract-download-${x.agent_id}`} onClick={async () => { try { await openPdf(`/admin/contracts/${x.contract_id}/pdf`, `contrat-${x.agent_name || "agent"}.pdf`); } catch { toast.error("Impossible d'ouvrir le contrat"); } }} className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-[#1A1A1A] text-white text-xs font-semibold"><Download className="w-3.5 h-3.5" /> Télécharger</button>
                          <button data-testid={`contract-renew-${x.agent_id}`} onClick={() => renewContract(x.contract_id, x.agent_name)} className={`inline-flex items-center gap-1 h-9 px-4 rounded-lg text-xs font-semibold ${x.expiring_soon ? "bg-[#F26419] text-white" : "bg-white border border-[#16a34a] text-[#16794a] hover:bg-[#16a34a]/5"}`}>🔄 Renouveler</button>
                        </div>
                      ) : (
                        <button data-testid={`contract-remind-${x.agent_id}`} onClick={() => remindContract(x.agent_id)} className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-white border border-[#D94F2B] text-[#D94F2B] text-xs font-semibold hover:bg-[#F26419]/5">Relancer par SMS</button>
                      )}
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          {section === "expenses" && (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h2 className="font-head font-black text-2xl">{t("admin.expenses")}</h2>
                {expenses && (
                  <div className="flex gap-3 text-sm">
                    <span data-testid="exp-pending-total" className="px-3 py-1.5 rounded-xl bg-[#F26419]/20 text-[#B8860B] font-semibold">{t("admin.expPending")} : {fcfa(expenses.pending_total || 0)}</span>
                    <span data-testid="exp-reimbursed-total" className="px-3 py-1.5 rounded-xl bg-[#16a34a]/15 text-[#16794a] font-semibold">{t("admin.expReimbursedTotal")} : {fcfa(expenses.reimbursed_total || 0)}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2 mb-4">
                {[{ id: "pending", label: t("admin.expTabPending"), n: expenses?.pending_count || 0 }, { id: "all", label: t("admin.expTabAll") }].map((f) => (
                  <button key={f.id} data-testid={`exp-filter-${f.id}`} onClick={() => setExpFilter(f.id)}
                    className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${expFilter === f.id ? "bg-[#1A1A1A] text-white" : "bg-white border border-[#E2DFD8] text-[#75807A]"}`}>
                    {f.label}{f.id === "pending" && f.n > 0 ? ` (${f.n})` : ""}
                  </button>
                ))}
              </div>
              <div className="space-y-3">
                {(() => {
                  const all = expenses?.items || [];
                  const list = expFilter === "pending" ? all.filter((x) => ["submitted", "processing"].includes(x.status)) : all;
                  if (list.length === 0) return <div data-testid="no-expenses" className="bg-white rounded-2xl border border-[#E2DFD8] p-8 text-center text-[#75807A]">{expFilter === "pending" ? t("admin.expNoPending") : t("admin.expEmpty")}</div>;
                  return list.map((x) => (
                    <div key={x.id} data-testid={`admin-expense-${x.id}`} className="bg-white rounded-2xl border border-[#E2DFD8] p-5 flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex gap-4">
                        {x.attachment && <a data-testid={`admin-expense-att-${x.id}`} href={x.attachment} target="_blank" rel="noreferrer" className="shrink-0"><img src={x.attachment} alt="justificatif" className="w-16 h-16 rounded-lg object-cover border border-[#E2DFD8]" /></a>}
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${x.type === "note" ? "bg-[#2563EB]/15 text-[#2563EB]" : "bg-[#F26419]/15 text-[#D8531E]"}`}>{x.type === "note" ? t("admin.expTypeNote") : t("admin.expTypeExpense")}</span>
                            {x.type !== "note" && <span className="font-head font-bold text-lg text-[#1A1A1A]">{fcfa(x.amount)}</span>}
                            <span className="text-xs px-2 py-0.5 rounded-full bg-[#F0EBE1]">{x.category}</span>
                            <span data-testid={`admin-expense-status-${x.id}`} className={`text-xs px-2 py-0.5 rounded-full font-semibold ${(x.status === "validated" || x.status === "reimbursed") ? "bg-[#16a34a]/15 text-[#16794a]" : x.status === "rejected" ? "bg-[#D32F2F]/15 text-[#D32F2F]" : x.status === "processing" ? "bg-[#2563EB]/15 text-[#2563EB]" : "bg-[#F26419]/25 text-[#B8860B]"}`}>{t(`admin.expStatus_${x.status}`)}</span>
                          </div>
                          <div className="text-sm text-[#3B423E] mt-1">{x.note || "—"}</div>
                          <div className="text-xs text-[#75807A] mt-0.5">{x.agent_name || "Agent"}{x.agent_code ? ` · ${x.agent_code}` : ""} · {new Date(x.created_at).toLocaleString("fr-FR")}</div>
                        </div>
                      </div>
                      {["submitted", "processing"].includes(x.status) && (
                        <div className="flex gap-2 shrink-0 flex-wrap">
                          {x.status === "submitted" && <Button data-testid={`expense-process-${x.id}`} onClick={() => processExpense(x.id)} className="h-9 px-3 rounded-lg bg-[#2563EB] text-white text-xs font-semibold"><Clock className="w-3.5 h-3.5 mr-1" />{t("admin.expProcess")}</Button>}
                          <Button data-testid={`expense-validate-${x.id}`} onClick={() => validateExpense(x.id)} className="h-9 px-3 rounded-lg bg-[#16a34a] text-white text-xs font-semibold"><Check className="w-3.5 h-3.5 mr-1" />{t("admin.expValidate")}</Button>
                          <Button data-testid={`expense-reject-${x.id}`} onClick={() => rejectExpense(x.id)} className="h-9 px-3 rounded-lg bg-white border border-[#D32F2F] text-[#D32F2F] text-xs font-semibold"><X className="w-3.5 h-3.5 mr-1" />{t("admin.expReject")}</Button>
                        </div>
                      )}
                      <div className="w-full mt-3 pt-3 border-t border-[#F0EBE1]" data-testid={`expense-thread-${x.id}`}>
                        {(x.replies || []).map((rp) => (
                          <div key={rp.id} className={`mb-2 flex ${rp.author_role === "admin" ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${rp.author_role === "admin" ? "bg-[#F26419]/10 text-[#1A1A1A]" : "bg-[#F0EBE1] text-[#3B423E]"}`}>
                              <div className="text-[10px] font-bold text-[#75807A] mb-0.5">{rp.author_name} · {new Date(rp.at).toLocaleString("fr-FR")}</div>
                              {rp.text}
                            </div>
                          </div>
                        ))}
                        <div className="flex gap-2 mt-1">
                          <Input data-testid={`expense-reply-input-${x.id}`} value={expReply[x.id] || ""} onChange={(e) => setExpReply((s) => ({ ...s, [x.id]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && replyExpense(x.id)} placeholder="Répondre à l'agent…" className="h-10 rounded-xl border-2 flex-1" />
                          <Button data-testid={`expense-reply-send-${x.id}`} onClick={() => replyExpense(x.id)} className="h-10 px-4 rounded-xl bg-[#1A1A1A] text-white text-sm font-bold">Répondre</Button>
                        </div>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          {section === "mail" && (
            <div className="grid gap-6">
              <div className="grid gap-6 lg:grid-cols-2">
                <MailConsole />
                <MailGenerator />
              </div>
              <BrandAssetsGenerator />
            </div>
          )}

          {section === "documents" && (
            <div className="max-w-3xl" data-testid="admin-documents-section">
              <h2 className="font-head font-black text-2xl text-[#1A1A1A] mb-1">📁 Documents Nold Go</h2>
              <p className="text-sm text-[#75807A] mb-6">Pack administratif officiel prêt à imprimer ou envoyer. Belgique · FR + NL.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { k: "contract", icon: "📄", label: "Contrat de partenariat", desc: "TVA belge · commission 25% · signatures" },
                  { k: "manual-resto", icon: "📘", label: "Manuel Restaurateur", desc: "Guide 10 étapes pour bien démarrer" },
                  { k: "manual-livreur", icon: "🚲", label: "Manuel Livreur vélo", desc: "Guide 10 étapes livraison Bruxelles" },
                  { k: "brochure", icon: "✨", label: "Brochure commerciale", desc: "Impact social & écologique · à partager" },
                ].map((d) => (
                  <div key={d.k} className="bg-white rounded-2xl border border-[#E2DFD8] p-4 flex flex-col gap-3">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl">{d.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-head font-bold text-[#1A1A1A]">{d.label}</div>
                        <div className="text-xs text-[#75807A] mt-0.5">{d.desc}</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => showPdf(`${d.label} (FR)`, `/restaurateur/admin/documents/${d.k}.pdf?lang=fr`, `nold-go-${d.k}-fr.pdf`)}
                        data-testid={`admin-doc-${d.k}-fr`}
                        className="flex-1 h-10 rounded-full bg-[#F26419] hover:bg-[#D94D0E] text-white text-xs font-bold inline-flex items-center justify-center gap-1">
                        <Download className="w-3.5 h-3.5" /> FR
                      </button>
                      <button onClick={() => showPdf(`${d.label} (NL)`, `/restaurateur/admin/documents/${d.k}.pdf?lang=nl`, `nold-go-${d.k}-nl.pdf`)}
                        data-testid={`admin-doc-${d.k}-nl`}
                        className="flex-1 h-10 rounded-full bg-white border border-[#F26419] text-[#F26419] hover:bg-[#FFF3E6] text-xs font-bold inline-flex items-center justify-center gap-1">
                        <Download className="w-3.5 h-3.5" /> NL
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs text-[#75807A]">Le contrat inclut désormais un <b>QR code de vérification</b> quand il est signé électroniquement par un restaurateur.</p>
            </div>
          )}


          {section === "flyer" && (
            <div className="max-w-3xl" data-testid="admin-flyer-section">
              <h2 className="font-head font-black text-xl text-[#1A1A1A] mb-1">Flyer QR promotionnel</h2>
              <p className="text-sm text-[#75807A] mb-4">{isNoldDomain() ? "Flyer officiel Nold Go « Scannez pour commander ». PDF pro prêt pour Vistaprint (A5, CMJN, 300 DPI, fonds perdus)." : "Flyer officiel « Scannez pour commander ». PDF pro prêt pour Vistaprint (A5, CMJN, 300 DPI, fonds perdus) ou PDF maison (2 flyers A5 sur A4)."}</p>
              <ShareStatsBlock />
              {!(isNoldDomain() || viewZone === "europe") && <PromoPoster />}
              <div className="mt-6 bg-[#0E1624] text-white rounded-2xl border border-white/10 p-6" data-testid="nold-flyer-card">
                <h3 className="font-head font-black text-lg flex items-center gap-2"><QrCode className="w-5 h-5 text-[#FFD23F]" /> Flyers Nold Go (Europe)</h3>
                <p className="mt-1 text-sm text-white/70">{isNoldDomain() ? "Version Europe premium (fond sombre « Nold Go »). VTC voiture · Livraison de repas · Bruxelles." : "Version Europe premium (fond sombre « Nold Go »). Les flyers BentSkin Go de l'espace équipe / agents africains restent inchangés."}</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button data-testid="nold-flyer-pdf" onClick={() => showPdf('Flyer A5 Nold Go', '/marketing/flyer.pdf?brand=nold', 'nold-go-flyer-A5.pdf')} className="h-11 px-4 rounded-xl bg-[#FFD23F] hover:bg-[#f5c518] text-[#1A1A1A] font-bold"><Download className="w-4 h-4 mr-1.5" /> Flyer A5 Nold Go</Button>
                  <Button data-testid="nold-card-pdf" onClick={() => showPdf('Carte de visite Nold Go', '/marketing/card.pdf?brand=nold', 'nold-go-carte-visite.pdf')} className="h-11 px-4 rounded-xl bg-white/10 border border-white/20 hover:bg-white/15 text-white font-bold"><Download className="w-4 h-4 mr-1.5" /> Carte de visite Nold Go</Button>
                  <PrintShareButtons kind="flyer-europe" pdfPath="/marketing/flyer.pdf?brand=nold" jpgUrl="/marketing/flyer-side.jpg?brand=nold&v=client" shareTitle="Flyer Nold Go" shareText="Découvrez Nold Go — Livraison de repas à vélo à Bruxelles 🛵" testIdPrefix="nold-flyer" />
                </div>
                <p className="mt-5 mb-2 text-xs font-bold uppercase tracking-wider text-white/50">Aperçu à l'écran</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="nold-flyer-preview">
                  <img src={`${API}/marketing/flyer-side.jpg?brand=nold&v=client`} alt="Aperçu flyer Nold Go — passager" className="w-full rounded-xl border border-white/10 bg-white/5" loading="lazy" />
                  <img src={`${API}/marketing/flyer-side.jpg?brand=nold&v=chauffeur`} alt="Aperçu flyer Nold Go — chauffeur" className="w-full rounded-xl border border-white/10 bg-white/5" loading="lazy" />
                </div>
              </div>

              <div className="mt-6 bg-white rounded-2xl border-2 border-[#F26419] p-6" data-testid="nold-restaurateur-flyer-card">
                <h3 className="font-head font-black text-lg flex items-center gap-2 text-[#1A1A1A]"><UtensilsCrossed className="w-5 h-5 text-[#F26419]" /> Flyer Restaurateurs Nold Go</h3>
                <p className="mt-1 text-sm text-[#75807A]">Argument choc : <strong>« Seulement 25% de commission — Gardez 75% de vos ventes »</strong>. Design minimaliste orange sur crème, photo coursier réel, QR direct vers le formulaire d'inscription restaurateur.</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button data-testid="nold-rt-flyer-pdf" onClick={() => showPdf('Flyer Restaurateurs A5', '/marketing/restaurateur-flyer.pdf', 'nold-go-flyer-restaurateurs-A5.pdf')} className="h-11 px-4 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold"><Download className="w-4 h-4 mr-1.5" /> Flyer A5 (PDF)</Button>
                  <Button data-testid="nold-rt-flyer-jpg" onClick={() => window.open(`${API}/marketing/restaurateur-flyer.jpg`, '_blank')} className="h-11 px-4 rounded-xl bg-white border-2 border-[#F26419] hover:bg-[#F26419]/5 text-[#F26419] font-bold"><Download className="w-4 h-4 mr-1.5" /> Flyer JPG</Button>
                  <Button data-testid="nold-rt-panel-pdf" onClick={() => showPdf('Panneau vitrine A3', '/marketing/restaurateur-panel.pdf', 'nold-go-panneau-vitrine-A3.pdf')} className="h-11 px-4 rounded-xl bg-[#1A1A1A] hover:bg-black text-white font-bold"><Download className="w-4 h-4 mr-1.5" /> Panneau A3 vitrine (PDF)</Button>
                  <Button data-testid="nold-rt-sticker-pdf" onClick={() => showPdf('Sticker vitrine', '/marketing/restaurateur-sticker.pdf', 'nold-go-sticker-vitrine.pdf')} className="h-11 px-4 rounded-xl bg-white border-2 border-[#1A1A1A] hover:bg-[#F0EBE1] text-[#1A1A1A] font-bold"><Download className="w-4 h-4 mr-1.5" /> Sticker vitrine (PDF)</Button>
                  <Button data-testid="nold-rt-kit-zip" onClick={() => window.open(`${API}/marketing/restaurateur-kit.zip`, '_blank')} className="h-11 px-4 rounded-xl bg-[#16a34a] hover:bg-[#128a3e] text-white font-bold"><Download className="w-4 h-4 mr-1.5" /> Kit complet (.zip)</Button>
                  <PrintShareButtons kind="flyer-restaurateur" pdfPath="/marketing/restaurateur-flyer.pdf" jpgUrl="/marketing/restaurateur-flyer.jpg" shareTitle="Devenez partenaire Nold Go — 25% de commission" shareText="Rejoignez Nold Go et gardez 75% de vos ventes 🍔 Inscription gratuite en 2 min." testIdPrefix="nold-rt-flyer" />
                </div>
                <p className="mt-5 mb-2 text-xs font-bold uppercase tracking-wider text-[#75807A]">Aperçu à l'écran</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <img data-testid="nold-rt-flyer-preview" src={`${API}/marketing/restaurateur-flyer.jpg`} alt="Aperçu flyer restaurateurs Nold Go" className="w-full rounded-xl border border-[#E2DFD8] shadow-sm" loading="lazy" />
                  <img data-testid="nold-rt-panel-preview" src={`${API}/marketing/restaurateur-panel.jpg`} alt="Aperçu panneau vitrine A3 Nold Go" className="w-full rounded-xl border border-[#E2DFD8] shadow-sm" loading="lazy" />
                </div>
              </div>

              <BusinessCardCard />

              <div className="mt-6 bg-white rounded-2xl border-2 border-[#16a34a] p-6" data-testid="nold-car-sticker-card">
                <h3 className="font-head font-black text-lg flex items-center gap-2 text-[#1A1A1A]"><Download className="w-5 h-5 text-[#16a34a]" /> Autocollant voiture rond Ø 100 mm</h3>
                <p className="mt-1 text-sm text-[#75807A]">Autocollant <strong>rond 10 cm de diamètre</strong> pour les voitures Nold Go Food. Design minimaliste, fond vert, logo <strong>Nold Go Food</strong> blanc au centre. PDF <strong>CMJN 300 DPI</strong>, fond perdu 3 mm, traits de coupe vectoriels — prêt pour l'imprimeur pro (Vistaprint, Onlineprinters, HelloPrint).</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button data-testid="nold-car-sticker-pdf" onClick={() => showPdf('Autocollant voiture Ø100 mm', '/marketing/car-sticker.pdf', 'nold-go-food-autocollant-voiture-100mm-CMJN.pdf')} className="h-11 px-4 rounded-xl bg-[#16a34a] hover:bg-[#128a3e] text-white font-bold"><Download className="w-4 h-4 mr-1.5" /> Autocollant voiture (PDF)</Button>
                  <Button data-testid="nold-car-sticker-jpg" onClick={() => window.open(`${API}/marketing/car-sticker.jpg`, '_blank')} className="h-11 px-4 rounded-xl bg-white border-2 border-[#16a34a] hover:bg-[#16a34a]/5 text-[#16a34a] font-bold"><Download className="w-4 h-4 mr-1.5" /> Aperçu JPG</Button>
                </div>
                <p className="mt-5 mb-2 text-xs font-bold uppercase tracking-wider text-[#75807A]">Aperçu à l'écran</p>
                <div className="flex justify-center">
                  <img data-testid="nold-car-sticker-preview" src={`${API}/marketing/car-sticker.jpg`} alt="Aperçu autocollant voiture Nold Go Food" className="w-64 h-64 rounded-full border border-[#E2DFD8] shadow-sm bg-white object-contain" loading="lazy" />
                </div>
                <p className="mt-3 text-[11px] text-[#75807A] text-center">💡 Impression conseillée : vinyle adhésif blanc mat ou brillant, résistant UV et eau (extérieur véhicule).</p>
              </div>

              <div className="mt-6 bg-white rounded-2xl border-2 border-[#059669] p-6" data-testid="nold-partner-sticker-card">
                <h3 className="font-head font-black text-lg flex items-center gap-2 text-[#1A1A1A]"><Download className="w-5 h-5 text-[#059669]" /> Sticker vitrine partenaire rond Ø 100 mm</h3>
                <p className="mt-1 text-sm text-[#75807A]">Autocollant <strong>rond 10 cm</strong> à coller sur la vitrine des restaurants partenaires. Fond blanc + anneau vert Nold Go, logo couleur, QR code du resto au centre + « SCANNEZ POUR COMMANDER ». PDF <strong>CMJN 300 DPI</strong>, fond perdu 3 mm, traits de coupe, cercle de coupe pointillé, <strong>zone de sécurité à r=44 mm</strong> (aucun contenu critique ne dépasse) — prêt pour l'imprimeur pro.</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button data-testid="nold-partner-sticker-pdf" onClick={() => showPdf('Sticker vitrine partenaire Ø100 mm', '/marketing/partner-sticker.pdf', 'nold-go-sticker-vitrine-generic-100mm-CMJN.pdf')} className="h-11 px-4 rounded-xl bg-[#059669] hover:bg-[#047857] text-white font-bold"><Download className="w-4 h-4 mr-1.5" /> Sticker générique (PDF)</Button>
                  <Button data-testid="nold-partner-sticker-jpg" onClick={() => window.open(`${API}/marketing/partner-sticker.jpg`, '_blank')} className="h-11 px-4 rounded-xl bg-white border-2 border-[#059669] hover:bg-[#059669]/5 text-[#059669] font-bold"><Download className="w-4 h-4 mr-1.5" /> Aperçu JPG</Button>
                </div>
                <p className="mt-5 mb-2 text-xs font-bold uppercase tracking-wider text-[#75807A]">Aperçu à l'écran (générique — sans QR spécifique)</p>
                <div className="flex justify-center">
                  <img data-testid="nold-partner-sticker-preview" src={`${API}/marketing/partner-sticker.jpg`} alt="Aperçu sticker vitrine partenaire Nold Go Food" className="w-64 h-64 rounded-full border border-[#E2DFD8] shadow-sm bg-white object-contain" loading="lazy" />
                </div>
                <p className="mt-3 text-[11px] text-[#75807A] text-center">💡 Pour un sticker <strong>personnalisé par restaurant</strong> (QR pointant vers le menu du resto), utilisez : <code className="bg-[#F5F1E8] px-1.5 py-0.5 rounded">/api/marketing/partner-sticker.pdf?resto_id=&lt;id&gt;&amp;resto_name=&lt;nom&gt;</code></p>
                <p className="mt-1 text-[11px] text-[#75807A] text-center">Impression conseillée : vinyle adhésif transparent ou blanc mat pour vitrine, résistant UV.</p>
              </div>

              <div className="mt-6 bg-white rounded-2xl border-2 border-[#F26419] p-6" data-testid="nold-trifold-card">
                <h3 className="font-head font-black text-lg flex items-center gap-2 text-[#1A1A1A]"><Download className="w-5 h-5 text-[#F26419]" /> Triptyque commercial A4 (plié en 3)</h3>
                <p className="mt-1 text-sm text-[#75807A]">Prospectus <strong>A4 paysage plié en 3 volets</strong> — 300 DPI CMJN, 2 pages recto/verso, prêt impression pro (Vistaprint, HelloPrint…). Contient : couverture avec QR + photo livreur, présentation, section restaurateurs, section chauffeurs, témoignages 4,8/5, 3 étapes pour commander, QR téléchargement app, contact + mentions légales belges.</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button data-testid="nold-trifold-pdf" onClick={() => showPdf('Triptyque A4 Nold Go', '/marketing/trifold.pdf', 'nold-go-triptyque-A4-CMJN-300dpi.pdf')} className="h-11 px-4 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold"><Download className="w-4 h-4 mr-1.5" /> Télécharger le PDF (2 pages)</Button>
                  <Button data-testid="nold-trifold-inside-jpg" onClick={() => window.open(`${API}/marketing/trifold-inside.jpg`, '_blank')} className="h-11 px-4 rounded-xl bg-white border-2 border-[#F26419] hover:bg-[#F26419]/5 text-[#F26419] font-bold"><Download className="w-4 h-4 mr-1.5" /> Aperçu intérieur (JPG)</Button>
                  <Button data-testid="nold-trifold-outside-jpg" onClick={() => window.open(`${API}/marketing/trifold-outside.jpg`, '_blank')} className="h-11 px-4 rounded-xl bg-white border-2 border-[#1A1A1A] hover:bg-[#F0EBE1] text-[#1A1A1A] font-bold"><Download className="w-4 h-4 mr-1.5" /> Aperçu extérieur (JPG)</Button>
                  <PrintShareButtons kind="trifold" pdfPath="/marketing/trifold.pdf" jpgUrl="/marketing/trifold-inside.jpg" shareTitle="Triptyque Nold Go — Bruxelles" shareText="Découvrez le prospectus commercial Nold Go — 3 volets pliables, prêt impression." testIdPrefix="nold-trifold" />
                </div>
                <p className="mt-5 mb-2 text-xs font-bold uppercase tracking-wider text-[#75807A]">Aperçu des deux faces (à imprimer recto/verso avant pliage)</p>
                <div className="grid grid-cols-1 gap-3">
                  <img data-testid="nold-trifold-inside-preview" src={`${API}/marketing/trifold-inside.jpg`} alt="Aperçu intérieur triptyque Nold Go" className="w-full rounded-xl border border-[#E2DFD8] shadow-sm" loading="lazy" />
                  <img data-testid="nold-trifold-outside-preview" src={`${API}/marketing/trifold-outside.jpg`} alt="Aperçu extérieur triptyque Nold Go" className="w-full rounded-xl border border-[#E2DFD8] shadow-sm" loading="lazy" />
                </div>
              </div>

              {/* Sticker Vitrine A5 — restaurants partenaires Nold Go Food */}
              <div className="mt-6 bg-white rounded-2xl border-2 border-[#059669] p-6" data-testid="nold-vitrine-sticker-card">
                <h3 className="font-head font-black text-lg flex items-center gap-2 text-[#1A1A1A]"><Download className="w-5 h-5 text-[#059669]" /> Sticker Vitrine A5 · Restaurants partenaires</h3>
                <p className="mt-1 text-sm text-[#75807A]">Sticker <strong>A5 (148×210 mm)</strong> à coller sur la vitrine ou porte d'entrée du restaurant. Design vert & orange Nold Go Food, avec icône assiette, QR code vers <code className="text-[#059669]">noldgo.com</code>, phrase "Commandez ici sur Nold Go Food". 300 DPI CMJN, prêt impression pro.</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button data-testid="nold-vitrine-pdf" onClick={() => showPdf('Sticker Vitrine A5 Nold Go Food', '/marketing/vitrine-sticker.pdf', 'nold-go-food-sticker-vitrine-A5.pdf')} className="h-11 px-4 rounded-xl bg-[#059669] hover:bg-[#047857] text-white font-bold"><Download className="w-4 h-4 mr-1.5" /> Télécharger le PDF (A5)</Button>
                  <Button data-testid="nold-vitrine-jpg" onClick={() => window.open(`${API}/marketing/vitrine-sticker.jpg`, '_blank')} className="h-11 px-4 rounded-xl bg-white border-2 border-[#059669] hover:bg-[#F0FDF4] text-[#059669] font-bold"><Download className="w-4 h-4 mr-1.5" /> Aperçu (JPG)</Button>
                </div>
                <div className="mt-5">
                  <img data-testid="nold-vitrine-preview" src={`${API}/marketing/vitrine-sticker.jpg`} alt="Aperçu sticker vitrine Nold Go Food" className="w-full max-w-sm mx-auto rounded-xl border border-[#E2DFD8] shadow-sm" loading="lazy" />
                </div>
              </div>

              <div className="mt-6 bg-white rounded-2xl border-2 border-[#16a34a] p-6" data-testid="nold-livreur-flyer-card">
                <h3 className="font-head font-black text-lg flex items-center gap-2 text-[#1A1A1A]"><Bike className="w-5 h-5 text-[#16a34a]" /> Flyer Recrutement Livreurs</h3>
                <p className="mt-1 text-sm text-[#75807A]">Argument choc : <strong>« Soyez votre patron · Payé à chaque course »</strong> · aucun permis · paiement rapide · inscription gratuite. Photo IA d'un livreur Nold Go réel à Bruxelles.</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button data-testid="nold-livreur-flyer-pdf" onClick={() => showPdf('Flyer Recrutement Livreurs', '/marketing/livreur-flyer.pdf', 'nold-go-flyer-livreurs-A5.pdf')} className="h-11 px-4 rounded-xl bg-[#16a34a] hover:bg-[#128a3e] text-white font-bold"><Download className="w-4 h-4 mr-1.5" /> Flyer Livreurs A5 (PDF)</Button>
                  <Button data-testid="nold-livreur-flyer-jpg" onClick={() => window.open(`${API}/marketing/livreur-flyer.jpg`, '_blank')} className="h-11 px-4 rounded-xl bg-white border-2 border-[#16a34a] hover:bg-[#16a34a]/5 text-[#16a34a] font-bold"><Download className="w-4 h-4 mr-1.5" /> Flyer JPG</Button>
                  <PrintShareButtons kind="flyer-livreur" pdfPath="/marketing/livreur-flyer.pdf" jpgUrl="/marketing/livreur-flyer.jpg" shareTitle="Devenez livreur Nold Go à Bruxelles 🚲" shareText="Soyez votre propre patron — Payé à chaque course. Inscription gratuite en 2 min." testIdPrefix="nold-livreur-flyer" />
                </div>
                <p className="mt-5 mb-2 text-xs font-bold uppercase tracking-wider text-[#75807A]">Aperçu à l'écran</p>
                <img data-testid="nold-livreur-flyer-preview" src={`${API}/marketing/livreur-flyer.jpg`} alt="Aperçu flyer recrutement livreurs Nold Go" className="w-full max-w-md mx-auto rounded-xl border border-[#E2DFD8] shadow-sm" loading="lazy" />
              </div>

              <div className="mt-6 bg-white rounded-2xl border-2 border-[#1A1A1A] p-6" data-testid="agent-badge-card">
                <h3 className="font-head font-black text-lg flex items-center gap-2 text-[#1A1A1A]">🪪 Badge Agent Nold Go</h3>
                <p className="mt-1 text-sm text-[#75807A]">Générez un badge plastifiable (86×135 mm · 300 DPI) pour vos agents terrain. Photo idéalement déjà retouchée (fond neutre).</p>
                <form
                  data-testid="agent-badge-form"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const f = e.currentTarget;
                    const nameV = f.elements.namedItem("name").value.trim();
                    const roleV = f.elements.namedItem("role").value.trim();
                    const badgeIdV = f.elements.namedItem("badge_id").value.trim();
                    const file = f.elements.namedItem("photo").files?.[0];
                    if (!nameV) return toast.error("Renseignez le nom.");
                    if (!file) return toast.error("Choisissez une photo.");
                    const fd = new FormData();
                    fd.append("name", nameV);
                    fd.append("role", roleV || "Agent");
                    fd.append("badge_id", badgeIdV);
                    fd.append("photo", file);
                    try {
                      const r = await api.post("/admin/agent-badge", fd, { responseType: 'blob' });
                      const url = URL.createObjectURL(r.data);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `badge-${nameV.toLowerCase().replace(/\s+/g, "-")}.jpg`;
                      document.body.appendChild(a); a.click(); a.remove();
                      setTimeout(() => URL.revokeObjectURL(url), 60000);
                      toast.success("Badge généré ✓");
                    } catch (err) {
                      // Quand responseType est 'blob' et que l'API renvoie une erreur JSON,
                      // axios met le JSON dans le blob → on doit le lire manuellement.
                      let detail = "";
                      try {
                        const blob = err?.response?.data;
                        if (blob && typeof blob.text === "function") {
                          const t = await blob.text();
                          detail = JSON.parse(t).detail || t;
                        }
                      } catch { /* ignore */ }
                      toast.error(detail || apiError(err));
                    }
                  }}
                  className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3"
                >
                  <input data-testid="badge-name" name="name" required placeholder="Nom complet (ex. Bertrand Kamga)" className="h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
                  <input data-testid="badge-role" name="role" defaultValue="Responsable Terrain" placeholder="Fonction" className="h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
                  <input data-testid="badge-id" name="badge_id" placeholder="N° badge (vide = auto)" className="h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
                  <input data-testid="badge-photo" name="photo" type="file" accept="image/*" required className="h-11 rounded-xl border-2 border-dashed border-[#F26419] px-3 text-sm bg-[#F26419]/5" />
                  <Button data-testid="badge-generate" type="submit" className="md:col-span-2 h-11 rounded-xl bg-[#1A1A1A] hover:bg-black text-white font-bold"><Download className="w-4 h-4 mr-1.5" /> Générer et télécharger le badge (JPG 300 DPI)</Button>
                </form>

                <p className="mt-6 mb-2 text-xs font-bold uppercase tracking-wider text-[#75807A]">Badges déjà générés</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {["bertrand", "pascal"].map((slug) => (
                    <AgentBadgePreview key={slug} slug={slug} label={slug === "bertrand" ? "Bertrand N. Kamga" : "Pascal Louis"} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {section === "countries" && (
            <div className="space-y-4">
              <div data-testid="cleanup-card" className="bg-white rounded-2xl border-2 border-[#D32F2F]/40 p-5">
                <h3 className="font-head font-bold flex items-center gap-2 text-[#1A1A1A]"><Trash2 className="w-4 h-4 text-[#D32F2F]" /> Données de test</h3>
                <p className="mt-1 text-sm text-[#75807A]">Nettoyez le site des chauffeurs de démo et des comptes / courses de test qui sèment la confusion.</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button data-testid="cleanup-demo-btn" onClick={() => cleanupTestData("demo")} className="h-10 px-4 rounded-lg bg-white border border-[#D32F2F] text-[#D32F2F] text-sm font-semibold">Supprimer démo & tests</Button>
                  <Button data-testid="cleanup-all-btn" onClick={() => cleanupTestData("all")} className="h-10 px-4 rounded-lg bg-[#D32F2F] text-white text-sm font-semibold">Réinitialiser (tout effacer)</Button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {countries.map((c) => (
                  <CountryCard key={c.code} c={c} onSave={saveCountry} t={t} />
                ))}
              </div>
            </div>
          )}

          {section === "vat" && <VatSection />}
        </main>
      </div>

      {/* MODAL Édition/Création restaurant — accessible depuis toutes les sections (restaurateurs + restos-manage) */}
      {rtCreatedResto && (
        <div data-testid="rt-created-modal" className="fixed inset-0 z-[3000] bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setRtCreatedResto(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full space-y-4 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-3xl mb-1">🍽️</div>
                <h3 className="font-head font-black text-xl">{rtCreatedResto.name}</h3>
                <p className="text-sm text-[#75807A]">{rtCreatedResto.published ? "En ligne sur noldgo.com" : "Brouillon · non publié"}</p>
              </div>
              <button data-testid="rt-created-close" onClick={() => setRtCreatedResto(null)} className="text-2xl text-[#75807A] hover:text-[#1A1A1A]">×</button>
            </div>
            <AdminMenuManager restaurantId={rtCreatedResto.id} restaurantName={rtCreatedResto.name} initialPublished={!!rtCreatedResto.published} onClose={() => { setRtCreatedResto(null); if (section === "restos-manage") loadRestos(restosTab, restosQuery); }} />
          </div>
        </div>
      )}

      {/* MODAL "Ajuster gratuité" — global (accessible depuis Restaurateurs ET Vitrines Food) */}
      {rtFreeId && (
        <div data-testid="rt-free-modal-global" className="fixed inset-0 z-[3300] bg-black/50 flex items-center justify-center p-4" onClick={() => setRtFreeId(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="font-head font-bold text-lg">💰 Ajuster la gratuité</h3>
              <p className="text-xs text-[#75807A] mt-1">Passe le compte en <b>admin_free</b>. Le restaurateur ne paie plus d'abonnement pendant la durée choisie (0 j = à vie). Il continue à payer 25 % de commission sur les commandes livrées.</p>
            </div>
            <div>
              <label className="text-sm font-semibold text-[#1A1A1A]">Durée de la gratuité</label>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {[{d:0,l:"À vie"},{d:30,l:"30 j"},{d:60,l:"60 j"},{d:90,l:"90 j"}].map((o) => (
                  <button key={o.d} data-testid={`rt-free-global-preset-${o.d}`}
                    onClick={() => setRtFreeDays(o.d)}
                    className={`h-11 rounded-xl border-2 text-sm font-bold transition-colors ${Number(rtFreeDays) === o.d ? "border-[#16a34a] bg-[#16a34a]/10 text-[#16a34a]" : "border-[#E2DFD8] text-[#1A1A1A] hover:border-[#16a34a]"}`}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-[#1A1A1A]">Note interne <span className="text-xs font-normal text-[#75807A]">(optionnelle)</span></label>
              <input data-testid="rt-free-global-note" value={rtFreeNote} onChange={(e) => setRtFreeNote(e.target.value)} placeholder="Ex: partenaire de longue date, ambassadeur…" className="mt-1.5 w-full h-10 rounded-lg border-2 border-[#E2DFD8] px-3 text-sm" />
            </div>
            <div className="flex gap-2">
              <Button data-testid="rt-free-global-cancel" onClick={() => setRtFreeId(null)} className="flex-1 h-11 rounded-lg bg-white border-2 border-[#E2DFD8] text-[#1A1A1A] text-sm font-semibold">Annuler</Button>
              <Button data-testid="rt-free-global-confirm" onClick={grantFreeRestaurateur} className="flex-1 h-11 rounded-lg bg-[#16a34a] hover:bg-[#128a3e] text-white text-sm font-bold">
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL Approbation restaurateur — choix de la durée de gratuité (4 options) */}
      {approveId && (
        <div data-testid="approve-modal" className="fixed inset-0 z-[3200] bg-black/50 flex items-center justify-center p-4" onClick={() => setApproveId(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-3xl mb-1">✅</div>
                <h3 className="font-head font-black text-xl">Approuver le dossier</h3>
                <p className="text-sm text-[#75807A]">Le restaurateur sera publié immédiatement en ligne.</p>
              </div>
              <button data-testid="approve-close" onClick={() => setApproveId(null)} className="text-2xl text-[#75807A] hover:text-[#1A1A1A]">×</button>
            </div>
            <div>
              <label className="text-sm font-semibold text-[#1A1A1A]">Durée de la gratuité</label>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {[{d:0,l:"À vie"},{d:30,l:"30 j"},{d:60,l:"60 j"},{d:90,l:"90 j"}].map((o) => (
                  <button key={o.d} data-testid={`approve-days-${o.d}`}
                    onClick={() => setApproveDays(o.d)}
                    className={`h-11 rounded-xl border-2 text-sm font-bold transition-colors ${Number(approveDays) === o.d ? "border-[#16a34a] bg-[#16a34a]/10 text-[#16a34a]" : "border-[#E2DFD8] text-[#1A1A1A] hover:border-[#16a34a]"}`}>
                    {o.l}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs to-[#75807A] text-[#75807A]">Durée de la période gratuite avant activation payante (0 j = jamais facturé). Nold Go facture un paiement unique 195 € (activation + pack matériel), pas d'abonnement mensuel.</p>
            </div>
            <div className="flex gap-2 pt-1">
              <button data-testid="approve-cancel-btn" onClick={() => setApproveId(null)} className="flex-1 h-11 rounded-xl border-2 border-[#E2DFD8] text-[#75807A] font-bold text-sm hover:bg-[#F0EBE1]">
                Annuler
              </button>
              <button data-testid="approve-confirm-btn" onClick={approveRestaurateur}
                className="flex-1 h-11 rounded-xl bg-[#16a34a] hover:bg-[#128a3e] text-white font-black text-sm">
                Approuver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL Contrat de remise de matériel — génère un PDF à faire signer par le restaurateur */}
      {handoverResto && (
        <div data-testid="handover-modal" className="fixed inset-0 z-[3200] bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setHandoverResto(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full space-y-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-3xl mb-1">📦</div>
                <h3 className="font-head font-black text-xl">Contrat de remise de matériel</h3>
                <p className="text-sm text-[#75807A]">🍽️ {handoverResto.name}</p>
              </div>
              <button data-testid="handover-close" onClick={() => setHandoverResto(null)} className="text-2xl text-[#75807A] hover:text-[#1A1A1A]">×</button>
            </div>
            <div className="bg-[#0EA5E9]/10 border border-[#0EA5E9]/30 rounded-xl p-3 text-xs text-[#3B423E] leading-relaxed">
              Génère un PDF officiel « Nold Go — Remise de matériel » avec les articles listés,
              une clause de responsabilité, une pénalité forfaitaire par appareil perdu ou dégradé,
              et un bloc « Lu et approuvé » pour signature manuscrite du restaurateur.
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-[#75807A] uppercase mb-1.5">Nom et prénom du restaurateur</label>
                <input data-testid="handover-name" value={handoverName} onChange={(e) => setHandoverName(e.target.value)}
                  placeholder="Marc Dupont" className="w-full h-10 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm focus:border-[#0EA5E9] outline-none" />
              </div>
              <div>
                <label className="block text-xs font-bold text-[#75807A] uppercase mb-1.5">Pénalité totale (€) — matériel complet</label>
                <input data-testid="handover-penalty" type="number" min="0" value={handoverPenalty} onChange={(e) => setHandoverPenalty(Number(e.target.value) || 0)}
                  className="w-full h-10 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm focus:border-[#0EA5E9] outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-[#75807A] uppercase mb-1.5">Adresse du restaurateur (optionnel)</label>
              <input data-testid="handover-address" value={handoverAddress || handoverResto.address || ""} onChange={(e) => setHandoverAddress(e.target.value)}
                placeholder="Adresse personnelle ou du restaurant" className="w-full h-10 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm focus:border-[#0EA5E9] outline-none" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold text-[#75807A] uppercase">Matériel remis</label>
                <button data-testid="handover-add-item"
                  onClick={() => setHandoverItems([...handoverItems, { label: "", serial: "" }])}
                  className="text-xs font-bold text-[#0EA5E9] hover:underline">+ Ajouter un appareil</button>
              </div>
              <div className="space-y-2">
                {handoverItems.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2">
                    <input data-testid={`handover-item-label-${i}`} value={it.label}
                      onChange={(e) => { const c = [...handoverItems]; c[i] = { ...c[i], label: e.target.value }; setHandoverItems(c); }}
                      placeholder="Désignation (ex: Tablette Samsung Galaxy Tab A9)"
                      className="col-span-6 h-10 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm focus:border-[#0EA5E9] outline-none" />
                    <input data-testid={`handover-item-serial-${i}`} value={it.serial}
                      onChange={(e) => { const c = [...handoverItems]; c[i] = { ...c[i], serial: e.target.value }; setHandoverItems(c); }}
                      placeholder="N° de série"
                      className="col-span-5 h-10 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm focus:border-[#0EA5E9] outline-none" />
                    <button data-testid={`handover-item-remove-${i}`}
                      onClick={() => setHandoverItems(handoverItems.filter((_, j) => j !== i))}
                      className="col-span-1 h-10 rounded-xl bg-[#FEE2E2] hover:bg-[#FCA5A5] text-[#B91C1C] font-black">×</button>
                  </div>
                ))}
                {handoverItems.length === 0 && (
                  <div className="text-xs text-[#75807A] italic">Ajoute au moins un appareil (tablette, imprimante, etc.)</div>
                )}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button data-testid="handover-cancel" onClick={() => setHandoverResto(null)}
                className="flex-1 h-11 rounded-xl border-2 border-[#E2DFD8] text-[#75807A] font-bold text-sm hover:bg-[#F0EBE1]">
                Annuler
              </button>
              <button data-testid="handover-generate" disabled={handoverBusy || !handoverName.trim() || handoverItems.length === 0}
                onClick={async () => {
                  setHandoverBusy(true);
                  try {
                    const r = await api.post(
                      `/restaurateur/admin/restaurant/${handoverResto.id}/handover.pdf`,
                      {
                        restaurateur_name: handoverName.trim(),
                        restaurateur_address: (handoverAddress || handoverResto.address || "").trim(),
                        items: handoverItems.filter(x => (x.label || "").trim()),
                        unit_penalty_eur: handoverPenalty,
                      },
                      { responseType: "blob" }
                    );
                    const url = URL.createObjectURL(r.data);
                    const a = document.createElement("a");
                    a.href = url; a.download = `nold-go-remise-materiel-${handoverResto.name}.pdf`; document.body.appendChild(a); a.click(); a.remove();
                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                    toast.success("✓ PDF généré — imprime-le en 2 exemplaires pour signature");
                    setHandoverResto(null);
                  } catch (err) { toast.error(apiError(err)); }
                  finally { setHandoverBusy(false); }
                }}
                className="flex-1 h-11 rounded-xl bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-bold text-sm disabled:opacity-50">
                {handoverBusy ? "Génération…" : "📥 Générer le PDF"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL Prospectus PDF automne (preview + download + email restaurateur) */}
      {flyerResto && (
        <div data-testid="flyer-modal" className="fixed inset-0 z-[3300] bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={() => { if (!flyerBusy) setFlyerResto(null); }}>
          <div className="bg-white rounded-2xl p-6 max-w-3xl w-full space-y-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-3xl mb-1">📄</div>
                <h3 className="font-head font-black text-xl">Prospectus automne A5</h3>
                <p className="text-sm text-[#75807A]">🍽️ {flyerResto.name} · Logo Nold Go + QR + Photo + Menu complet</p>
              </div>
              <button data-testid="flyer-close" onClick={() => { if (!flyerBusy) setFlyerResto(null); }} className="text-2xl text-[#75807A] hover:text-[#1A1A1A]">×</button>
            </div>

            {/* Preview iframe du PDF (Cloudflare pré-génère à la volée) */}
            <div className="rounded-xl border-2 border-[#E2DFD8] overflow-hidden bg-[#F0EBE1]" style={{ height: "480px" }}>
              <iframe data-testid="flyer-preview" title="Prospectus"
                src={`${API}/marketing/restaurant-autumn-flyer.pdf?restaurant_id=${flyerResto.id}`}
                className="w-full h-full" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <a data-testid="flyer-download"
                href={`${API}/marketing/restaurant-autumn-flyer.pdf?restaurant_id=${flyerResto.id}`}
                download={`nold-go-prospectus-${flyerResto.name}.pdf`} target="_blank" rel="noreferrer"
                className="h-11 rounded-xl bg-[#1A1A1A] hover:bg-black text-white font-bold text-sm flex items-center justify-center gap-2">
                <Download className="w-4 h-4" /> Télécharger le PDF
              </a>
              <button data-testid="flyer-copy-link"
                onClick={() => {
                  const origin = window.location.origin;
                  navigator.clipboard.writeText(`${origin}/api/marketing/restaurant-autumn-flyer.pdf?restaurant_id=${flyerResto.id}`);
                  toast.success("Lien copié !");
                }}
                className="h-11 rounded-xl bg-white border-2 border-[#B45309] hover:bg-[#B45309]/5 text-[#B45309] font-bold text-sm flex items-center justify-center gap-2">
                <Copy className="w-4 h-4" /> Copier le lien
              </button>
            </div>

            <div className="pt-3 border-t border-[#E2DFD8] space-y-2">
              <label className="block text-xs font-bold text-[#75807A] uppercase">Envoyer par email au restaurateur (PDF en pièce jointe)</label>
              <div className="flex gap-2">
                <input data-testid="flyer-email" type="email" value={flyerEmail}
                  onChange={(e) => setFlyerEmail(e.target.value)}
                  placeholder="restaurateur@exemple.com"
                  className="flex-1 h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm focus:border-[#B45309] outline-none" />
                <button data-testid="flyer-send"
                  disabled={!flyerEmail.trim() || flyerBusy}
                  onClick={async () => {
                    setFlyerBusy(true);
                    try {
                      await api.post("/admin/marketing/send-flyer", { restaurant_id: flyerResto.id, email: flyerEmail.trim() });
                      toast.success(`📧 Prospectus envoyé à ${flyerEmail.trim()}`);
                      setFlyerEmail("");
                    } catch (err) { toast.error(apiError(err)); }
                    finally { setFlyerBusy(false); }
                  }}
                  className="h-11 px-5 rounded-xl bg-[#B45309] hover:bg-[#92400E] text-white font-bold text-sm disabled:opacity-50 flex items-center gap-1.5">
                  <Mail className="w-4 h-4" /> {flyerBusy ? "…" : "Envoyer"}
                </button>
              </div>
            </div>

            <button onClick={() => setFlyerResto(null)}
              className="w-full h-10 rounded-xl border-2 border-[#E2DFD8] text-[#75807A] font-bold text-sm hover:bg-[#F0EBE1]">
              Fermer
            </button>
          </div>
        </div>
      )}


      {/* MODAL Journal des refus livreur (manuel + timeout 30 s) */}
      {refusalsOpen && (
        <RefusalsModal
          onClose={() => { setRefusalsOpen(false); setRefusals(null); }}
          refusals={refusals}
          setRefusals={setRefusals}
        />
      )}

      {/* MODAL Photo preuve de livraison (visualisation plein écran) */}
      {proofOrder && (
        <div
          data-testid="proof-modal"
          className="fixed inset-0 z-[3300] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setProofOrder(null)}
        >
          <div className="max-w-3xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 p-4 border-b border-[#E2DFD8]">
              <div>
                <h3 className="font-head font-black text-lg">📷 Preuve de livraison</h3>
                <p className="text-xs text-[#75807A]">
                  {proofOrder.restaurant_name || "—"} · Livrée le {proofOrder.delivered_at ? new Date(proofOrder.delivered_at).toLocaleString("fr-BE", {dateStyle:"short", timeStyle:"short"}) : "—"} · #{proofOrder.id.slice(0, 8).toUpperCase()}
                </p>
              </div>
              <button data-testid="proof-close" onClick={() => setProofOrder(null)} className="text-2xl text-[#75807A] hover:text-[#1A1A1A]">×</button>
            </div>
            <div className="bg-[#0f0f0f] p-2 flex items-center justify-center" style={{ minHeight: 400 }}>
              <img
                src={proofOrder.url}
                alt="Photo de preuve de livraison"
                className="max-w-full max-h-[70vh] object-contain"
              />
            </div>
            <div className="p-3 flex items-center justify-end gap-2">
              <a
                href={proofOrder.url}
                target="_blank"
                rel="noreferrer"
                data-testid="proof-download"
                className="h-10 px-4 rounded-xl bg-[#16a34a] hover:bg-[#0f7a37] text-white font-bold text-sm inline-flex items-center gap-1.5"
              >
                ⬇ Télécharger
              </a>
            </div>
          </div>
        </div>
      )}

      {/* MODAL Spot publicitaire vidéo 30s (Sora 2 + diaporama + logos Nold Go) */}
      {videoResto && (
        <div data-testid="video-modal" className="fixed inset-0 z-[3300] bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={() => { if (!videoBusy) setVideoResto(null); }}>
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full space-y-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-3xl mb-1">🎬</div>
                <h3 className="font-head font-black text-xl">Spot publicitaire vidéo</h3>
                <p className="text-sm text-[#75807A]">🍽️ {videoResto.name} · 30 secondes · 1280×720 HD</p>
              </div>
              <button data-testid="video-close" onClick={() => { if (!videoBusy) setVideoResto(null); }} className="text-2xl text-[#75807A] hover:text-[#1A1A1A]">×</button>
            </div>

            {/* Étape 1 : Configuration & lancement */}
            {!videoJob && (
              <>
                <div className="bg-[#F26419]/10 border border-[#F26419]/30 rounded-xl p-3 text-xs text-[#3B423E] leading-relaxed">
                  Génère un spot 30 s prêt pour Instagram / TikTok / Facebook / écran boutique :
                  diaporama animé du menu du restaurant +
                  <b> logos Nold Go partout</b> + carte QR + musique.
                  <div className="mt-1.5 text-[11px] italic">⏱️ Mode standard : ~60 secondes. Avec Sora 2 : 2-5 minutes.</div>
                </div>

                <label className="flex items-start gap-2 cursor-pointer">
                  <input data-testid="video-use-sora" type="checkbox" checked={videoUseSora}
                    onChange={(e) => setVideoUseSora(e.target.checked)}
                    className="mt-0.5 h-5 w-5 accent-[#F26419]" />
                  <div>
                    <div className="text-sm font-bold text-[#1A1A1A]">Intro cinématique Sora 2 (optionnel, plus long)</div>
                    <div className="text-xs text-[#75807A]">Ajoute 4 s d'intro IA générative « pizza sortie du four » (facture crédit Emergent, +90s d'attente).</div>
                  </div>
                </label>

                {videoUseSora && (
                  <div>
                    <label className="block text-xs font-bold text-[#75807A] uppercase mb-1.5">Scène Sora 2 (optionnel — sinon générique appétissante)</label>
                    <textarea data-testid="video-prompt" value={videoPrompt} onChange={(e) => setVideoPrompt(e.target.value)}
                      rows={3}
                      placeholder="Cinematic close-up of a Neapolitan pizza margherita being pulled out of a wood-fired oven, warm lighting, 4K…"
                      className="w-full rounded-xl border-2 border-[#E2DFD8] px-3 py-2 text-sm focus:border-[#F26419] outline-none" />
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <button data-testid="video-cancel" onClick={() => setVideoResto(null)}
                    className="flex-1 h-11 rounded-xl border-2 border-[#E2DFD8] text-[#75807A] font-bold text-sm hover:bg-[#F0EBE1]">
                    Annuler
                  </button>
                  <button data-testid="video-start" disabled={videoBusy}
                    onClick={async () => {
                      setVideoBusy(true);
                      try {
                        const { data } = await api.post("/admin/marketing/restaurant-video", {
                          restaurant_id: videoResto.id,
                          use_sora: videoUseSora,
                          prompt: videoPrompt.trim() || undefined,
                        });
                        // On stocke `use_sora` dans le job côté client pour que
                        // <VideoJobPoller> adapte le message d'attente ("Sora 2" vs "diaporama").
                        setVideoJob({ ...data, restaurant_id: videoResto.id, use_sora: videoUseSora });
                        toast.success(videoUseSora ? "🎬 Génération lancée — 2 à 5 min avec Sora" : "🎬 Génération lancée — ~60 secondes");
                      } catch (err) { toast.error(apiError(err)); }
                      finally { setVideoBusy(false); }
                    }}
                    className="flex-1 h-11 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold text-sm disabled:opacity-50">
                    {videoBusy ? "Lancement…" : "🎬 Lancer la génération"}
                  </button>
                </div>
              </>
            )}

            {/* Étape 2 : Génération en cours (polling) */}
            {videoJob && videoJob.status !== "done" && videoJob.status !== "failed" && (
              <VideoJobPoller job={videoJob} onUpdate={setVideoJob} />
            )}

            {/* Étape 3 : Vidéo prête */}
            {videoJob && videoJob.status === "done" && (
              <>
                <div className="bg-[#16a34a]/10 border border-[#16a34a]/30 rounded-xl p-3 text-sm font-bold text-[#166534]">
                  ✅ Spot prêt ! ({Math.round((videoJob.size_bytes || 0) / 1024 / 1024 * 10) / 10} Mo)
                </div>
                <video data-testid="video-preview" src={mediaUrl(videoJob.video_url)} controls
                  className="w-full rounded-xl bg-black" style={{ aspectRatio: "16/9" }} />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <a data-testid="video-download" href={mediaUrl(videoJob.video_url)} download={`nold-go-spot-${videoResto.name}.mp4`}
                    className="h-11 rounded-xl bg-[#1A1A1A] hover:bg-black text-white font-bold text-sm flex items-center justify-center gap-2">
                    <Download className="w-4 h-4" /> Télécharger le MP4
                  </a>
                  <button data-testid="video-copy-link"
                    onClick={() => {
                      const origin = window.location.origin;
                      navigator.clipboard.writeText(`${origin}${videoJob.video_url}`);
                      toast.success("Lien copié !");
                    }}
                    className="h-11 rounded-xl bg-white border-2 border-[#F26419] hover:bg-[#F26419]/5 text-[#F26419] font-bold text-sm flex items-center justify-center gap-2">
                    <Copy className="w-4 h-4" /> Copier le lien
                  </button>
                </div>

                <div className="pt-3 border-t border-[#E2DFD8] space-y-2">
                  <label className="block text-xs font-bold text-[#75807A] uppercase">Envoyer par email au restaurateur</label>
                  <div className="flex gap-2">
                    <input data-testid="video-email" type="email" value={videoEmail}
                      onChange={(e) => setVideoEmail(e.target.value)}
                      placeholder="restaurateur@exemple.com"
                      className="flex-1 h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm focus:border-[#F26419] outline-none" />
                    <button data-testid="video-send"
                      disabled={!videoEmail.trim() || videoBusy}
                      onClick={async () => {
                        setVideoBusy(true);
                        try {
                          await api.post("/admin/marketing/send-restaurant-video", {
                            job_id: videoJob.job_id || videoJob.id, email: videoEmail.trim(),
                          });
                          toast.success(`📧 Spot envoyé à ${videoEmail.trim()}`);
                          setVideoEmail("");
                        } catch (err) { toast.error(apiError(err)); }
                        finally { setVideoBusy(false); }
                      }}
                      className="h-11 px-5 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold text-sm disabled:opacity-50 flex items-center gap-1.5">
                      <Mail className="w-4 h-4" /> {videoBusy ? "…" : "Envoyer"}
                    </button>
                  </div>
                </div>

                <button onClick={() => setVideoResto(null)}
                  className="w-full h-10 rounded-xl border-2 border-[#E2DFD8] text-[#75807A] font-bold text-sm hover:bg-[#F0EBE1]">
                  Fermer
                </button>
              </>
            )}

            {/* Étape 4 : Erreur */}
            {videoJob && videoJob.status === "failed" && (
              <>
                <div className="bg-[#DC2626]/10 border border-[#DC2626]/30 rounded-xl p-3 text-sm text-[#991B1B]">
                  ❌ Génération échouée : {videoJob.error || "erreur inconnue"}
                </div>
                <div className="flex gap-2">
                  <button data-testid="video-retry-now"
                    onClick={async () => {
                      // Réessayer = relance IMMÉDIATE d'un nouveau job (pas juste réafficher le formulaire).
                      // Force use_sora=false pour garantir la génération rapide (~60s).
                      try {
                        setVideoJob({ status: "pending", job_id: null });
                        const { data } = await api.post("/admin/marketing/restaurant-video", {
                          restaurant_id: videoResto.id,
                          use_sora: false,
                          prompt: videoPrompt?.trim() || null,
                        });
                        setVideoJob({ ...data, restaurant_id: videoResto.id, use_sora: false });
                      } catch (e) {
                        setVideoJob({ status: "failed",
                          error: (e?.response?.data?.detail) || e?.message || "Réessai impossible. Vérifiez votre connexion." });
                      }
                    }}
                    className="flex-1 h-11 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold text-sm">
                    Réessayer maintenant
                  </button>
                  <button onClick={() => { setVideoJob(null); setVideoResto(null); }}
                    className="flex-1 h-11 rounded-xl border-2 border-[#E2DFD8] text-[#75807A] font-bold text-sm hover:bg-[#F0EBE1]">
                    Fermer
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* MODAL Invitation restaurateur — envoie un email d'activation au restaurateur */}
      {inviteResto && (
        <div data-testid="invite-modal" className="fixed inset-0 z-[3100] bg-black/50 flex items-center justify-center p-4" onClick={() => setInviteResto(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-3xl mb-1">📧</div>
                <h3 className="font-head font-black text-xl">Inviter le restaurateur</h3>
                <p className="text-sm text-[#75807A]">🍽️ {inviteResto.name}</p>
              </div>
              <button data-testid="invite-close" onClick={() => setInviteResto(null)} className="text-2xl text-[#75807A] hover:text-[#1A1A1A]">×</button>
            </div>
            <div className="bg-[#F0EBE1]/60 rounded-xl p-3 text-xs text-[#3B423E] leading-relaxed">
              Un email d'activation sera envoyé depuis <b>noreply@noldgo.com</b>. Le restaurateur
              clique sur le lien, choisit son code d'accès à 4 chiffres, et atterrit directement
              sur son espace avec sa vitrine déjà pré-configurée.
            </div>
            <div>
              <label className="block text-xs font-bold text-[#75807A] uppercase tracking-wide mb-1.5">
                Email du restaurateur
              </label>
              <input
                data-testid="invite-email-input"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") sendRestaurateurInvite(); }}
                placeholder="contact@sonresto.be"
                autoFocus
                className="w-full h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm focus:border-[#F26419] outline-none"
              />
            </div>
            {inviteLastUrl && (
              <div data-testid="invite-success" className="bg-[#16a34a]/10 border border-[#16a34a]/30 rounded-xl p-3 text-sm">
                <div className="font-bold text-[#16a34a] flex items-center gap-2">
                  <span>✓</span>
                  <span>Invitation envoyée</span>
                </div>
                <div className="text-[#3B423E] text-xs mt-1">
                  Le restaurateur devrait la recevoir dans quelques secondes.
                </div>
                <button
                  data-testid="invite-copy-link"
                  onClick={() => { navigator.clipboard?.writeText(inviteLastUrl); toast.success("Lien copié — à partager uniquement si l'email n'arrive pas"); }}
                  className="mt-2 text-[11px] font-bold text-[#7C3AED] underline hover:text-[#5B21B6]"
                >
                  Copier le lien de secours
                </button>
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button
                data-testid="invite-cancel-btn"
                onClick={() => setInviteResto(null)}
                className="flex-1 h-11 rounded-xl border-2 border-[#E2DFD8] text-[#75807A] font-bold text-sm hover:bg-[#F0EBE1]"
              >
                Fermer
              </button>
              <button
                data-testid="invite-send-btn"
                onClick={sendRestaurateurInvite}
                disabled={inviteLoading || !inviteEmail.trim()}
                className="flex-1 h-11 rounded-xl bg-gradient-to-r from-[#7C3AED] to-[#F26419] text-white font-black text-sm disabled:opacity-40"
              >
                {inviteLoading ? "Envoi…" : (inviteLastUrl ? "Renvoyer" : "Envoyer l'invitation")}
              </button>
            </div>
          </div>
        </div>
      )}


      {activeMeeting && <MeetingRoom meetingId={activeMeeting} onClose={() => { setActiveMeeting(null); api.get("/admin/meetings").then((r) => setMeetings(r.data.items || [])).catch(() => {}); }} />}

      {meetModal && (
        <div data-testid="meet-modal" className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" onClick={() => setMeetModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-head font-black text-xl flex items-center gap-2"><Video className="w-5 h-5 text-[#D94F2B]" /> Nouvelle réunion</h3>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs font-semibold text-[#75807A]">Titre</label>
                <Input data-testid="meet-title" value={meetForm.title} onChange={(e) => setMeetForm({ ...meetForm, title: e.target.value })} placeholder="Point hebdo équipe" className="h-11 rounded-xl border-2 mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#75807A]">Inviter</label>
                <select data-testid="meet-target" value={meetForm.target} onChange={(e) => setMeetForm({ ...meetForm, target: e.target.value })} className="w-full h-11 rounded-xl border-2 border-[#E2DFD8] px-3 mt-1 bg-white font-semibold">
                  <option value="all">Tous les agents</option>
                  <option value="zone">Par zone / agence</option>
                </select>
              </div>
              {meetForm.target === "zone" && (
                <div>
                  <label className="text-xs font-semibold text-[#75807A]">Zone</label>
                  <select data-testid="meet-zone" value={meetForm.zone} onChange={(e) => setMeetForm({ ...meetForm, zone: e.target.value })} className="w-full h-11 rounded-xl border-2 border-[#E2DFD8] px-3 mt-1 bg-white font-semibold">
                    <option value="">— Choisir —</option>
                    {[...new Set(agents.map((a) => a.zone).filter(Boolean))].map((z) => <option key={z} value={z}>{z}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-[#75807A]">Quand</label>
                <div className="flex gap-2 mt-1">
                  <button data-testid="meet-when-now" onClick={() => setMeetForm({ ...meetForm, when: "now" })} className={`flex-1 h-11 rounded-xl text-sm font-semibold ${meetForm.when === "now" ? "bg-[#1A1A1A] text-white" : "bg-white border border-[#E2DFD8] text-[#75807A]"}`}>Maintenant</button>
                  <button data-testid="meet-when-later" onClick={() => setMeetForm({ ...meetForm, when: "later" })} className={`flex-1 h-11 rounded-xl text-sm font-semibold ${meetForm.when === "later" ? "bg-[#1A1A1A] text-white" : "bg-white border border-[#E2DFD8] text-[#75807A]"}`}>Programmer</button>
                </div>
                {meetForm.when === "later" && (
                  <>
                    <Input data-testid="meet-datetime" type="datetime-local" value={meetForm.scheduled_at} onChange={(e) => setMeetForm({ ...meetForm, scheduled_at: e.target.value })} className="h-11 rounded-xl border-2 mt-2" />
                    <div className="mt-2">
                      <label className="text-xs font-semibold text-[#75807A]">Rappel automatique avant la réunion</label>
                      <select data-testid="meet-reminder" value={meetForm.reminder} onChange={(e) => setMeetForm({ ...meetForm, reminder: parseInt(e.target.value, 10) })} className="w-full h-11 rounded-xl border-2 border-[#E2DFD8] px-3 mt-1 bg-white font-semibold">
                        <option value={15}>15 minutes avant</option>
                        <option value={30}>30 minutes avant</option>
                        <option value={60}>1 heure avant</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <Button data-testid="meet-cancel" onClick={() => setMeetModal(false)} className="flex-1 h-11 rounded-xl bg-white border border-[#E2DFD8] text-[#75807A] font-semibold">Annuler</Button>
              <Button data-testid="meet-create" onClick={createMeeting} disabled={meetForm.target === "zone" && !meetForm.zone} className="flex-1 h-11 rounded-xl bg-[#F26419] hover:bg-[#d9540f] text-white font-bold">{meetForm.when === "now" ? "Démarrer" : "Programmer"}</Button>
            </div>
          </div>
        </div>
      )}

      <PdfViewerModal
        open={pdfViewer.open}
        onClose={() => setPdfViewer({ open: false, title: "", path: "", filename: "" })}
        title={pdfViewer.title}
        path={pdfViewer.path}
        filename={pdfViewer.filename}
      />

      {/* Modal Badge Agent — affichage inline (évite les problèmes de popup blocker) */}
      {badgeModal.open && (
        <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setBadgeModal({ open: false, html: "", title: "" })}>
          <div className="bg-[#FDF8F0] rounded-2xl shadow-2xl w-full max-w-md max-h-[95vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2DFD8] bg-white shrink-0">
              <h3 className="font-head font-bold text-[#1A1A1A] truncate pr-2">{badgeModal.title}</h3>
              <div className="flex items-center gap-2 shrink-0">
                <Button data-testid="badge-print-btn" onClick={() => {
                  const ifr = document.getElementById("badge-iframe");
                  if (ifr && ifr.contentWindow) { ifr.contentWindow.focus(); ifr.contentWindow.print(); }
                }} className="h-8 px-3 rounded-lg bg-[#F26419] hover:bg-[#D94F2B] text-white text-xs font-bold"><Printer className="w-3.5 h-3.5 mr-1" />Imprimer</Button>
                <Button data-testid="badge-close-btn" onClick={() => setBadgeModal({ open: false, html: "", title: "" })} className="h-8 w-8 p-0 rounded-lg bg-white border border-[#E2DFD8] text-[#75807A]">✕</Button>
              </div>
            </div>
            <iframe
              id="badge-iframe"
              data-testid="badge-iframe"
              srcDoc={badgeModal.html}
              title={badgeModal.title}
              className="flex-1 w-full border-0 bg-white"
              style={{ minHeight: "600px" }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function CountryCard({ c, onSave, t }) {
  const [base, setBase] = useState(c.base_fare);
  const [perKm, setPerKm] = useState(c.per_km);
  const [minFare, setMinFare] = useState(c.min_fare ?? 0);
  return (
    <div data-testid={`country-card-${c.code}`} className="bg-white rounded-2xl border border-[#E2DFD8] p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><span className="text-2xl">{c.flag}</span><span className="font-head font-bold">{c.name}</span></div>
        <button data-testid={`toggle-country-${c.code}`} onClick={() => onSave(c.code, { active: !c.active })}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full ${c.active ? "bg-[#F26419] text-white" : "bg-[#F0EBE1] text-[#75807A]"}`}>
          {c.active ? t("coverage.live") : t("admin.activate")}
        </button>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3">
        <div><label className="text-xs text-[#75807A]">{t("admin.baseFare")}</label><Input data-testid={`base-${c.code}`} type="number" value={base} onChange={(e) => setBase(e.target.value)} className="h-11 rounded-lg border-2 mt-1" /></div>
        <div><label className="text-xs text-[#75807A]">{t("admin.perKm")}</label><Input data-testid={`perkm-${c.code}`} type="number" value={perKm} onChange={(e) => setPerKm(e.target.value)} className="h-11 rounded-lg border-2 mt-1" /></div>
        <div><label className="text-xs text-[#75807A]">Min. course</label><Input data-testid={`minfare-${c.code}`} type="number" value={minFare} onChange={(e) => setMinFare(e.target.value)} className="h-11 rounded-lg border-2 mt-1" /></div>
      </div>
      <Button data-testid={`save-country-${c.code}`} onClick={() => onSave(c.code, { base_fare: Number(base), per_km: Number(perKm), min_fare: Number(minFare) })} className="w-full mt-4 h-11 rounded-lg bg-[#1A1A1A] text-white text-sm">{t("common.save")}</Button>
    </div>
  );
}


function BusinessCardCard() {
  const [form, setForm] = useState({ name: "Ngnogue Bertrand", title: "Fondateur", email: "contact@noldgo.com", phone: "+32 492 84 23 54", website: "noldgo.com" });
  const [previewKey, setPreviewKey] = useState(0);
  const qs = new URLSearchParams({
    name: form.name || "", title: form.title || "", email: form.email || "",
    phone: form.phone || "", website: form.website || "",
  }).toString();
  const pdfUrl = `${API}/marketing/business-card.pdf?${qs}`;
  const frontUrl = `${API}/marketing/business-card-front.jpg?v=${previewKey}`;
  const backUrl = `${API}/marketing/business-card-back.jpg?${qs}&v=${previewKey}`;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const refreshPreview = () => setPreviewKey((k) => k + 1);

  return (
    <div className="mt-6 bg-white rounded-2xl border-2 border-[#F26419] p-6" data-testid="nold-business-card-card">
      <h3 className="font-head font-black text-lg flex items-center gap-2 text-[#1A1A1A]">
        <QrCode className="w-5 h-5 text-[#F26419]" /> Carte de visite Nold Go (85×55 mm)
      </h3>
      <p className="mt-1 text-sm text-[#75807A]">Carte de visite standard <strong>85×55 mm + 3 mm de fond perdu</strong>, PDF 300 DPI CMJN prêt impression pro. Personnalisez le nom du porteur et téléchargez.</p>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className="text-xs font-bold text-[#75807A] uppercase tracking-wider">Nom</label>
          <Input data-testid="bc-name" value={form.name} onChange={set("name")} onBlur={refreshPreview} placeholder="Prénom Nom" className="h-11 rounded-lg border-2 mt-1" /></div>
        <div><label className="text-xs font-bold text-[#75807A] uppercase tracking-wider">Fonction / Titre</label>
          <Input data-testid="bc-title" value={form.title} onChange={set("title")} onBlur={refreshPreview} placeholder="Fondateur, Commercial, Livreur…" className="h-11 rounded-lg border-2 mt-1" /></div>
        <div><label className="text-xs font-bold text-[#75807A] uppercase tracking-wider">Email</label>
          <Input data-testid="bc-email" type="email" value={form.email} onChange={set("email")} onBlur={refreshPreview} placeholder="prenom@noldgo.com" className="h-11 rounded-lg border-2 mt-1" /></div>
        <div><label className="text-xs font-bold text-[#75807A] uppercase tracking-wider">Téléphone</label>
          <Input data-testid="bc-phone" value={form.phone} onChange={set("phone")} onBlur={refreshPreview} placeholder="+32 …" className="h-11 rounded-lg border-2 mt-1" /></div>
        <div className="sm:col-span-2"><label className="text-xs font-bold text-[#75807A] uppercase tracking-wider">Site (verso)</label>
          <Input data-testid="bc-website" value={form.website} onChange={set("website")} onBlur={refreshPreview} placeholder="noldgo.com" className="h-11 rounded-lg border-2 mt-1" /></div>
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <Button data-testid="bc-download-pdf" onClick={() => window.open(pdfUrl, "_blank")} className="h-11 px-4 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold">
          <Download className="w-4 h-4 mr-1.5" /> Télécharger le PDF (recto/verso)
        </Button>
        <Button data-testid="bc-refresh" onClick={refreshPreview} className="h-11 px-4 rounded-xl bg-white border-2 border-[#F26419] hover:bg-[#F26419]/5 text-[#F26419] font-bold">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Rafraîchir l'aperçu
        </Button>
      </div>

      <p className="mt-5 mb-2 text-xs font-bold uppercase tracking-wider text-[#75807A]">Aperçu recto / verso</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <img data-testid="bc-front-preview" src={frontUrl} alt="Aperçu recto carte de visite Nold Go" className="w-full rounded-xl border border-[#E2DFD8] shadow-sm" loading="lazy" />
        <img data-testid="bc-back-preview" src={backUrl} alt="Aperçu verso carte de visite Nold Go" className="w-full rounded-xl border border-[#E2DFD8] shadow-sm" loading="lazy" />
      </div>
    </div>
  );
}



function PinLookupTool() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const lookup = async () => {
    if (!email.trim()) return toast.error("Entrez un email");
    setBusy(true); setInfo(null);
    try {
      const { data } = await api.get(`/admin/user-pin/lookup?email=${encodeURIComponent(email.trim())}`);
      setInfo(data);
      if (!data.found) toast.error("Compte introuvable");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    if (!info?.email) return;
    if (!window.confirm(`Réinitialiser le PIN de ${info.email} ? Il devra recevoir un nouveau code email et définir un nouveau PIN.`)) return;
    setBusy(true);
    try {
      await api.post("/admin/user-pin/reset", { email: info.email });
      toast.success("PIN réinitialisé — invitez le livreur à se reconnecter");
      lookup();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const unlock = async () => {
    if (!info?.email) return;
    setBusy(true);
    try {
      await api.post("/admin/user-pin/unlock", { email: info.email });
      toast.success("Compte déverrouillé");
      lookup();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button data-testid="pin-lookup-open" onClick={() => setOpen(true)} className="h-11 px-4 rounded-xl bg-white border-2 border-[#F26419] text-[#F26419] hover:bg-[#F26419]/5 text-sm font-bold">
        <KeyRound className="w-4 h-4 mr-1" /> Diagnostic PIN
      </Button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pin-lookup-modal">
          <div className="bg-white rounded-3xl w-full max-w-lg p-6 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs text-[#75807A] font-semibold uppercase tracking-wider">Support connexion</div>
                <h3 className="font-head font-black text-xl text-[#1A1A1A]">Diagnostic & reset PIN</h3>
              </div>
              <button onClick={() => { setOpen(false); setInfo(null); setEmail(""); }} className="text-[#75807A] hover:text-[#1A1A1A] p-2 rounded-lg hover:bg-[#F0EBE1]"><X className="w-4 h-4" /></button>
            </div>

            <div className="flex gap-2">
              <Input data-testid="pin-lookup-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email du livreur / restaurateur" className="h-11 rounded-lg border-2 flex-1" />
              <Button data-testid="pin-lookup-btn" onClick={lookup} disabled={busy} className="h-11 px-4 rounded-lg bg-[#1A1A1A] text-white font-bold text-sm">{busy ? "…" : "Chercher"}</Button>
            </div>

            {info?.found === false && (
              <div className="mt-4 rounded-lg bg-[#FEE2E2] p-3 text-sm text-[#B91C1C]">
                Aucun compte n'existe avec cet email. Le livreur doit d'abord s'inscrire.
              </div>
            )}

            {info?.found && (
              <div className="mt-4 rounded-2xl border border-[#E2DFD8] p-4 space-y-2 text-sm">
                <Row k="Nom" v={info.name || "—"} />
                <Row k="Email stocké" v={info.email_stored_case} />
                <Row k="Rôle actif" v={<><b>{info.role}</b> {info.roles?.length > 1 && <span className="text-xs text-[#75807A]">({info.roles.join(", ")})</span>}</>} />
                <Row k="PIN défini" v={info.has_pin ? <span className="text-[#16a34a] font-bold">✓ Oui</span> : <span className="text-[#B91C1C] font-bold">✗ Non — le livreur n'a jamais fini son inscription (étape "définir PIN")</span>} />
                <Row k="PIN verrouillé" v={info.pin_locked ? <span className="text-[#B91C1C] font-bold">⚠ Oui — encore {Math.ceil(info.pin_lock_remaining_seconds / 60)} min (trop d'essais)</span> : <span className="text-[#16a34a]">Non</span>} />
                <Row k="Tentatives" v={info.pin_attempts} />
                <Row k="Suspendu" v={info.suspended ? <span className="text-[#B91C1C] font-bold">⚠ Oui — {info.suspension_reason || "raison non précisée"}</span> : "Non"} />
                <Row k="Email vérifié" v={info.email_verified ? "✓ Oui" : <span className="text-[#B91C1C]">✗ Non</span>} />
                <Row k="KYC" v={info.kyc_status || "—"} />
                <Row k="Créé le" v={info.created_at ? new Date(info.created_at).toLocaleString("fr-FR") : "—"} />

                <div className="pt-2 flex flex-wrap gap-2">
                  {info.pin_locked && (
                    <Button data-testid="pin-unlock" onClick={unlock} disabled={busy} className="h-10 px-3 rounded-lg bg-[#F26419] text-white text-xs font-bold">🔓 Déverrouiller</Button>
                  )}
                  {info.has_pin && (
                    <Button data-testid="pin-reset" onClick={reset} disabled={busy} className="h-10 px-3 rounded-lg bg-white border-2 border-[#B91C1C] text-[#B91C1C] text-xs font-bold">🔁 Reset PIN (obligera un nouveau code email)</Button>
                  )}
                  {!info.has_pin && (
                    <div className="text-xs text-[#B91C1C] bg-[#FEE2E2] rounded-lg p-2">
                      💡 Ce livreur a créé son compte mais n'a jamais défini son PIN. Il doit refaire "Connexion" → saisir l'email → recevoir le code → puis créer un PIN à 4 chiffres.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}


function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-[#75807A]">{k}</span>
      <span className="text-[#1A1A1A] text-right">{v}</span>
    </div>
  );
}
